<template>
  <div ref="paperWorkspace" class="paper-workspace mobile-file-browser-active" :class="{ 'is-detail': detailMode, 'is-interaction-locked': interactionLocked }" @click="closeFloatingMenus">
    <mobile-file-browser-chrome
      v-if="!detailMode"
      ref="mobileFileBrowserChrome"
      :title="mobileFileBrowserTitle"
      :language="language"
      :can-import="mobileFileBrowserCanImport"
      :import-label="mobileFileBrowserImportLabel"
      compact-import-row
      :show-filter="mobileFileBrowserShowFilter"
      :filter-options="filterOptions"
      :filter-key="mobileFileBrowserFilterKey"
      :show-sort="mobileFileBrowserShowSort"
      :sort-options="sortOptions"
      :sort-key="mobileFileBrowserSortKey"
      :sort-desc="sortDesc"
      :view-mode="viewMode"
      :icons="mobileFileBrowserIcons"
      @back="handleMobileFileBrowserBack"
      @search="handleMobileFileBrowserSearch"
      @import="handleMobileFileBrowserImport"
      @select-filter="handleMobileFileBrowserFilter"
      @select-sort="handleMobileFileBrowserSort"
      @select-view="handleMobileFileBrowserView"
    />
    <div
      v-if="!detailMode && viewMode == 'list' && mobileFileItemActionItem"
      class="mobile-file-item-action-mask"
      @click="closeMobileFileItemAction"
    ></div>
    <div v-if="!detailMode && viewMode == 'list' && mobileFileItemActionItem" class="mobile-file-item-action-layer" @click.stop>
      <button type="button" :aria-label="ui.download" :title="ui.download" :disabled="!isPaperDownloadableItem(mobileFileItemActionItem)" @click="handleItemMenuAction('download', mobileFileItemActionItem)">
        <img :src="paperIcons.download" alt="" />
        <span>{{ ui.download }}</span>
      </button>
    </div>
    <input
      ref="cloudUploadInput"
      class="paper-upload-input"
      type="file"
      :accept="cloudUploadAccept"
      multiple
      @change="handleCloudUploadChange"
    />

    <header v-if="detailMode && !(contextPreviewMode && recordPanelOpen)" class="paper-detail-header">
      <div class="paper-detail-title">
        <button class="paper-back-button" type="button" :aria-label="ui.back" @click.stop="handleDetailBack">
          <span></span>
        </button>
        <strong :title="paperDetailTitle">{{ paperDetailTitle }}</strong>
      </div>
      <div class="paper-detail-actions">
        <button
          v-if="!pageGridOpen && canShowRecordButton"
          class="paper-tool-button"
          type="button"
          :class="{ active: recordPanelOpen && !recordTextOpen }"
          :disabled="paperDetailLoading || (contextPreviewMode && detailRecordings.length == 0)"
          :title="ui.recordings"
          @click.stop="toggleRecordPanel"
        >
          <img :src="paperIcons.record" alt="" />
        </button>
        <button class="paper-tool-button" type="button" :disabled="paperDetailLoading || detailPages.length == 0 || (pageGridOpen && selectedDetailPages.length == 0)" :title="ui.download" @click.stop="downloadCurrentPage">
          <img :src="paperIcons.download" alt="" />
        </button>
        <button
          v-if="!pageGridOpen"
          class="paper-tool-button"
          type="button"
          :class="{ active: pageGridOpen }"
          :disabled="paperDetailLoading || pageGridOpen || detailPages.length == 0 || !canUsePageGridButton"
          :title="ui.pageGrid"
          @click.stop="togglePageGrid"
        >
          <img :src="paperIcons.pageGrid" alt="" />
        </button>
        <button v-if="!pageGridOpen" class="paper-tool-button paper-zoom-button" type="button" :disabled="paperDetailLoading || pageGridOpen || zoomLevel >= 5" :title="ui.zoomIn" @click.stop="zoomIn">
          <img :src="paperIcons.zoomIn" alt="" />
          <span v-if="isPreviewZoomTipVisible('paper', 'in')" class="preview-zoom-tip">{{ previewZoomTip.text }}</span>
        </button>
        <button v-if="!pageGridOpen" class="paper-tool-button paper-zoom-button" type="button" :disabled="paperDetailLoading || pageGridOpen || zoomLevel <= -3" :title="ui.zoomOut" @click.stop="zoomOut">
          <img :src="paperIcons.zoomOut" alt="" />
          <span v-if="isPreviewZoomTipVisible('paper', 'out')" class="preview-zoom-tip">{{ previewZoomTip.text }}</span>
        </button>
      </div>
    </header>

    <template v-if="!detailMode">
      <div
        v-if="hasPaperSelection"
        ref="paperSelectionLayer"
        class="paper-selection-layer"
        :class="{ dragging: selectionBarDragging }"
        :style="selectionBarLayerStyle"
        @mousedown.stop="startSelectionBarDrag"
        @click.stop
      >
        <div class="paper-selection-bar">
          <span>{{ ui.selectedPrefix }} {{ selectedCount }} {{ ui.selectedSuffix }}</span>
          <button class="paper-selection-close" type="button" :aria-label="ui.cancel" @click="clearSelection">
            <span></span>
          </button>
        </div>
      </div>

      <div v-if="hasPaperSelection" class="paper-selection-actions" @click.stop>
        <button type="button" :disabled="!canBatchSelectionAction" :aria-label="ui.download" @click="handleSelectionAction('download')">
          <img :src="paperIcons.download" alt="" />
          <span>{{ ui.download }}</span>
        </button>
      </div>

      <div v-if="searchOpen && !hasPaperSelection" class="paper-search-line" @click.stop>
        <img :src="paperIcons.search" alt="" />
        <input ref="paperSearchInput" v-model.trim="searchKeyword" type="text" :placeholder="ui.search" maxlength="125" />
        <button v-if="searchKeyword" type="button" @click="searchKeyword = ''"></button>
      </div>

      <section
        ref="paperListArea"
        class="paper-list-area"
        :class="{ 'is-grid': viewMode == 'grid' }"
        @scroll.passive="handlePaperListScroll"
        @touchstart.passive="handleMobilePullRefreshStart"
        @touchmove="handleMobilePullRefreshMove"
        @touchend="handleMobilePullRefreshEnd"
        @touchcancel="cancelMobilePullRefreshGesture"
      >
        <mobile-pull-refresh-indicator
          :distance="mobilePullRefreshDistance"
          :refreshing="mobilePullRefreshing"
          :threshold="mobilePullRefreshThreshold"
        />
        <a-spin :spinning="paperListLoading" wrapper-class-name="paper-list-spin">
          <div class="paper-list-content">
            <div v-if="!paperListLoading && visiblePaperItems.length == 0" class="paper-empty">
              <img :src="paperIcons.recordEmpty" alt="" />
              <p>{{ ui.empty }}</p>
            </div>

            <div v-if="!paperListLoading && viewMode == 'grid' && visiblePaperItems.length > 0" ref="paperGrid" class="paper-grid">
              <div
                v-for="(item, index) in visiblePaperItems"
                :key="item.id"
                class="paper-card"
                :class="getPaperGridItemClasses(item, index)"
                role="button"
                tabindex="0"
                :data-mobile-file-id="item.id"
                @touchstart.passive="startMobileFileTouch(item, $event)"
                @touchmove="moveMobileFileTouch"
                @touchend="endMobileFileTouch"
                @touchcancel="cancelMobileFileTouch"
                @contextmenu.prevent="handleMobileFileContextMenu(item)"
                @click="handleMobileFileItemClick(item, $event)"
                @dblclick.stop.prevent="handleMobileFileItemDblClick(item, $event)"
                @keyup.enter="handleMobileFileItemKeyup(item)"
              >
                <span class="paper-cover" :class="{ empty: !shouldDisplayGridCover(item), locked: item.encrypted, template: item.template, 'folded-cover': shouldShowPaperCoverFold(item) }">
                  <img v-if="shouldDisplayGridCover(item)" class="paper-cover-image" :src="getPaperGridCoverImageUrl(item)" alt="" loading="lazy" decoding="async" @error="markPaperGridCoverFailed(item)" />
                  <img v-else class="paper-cover-default" :class="{ 'folder-icon': item.isFolder && !item.encrypted, 'encrypt-icon': item.isFolder && item.encrypted, 'placeholder-cover': isListPlaceholderCover(item) }" :src="getFallbackCover(item, 'grid')" alt="" />
                </span>
                <span class="paper-card-title-row">
                  <span class="paper-card-name" :title="getPaperDisplayName(item)">{{ getPaperDisplayName(item) }}</span>
                  <img v-if="item.favorite" class="paper-favorite-status paper-card-favorite-status" :src="paperIcons.favoriteFilled" :alt="ui.favorite" />
                </span>
                <span class="paper-card-meta">
                  <span>{{ formatDate(getDisplayDate(item)) }}</span>
                  <span class="paper-card-count" :title="formatGridChildSize(item)">{{ formatGridChildSize(item) }}</span>
                </span>
              </div>
            </div>

            <div v-if="!paperListLoading && viewMode == 'list' && visiblePaperItems.length > 0" class="paper-table">
              <div
                v-for="item in visiblePaperItems"
                :key="item.id"
                class="paper-row"
                :class="{ selected: isPaperSelected(item), 'item-action-active': moreMenuId == item.id }"
                role="button"
                tabindex="0"
                :title="getPaperDisplayName(item)"
                :data-mobile-file-id="item.id"
                @touchstart.passive="startMobileFileTouch(item, $event)"
                @touchmove="moveMobileFileTouch"
                @touchend="endMobileFileTouch"
                @touchcancel="cancelMobileFileTouch"
                @contextmenu.prevent="handleMobileFileContextMenu(item)"
                @click="handleMobileFileItemClick(item, $event)"
                @dblclick.stop.prevent="handleMobileFileItemDblClick(item, $event)"
                @keyup.enter="handleMobileFileItemKeyup(item)"
              >
                <span class="paper-row-cover" :class="{ empty: !shouldDisplayCover(item), locked: item.encrypted, 'has-cover': shouldDisplayCover(item) }">
                  <img v-if="shouldDisplayCover(item)" class="paper-row-cover-image" :src="item.cover" alt="" loading="lazy" decoding="async" @error="markCoverFailed(item)" />
                  <img v-else class="paper-row-cover-default" :class="{ 'folder-icon': item.isFolder && !item.encrypted, 'encrypt-icon': item.encrypted, 'placeholder-cover': isListPlaceholderCover(item) }" :src="getFallbackCover(item, 'list')" alt="" />
                </span>
                <span class="paper-row-main">
                  <span class="paper-row-title-line">
                    <strong>{{ getPaperDisplayName(item) }}</strong>
                    <img v-if="item.favorite" class="paper-favorite-status" :src="paperIcons.favoriteFilled" :alt="ui.favorite" />
                  </span>
                  <span class="paper-row-meta">
                    <span>{{ formatPageCount(item) }}</span>
                    <i></i>
                    <span>{{ formatDate(getDisplayDate(item)) }}</span>
                  </span>
                </span>
                <span class="paper-more-wrap" @touchstart.stop @touchmove.stop @touchend.stop @contextmenu.stop.prevent @click.stop>
                  <button class="paper-more-button" type="button" :title="ui.more" @click="toggleMobileFileItemAction(item)">
                    <svg class="paper-more-icon" viewBox="0 0 12 4" aria-hidden="true" focusable="false">
                      <circle cx="2" cy="2" r="1.25"></circle>
                      <circle cx="6" cy="2" r="1.25"></circle>
                      <circle cx="10" cy="2" r="1.25"></circle>
                    </svg>
                  </button>
                  <div v-if="moreMenuId == item.id" class="paper-more-menu">
                    <button type="button" :disabled="!isPaperDownloadableItem(item)" @click="handleItemMenuAction('download', item)">
                      <img :src="paperIcons.download" alt="" />
                      <span>{{ ui.download }}</span>
                    </button>
                  </div>
                </span>
              </div>
            </div>
            <div v-if="paperListAppendLoading" class="list-bottom-loading">
              <a-spin size="small" :spinning="true" />
              <span>{{ ui.loading }}</span>
            </div>
          </div>
        </a-spin>
      </section>
    </template>

    <section v-else class="paper-detail-body" :class="{ 'pages-open': pageGridOpen, 'is-resolving': paperDetailLoading, 'is-default-zoom': zoomLevel == 0 }">
      <div v-if="paperDetailLoading || paperDetailError" class="paper-detail-resolving">
        <div class="paper-detail-progress-box" :class="{ error: paperDetailError }">
          <strong>{{ paperDetailError ? ui.loadFailed : ui.loading }}</strong>
          <span v-if="!paperDetailError">{{ paperDetailProgress }}%</span>
          <div v-if="!paperDetailError" class="paper-detail-progress-track">
            <i :style="{ width: paperDetailProgress + '%' }"></i>
          </div>
          <p v-if="paperDetailError">{{ paperDetailError }}</p>
          <button v-if="paperDetailError" type="button" @click.stop="retryPaperDetailLoad">{{ ui.retry }}</button>
        </div>
      </div>
      <template v-else>
        <div v-if="pageGridOpen" ref="paperPagesGrid" class="paper-pages-grid">
          <button
            v-for="(page, index) in detailPages"
            :key="page.pageNo"
            type="button"
            class="mobile-preview-grid-item"
            data-preview-grid-scope="paper-pages"
            :data-preview-grid-id="page.pageNo"
            :class="{ active: page.pageNo == currentPageNo, selected: isDetailPageSelected(page.pageNo), 'grid-edge-top': isPaperPagesGridTopEdge(index), 'grid-selected-row-start': isPaperPagesGridSelectedRowStart(index), 'grid-selection-continues-below': isPaperPagesGridSelectionContinuesBelow(index) }"
            @touchstart.passive="startMobilePreviewGridTouch('paper-pages', page.pageNo, $event, mobilePreviewGridAdditiveLongPressSelection)"
            @touchmove="moveMobilePreviewGridTouch"
            @touchend="endMobilePreviewGridTouch"
            @touchcancel="cancelMobilePreviewGridTouch"
            @contextmenu.prevent="handleMobilePreviewGridContextMenu('paper-pages', page.pageNo, mobilePreviewGridAdditiveLongPressSelection)"
            @click="handleMobilePreviewGridItemClick('paper-pages', page.pageNo)"
          >
            <span>
              <img v-if="page.imageUrl" :src="page.imageUrl" alt="" loading="lazy" decoding="async" />
              <em v-else class="page-fallback" :class="{ pdf: page.pdfPreviewUrl }">{{ page.pdfPreviewUrl ? 'PDF' : '' }}</em>
            </span>
            <strong :title="'Page ' + page.pageNo">Page {{ page.pageNo }}</strong>
          </button>
        </div>
        <div
          v-else
          ref="paperPreviewShell"
          class="paper-preview-shell"
          :class="{ dragging: paperPreviewDragState }"
          :style="{ '--paper-preview-zoom': zoomValue }"
          @mousedown="startPaperPreviewDrag"
        >
          <div class="paper-preview-zoom">
            <div class="paper-preview-stage">
              <iframe v-if="currentPage && currentPage.pdfPreviewUrl" class="paper-preview-pdf" :src="currentPage.pdfPreviewUrl" title="PDF" @mousedown.stop></iframe>
              <img v-else-if="currentPage && currentPage.imageUrl" :key="currentPage.imageUrl" class="paper-preview-page-image" :class="{ 'is-loading': isPaperPreviewPageImageLoading(currentPage) }" :src="currentPage.imageUrl" alt="" decoding="async" fetchpriority="high" @load="handlePaperPreviewPageImageLoad($event, currentPage)" @error="handlePaperPreviewPageImageError($event, currentPage)" @dragstart.prevent />
              <div v-else class="paper-preview-empty"></div>
              <div v-if="isPaperPreviewPageImageLoading(currentPage)" class="paper-preview-page-loading" aria-hidden="true">
                <a-spin :spinning="true" class="paper-preview-page-loading-spin" />
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-if="!pageGridOpen && detailPages.length > 0" class="paper-page-controls">
        <div class="paper-page-count">
          <button type="button" :disabled="isPaperPreviewNavigationLocked() || currentPageIndex <= 0" @click="goPrevPage"></button>
          <span>{{ currentPageIndex + 1 }}/{{ detailPages.length }}</span>
          <button type="button" :disabled="isPaperPreviewNavigationLocked() || currentPageIndex >= detailPages.length - 1" @click="goNextPage"></button>
        </div>
      </div>

      <div v-if="recordPanelOpen && canShowRecordButton && !contextPreviewMode" class="paper-record-mask" @click="closeRecordPanel"></div>
      <section
        v-if="recordPanelOpen && canShowRecordButton"
        class="paper-record-panel"
        :class="{ 'is-recent-preview': contextPreviewMode, 'is-text-open': recordTextOpen }"
        @click.stop
      >
        <button class="paper-record-close" type="button" :aria-label="contextPreviewMode ? ui.back : ui.close" @click="handleRecordPanelBack">
          <span></span>
        </button>
        <template v-if="!recordTextOpen">
          <header>{{ ui.allRecordings }}</header>
          <div v-if="detailRecordings.length > 0" class="record-list">
            <div v-for="recording in detailRecordings" :key="recording.id" class="record-row">
              <img class="record-icon" :src="contextPreviewMode ? paperIcons.recentRecord : paperIcons.record" alt="" />
              <div class="record-main">
                <strong>{{ recording.recordingName }}</strong>
                <span>{{ formatDuration(recording.duration) }} <em></em> {{ formatDateTime(recording.recordedAtMs) }}</span>
              </div>
              <button
                class="record-action"
                type="button"
                :disabled="!hasTranscript(recording)"
                :title="ui.transcript"
                @click="openRecordingText(recording)"
              >
                <img :src="paperIcons.transcript" alt="" />
              </button>
              <button class="record-action" type="button" :title="ui.download" @click="enqueueRecordingDownload(recording)">
                <img :src="paperIcons.download" alt="" />
              </button>
            </div>
          </div>
          <div v-else class="record-empty">
            <img :src="paperIcons.recordEmpty" alt="" />
            <p>{{ ui.empty }}</p>
          </div>
        </template>

        <template v-else>
          <header class="record-text-head">
            <button type="button" @click="closeRecordingText">
              <span></span>
            </button>
            <strong>{{ activeRecordingName }}</strong>
          </header>
          <div class="record-text-body">
            {{ activeRecordingText }}
          </div>
        </template>
      </section>
    </section>

    <div v-if="paperPasswordModalOpen" class="paper-password-mask" @click="closePaperPasswordModal"></div>
    <section v-if="paperPasswordModalOpen" class="paper-password-modal" @click.stop>
      <header>{{ ui.passwordTitle }}</header>
      <div class="paper-password-field" :class="{ error: paperPasswordError }">
        <input
          ref="paperPasswordInput"
          v-model="paperPasswordInput"
          :type="paperPasswordVisible ? 'text' : 'password'"
          :placeholder="ui.passwordPlaceholder"
          maxlength="125"
          :disabled="paperPasswordSubmitting"
          @keyup.enter="confirmPaperPassword"
        />
        <button type="button" :disabled="paperPasswordSubmitting" :aria-label="paperPasswordVisible ? ui.passwordHide : ui.passwordShow" @click="togglePaperPasswordVisible">
          <span :class="{ visible: paperPasswordVisible }"></span>
        </button>
      </div>
      <p v-if="paperPasswordError" class="paper-password-error">{{ paperPasswordError }}</p>
      <footer>
        <div>
          <button type="button" :disabled="paperPasswordSubmitting" @click="closePaperPasswordModal">{{ ui.cancel }}</button>
          <button type="button" :disabled="paperPasswordSubmitting" @click="confirmPaperPassword">{{ paperPasswordSubmitting ? ui.passwordConfirming : ui.ok }}</button>
        </div>
      </footer>
    </section>

    <div v-if="paperDownloadFormatModalOpen" class="paper-download-mask paper-download-format-mask" @click="closePaperDownloadFormatModal"></div>
    <section v-if="paperDownloadFormatModalOpen" class="paper-download-modal paper-download-format-modal" @click.stop>
      <header>
        <strong>{{ ui.chooseFormat }}</strong>
        <button type="button" :aria-label="ui.close" @click="closePaperDownloadFormatModal">
          <span></span>
        </button>
      </header>
      <div class="paper-download-options">
        <label>
          <input v-model="paperDownloadFormat" type="radio" value="pdf" />
          <span>PDF</span>
        </label>
        <label>
          <input v-model="paperDownloadFormat" type="radio" value="png" />
          <span>PNG</span>
        </label>
      </div>
      <footer>
        <button type="button" @click="closePaperDownloadFormatModal">{{ ui.cancel }}</button>
        <button type="button" @click="confirmPaperDownloadFormat">{{ ui.ok }}</button>
      </footer>
    </section>
  </div>
</template>

<script>
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import pako from 'pako'
import pdfjsLib from '@/utils/pdfjs'
import { getPaperSyncList, getPaperSyncDetail , checkCloudPassword } from '@/api/api'
import downloadCenterMixin from '@/mixins/downloadCenterMixin'
import paperListPaginationMixin from '@/mixins/paperListPaginationMixin'
import mobileCloudListStateMixin from '@/mixins/mobileCloudListStateMixin'
import mobileFileBrowserMixin from '@/mixins/mobileFileBrowserMixin'
import mobilePullRefreshMixin from '@/mixins/mobilePullRefreshMixin'
import mobilePreviewGridSelectionMixin from '@/mixins/mobilePreviewGridSelectionMixin'
import cloudResourceUploadMixin from '@/mixins/cloudResourceUploadMixin'
import directFilePreviewMixin from '@/mixins/directFilePreviewMixin'
import previewPerformanceMixin from '@/mixins/previewPerformanceMixin'
import MobileFileBrowserChrome from '@/wap/components/MobileFileBrowserChrome.vue'
import { getAppUiText, getResourceItemUnitLabel, getTemplateFolderDisplayName, getTrashLabel } from '@/utils/language/common'
import { selectItemWithWindowsRange } from '@/utils/rangeSelection'

const MOCK_PAGE_IMAGE = require('@/assets/wap/paper/mock-page.png')
const MOCK_SMALL_PAGE_IMAGE = require('@/assets/wap/paper/mock-small-page.png')
const MOCK_TEMPLATE_IMAGE = require('@/assets/wap/paper/template-placeholder.png')
const PAPER_EMPTY_ICON = require('@/assets/wap/paper/paper_empty.png')
const ICON_CREATE_FOLDER = require('@/assets/wap/paper/create-folder.png')
const ICON_SEARCH = require('@/assets/wap/paper/search.png')
const ICON_IMPORT = require('@/assets/wap/mobile-import-drawer.svg')
const ICON_VIEW_GRID = require('@/assets/wap/paper/view-grid.png')
const ICON_VIEW_LIST = require('@/assets/wap/paper/view-list.png')
const ICON_SORT_DESC = require('@/assets/wap/paper/sort-desc.png')
const ICON_SORT_ASC = require('@/assets/wap/paper/sort-asc.png')
const ICON_BREADCRUMB_SEPARATOR = require('@/assets/wap/paper/more.png')
const ICON_FILTER_ALL = require('@/assets/wap/paper/filter-all.png')
const ICON_FILTER_PDF = require('@/assets/wap/paper/filter-pdf.png')
const ICON_FILTER_FAVORITE = require('@/assets/wap/paper/favorite.png')
const ICON_FILTER_DELETED = require('@/assets/wap/paper/delete.png')
const ICON_CHECK = require('@/assets/wap/paper/check.png')
const ICON_DOWNLOAD = require('@/assets/wap/paper/download.png')
const ICON_RENAME = require('@/assets/wap/paper/rename.png')
const ICON_MOVE = require('@/assets/wap/paper/move.png')
const ICON_ENCRYPT_MENU = require('@/assets/wap/paper/encrypt-file.png')
const ICON_DELETE = require('@/assets/wap/paper/delete.png')
const ICON_FAVORITE = require('@/assets/wap/paper/favorite.png')
const ICON_FAVORITE_FILLED = require('@/assets/wap/paper/favorite-filled.png')
const ICON_FOLDER_LIST_FILLED = require('@/assets/wap/paper/list-folder-filled.png')
const ICON_FOLDER_LIST_EMPTY = require('@/assets/wap/paper/list-folder-empty.png')
const ICON_FOLDER_GRID_FILLED = require('@/assets/wap/paper/grid-folder-filled.png')
const ICON_FOLDER_GRID_EMPTY = require('@/assets/wap/paper/grid-folder-empty.png')
const ICON_TEMPLATE_LIST_FILLED = require('@/assets/wap/paper/list-template-filled.png')
const ICON_TEMPLATE_LIST_EMPTY = require('@/assets/wap/paper/list-template-empty.png')
const ICON_TEMPLATE_GRID_FILLED = require('@/assets/wap/paper/grid-template-filled.png')
const ICON_TEMPLATE_GRID_EMPTY = require('@/assets/wap/paper/grid-template-empty.png')
const ICON_FILE_LIST_PDF = require('@/assets/wap/paper/list-pdf.png')
const ICON_FILE_LIST_PAPER = require('@/assets/wap/paper/list-paper.png')
const ICON_FILE_LIST_ENCRYPT = require('@/assets/wap/paper/list-encrypt.png')
const ICON_FILE_GRID_PDF = require('@/assets/wap/paper/grid-pdf.png')
const ICON_FILE_GRID_PAPER = require('@/assets/wap/paper/grid-paper.png')
const ICON_FILE_GRID_ENCRYPT = require('@/assets/wap/paper/grid-encrypt.png')
const ICON_PAGE_GRID = require('@/assets/wap/paper/page-grid.png')
const ICON_ZOOM_IN = require('@/assets/wap/paper/zoom-in.png')
const ICON_ZOOM_OUT = require('@/assets/wap/paper/zoom-out.png')
const ICON_CLOSE = require('@/assets/wap/paper/close-plain.png')
const RECORD_ICON = require('@/assets/wap/paper/record-file.png')
const RECENT_RECORD_ICON = require('@/assets/wap/recording-file-page.svg')
const TRANSCRIPT_ICON = require('@/assets/wap/paper/record-transcript.png')
const RECORD_EMPTY_ICON = require('@/assets/wap/paper/record-empty.png')
const PAPER_APP_TYPE = 1

export default {
  name: 'PaperSync',
  downloadPdfPageSize: 'a4',
  paperPreviewPagePreload: true,
  cloudUploadFolderType: 'paper',
  cloudUploadAppType: PAPER_APP_TYPE,
  cloudUploadExtensions: ['pdf'],
  cloudUploadTemplateImport: true,
  cloudUploadTemplateExtensions: ['png', 'jpg', 'jpeg'],
  mobileFileBrowserConfig: {
    title: 'paper',
    importMode: 'always',
    filterMode: 'root',
    sortMode: 'always'
  },
  components: { MobileFileBrowserChrome },
  mixins: [downloadCenterMixin, mobileCloudListStateMixin, mobileFileBrowserMixin, mobilePullRefreshMixin, mobilePreviewGridSelectionMixin, cloudResourceUploadMixin, paperListPaginationMixin, directFilePreviewMixin, previewPerformanceMixin],
  props: {
    skipInitialListLoad: {
      type: Boolean,
      default: false
    },
    detailBackRoot: {
      type: Boolean,
      default: false
    },
    recentPreviewMode: {
      type: Boolean,
      default: false
    },
    myFilesRecordingPreviewMode: {
      type: Boolean,
      default: false
    },
    searchPreviewMode: {
      type: Boolean,
      default: false
    },
    mobilePreviewGridAdditiveLongPressSelection: {
      type: Boolean,
      default: false
    },
    language: {
      type: String,
      default: 'us'
    },
    initialRefreshState: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      paperListLoading: false,
      paperDetailLoading: false,
      paperDetailProgress: 0,
      paperDetailError: '',
      paperItems: [],
      paperFilter: 'all',
      filterMenuOpen: false,
      sortMenuOpen: false,
      sortField: 'createTime',
      sortDesc: true,
      viewMode: 'list',
      searchOpen: false,
      searchKeyword: '',
      paperPasswordModalOpen: false,
      paperPasswordTarget: null,
      paperPasswordInput: '',
      paperPasswordVisible: false,
      paperPasswordError: '',
      paperPasswordSubmitting: false,
      paperPasswordRequestSeq: 0,
      paperPasswordAction: 'open',
      paperPasswordItems: [],
      paperPasswordOpenOptions: null,
paperDownloadFormatModalOpen: false,
      paperDownloadFormat: 'pdf',
      paperDownloadItems: [],
      paperDownloadPassword: '',
paperDownloadFormatContext: 'items',
      paperDownloadDetailPages: [],Visible: true,
      selectedPaperId: '',
      selectedPaperIds: [],
      selectedPaperAnchorId: '',
      paperItemClickTimer: null,
      paperListReturnScrollTop: 0,
      moreMenuId: '',
      detailMode: false,
      currentPaper: null,
      currentPaperPassword: '',
      paperDetail: null,
      paperDetailRequestSeq: 0,
      paperPreviewObjectUrls: [],
      paperDetailCache: {},
      currentPageNo: 1,
      pageGridOpen: false,
      pageGridReturnPageNo: 1,
      paperPagesGridColumnCount: 1,
      selectedDetailPageNos: [],
      selectedDetailPageAnchorNo: '',
      recordPanelOpen: false,
      recordTextOpen: false,
      recordTextLoading: false,
      recordTextError: '',
      recordTextRequestSeq: 0,
      activeRecording: null,
      zoomLevel: 0,
      zoomValue: 1,
      paperPreviewDragState: null,
      paperListPendingLoad: false,
      paperListRequestSeq: 0,
      paperListResourceId: '',
      paperFolderPath: [],
      paperGridColumnCount: 1,
      selectionBarOffsetY: 0,
      selectionBarScrollbarOffset: 0,
      selectionBarDragging: false,
      selectionBarDragState: null,
      paperIcons: {
        createFolder: ICON_CREATE_FOLDER,
        search: ICON_SEARCH,
        import: ICON_IMPORT,
        viewList: ICON_VIEW_LIST,
        viewGrid: ICON_VIEW_GRID,
        sortDesc: ICON_SORT_DESC,
        sortAsc: ICON_SORT_ASC,
        breadcrumbSeparator: ICON_BREADCRUMB_SEPARATOR,
        filterAll: ICON_FILTER_ALL,
        filterPdf: ICON_FILTER_PDF,
        filterFavorite: ICON_FILTER_FAVORITE,
        filterDeleted: ICON_FILTER_DELETED,
        check: ICON_CHECK,
        download: ICON_DOWNLOAD,
        rename: ICON_RENAME,
        move: ICON_MOVE,
        encryptMenu: ICON_ENCRYPT_MENU,
        delete: ICON_DELETE,
        favorite: ICON_FAVORITE,
        favoriteFilled: ICON_FAVORITE_FILLED,
        folderListFilled: ICON_FOLDER_LIST_FILLED,
        folderListEmpty: ICON_FOLDER_LIST_EMPTY,
        folderGridFilled: ICON_FOLDER_GRID_FILLED,
        folderGridEmpty: ICON_FOLDER_GRID_EMPTY,
        templateListFilled: ICON_TEMPLATE_LIST_FILLED,
        templateListEmpty: ICON_TEMPLATE_LIST_EMPTY,
        templateGridFilled: ICON_TEMPLATE_GRID_FILLED,
        templateGridEmpty: ICON_TEMPLATE_GRID_EMPTY,
        fileListPdf: ICON_FILE_LIST_PDF,
        fileListPaper: ICON_FILE_LIST_PAPER,
        filePaperEmpty: PAPER_EMPTY_ICON,
        fileListEncrypt: ICON_FILE_LIST_ENCRYPT,
        fileGridPdf: ICON_FILE_GRID_PDF,
        fileGridPaper: ICON_FILE_GRID_PAPER,
        fileGridEncrypt: ICON_FILE_GRID_ENCRYPT,
        pageGrid: ICON_PAGE_GRID,
        zoomIn: ICON_ZOOM_IN,
        zoomOut: ICON_ZOOM_OUT,
        close: ICON_CLOSE,
        record: RECORD_ICON,
        recentRecord: RECENT_RECORD_ICON,
        transcript: TRANSCRIPT_ICON,
        recordEmpty: RECORD_EMPTY_ICON
      },
      ui: {
        all: '\u5168\u90e8',
        favorite: '\u6536\u85cf',
        recentDeleted: '\u56de\u6536\u7ad9',
        search: '\u641c\u7d22',
        createFolder: '\u65b0\u5efa\u6587\u4ef6\u5939',
        importPdf: '\u5bfc\u5165 PDF',
        pdf: 'PDF',
        modifyTime: '\u4fee\u6539\u65f6\u95f4',
        createTime: '\u521b\u5efa\u65f6\u95f4',
        name: '\u540d\u79f0',
        desc: '\u5012\u5e8f',
        asc: '\u6b63\u5e8f',
        listView: '\u5217\u8868',
        gridView: '\u7f51\u683c',
        empty: '\u6682\u65e0\u6570\u636e',
        item: '\u9879',
        page: '\u9875',
        selectedPrefix: '\u5df2\u9009\u62e9',
        selectedSuffix: '\u9879',
        cancel: '\u53d6\u6d88',
        more: '\u66f4\u591a',
        download: '\u4e0b\u8f7d',
        rename: '\u91cd\u547d\u540d',
        move: '\u79fb\u52a8',
        encrypt: '\u52a0\u5bc6',
        decrypt: '\u53d6\u6d88\u52a0\u5bc6',
        encrypted: '\u5df2\u52a0\u5bc6',
        unfavorite: '\u53d6\u6d88\u6536\u85cf',
        delete: '\u5220\u9664',
        back: '\u8fd4\u56de',
        recordings: '\u5f55\u97f3',
        allRecordings: '\u5168\u90e8\u5f55\u97f3',
        transcript: '\u5f55\u97f3\u8f6c\u6587\u672c',
        pageGrid: '\u9875\u9762\u5217\u8868',
        zoomIn: '\u653e\u5927',
        zoomOut: '\u7f29\u5c0f',
        close: '\u5173\u95ed',
        fakeTip: '\u63a5\u53e3\u6682\u672a\u5f00\u653e\uff0c\u5df2\u7528\u5047\u63a5\u53e3\u5360\u4f4d'
      }
    }
  },
  computed: {
    contextPreviewMode() {
      return this.recentPreviewMode || this.myFilesRecordingPreviewMode || this.searchPreviewMode
    },
    filterOptions() {
      return [
        { key: 'all', label: this.ui.all, icon: this.paperIcons.filterAll },
        { key: 'pdf', label: this.ui.pdf || 'PDF', icon: this.paperIcons.filterPdf },
        { key: 'favorite', label: this.ui.favorite, icon: this.paperIcons.filterFavorite },
        { key: 'deleted', label: this.ui.recentDeleted, icon: this.paperIcons.filterDeleted }
      ]
    },
    showRootFilter() {
      return !this.paperListResourceId && this.paperFolderPath.length == 0
    },
    effectivePaperFilter() {
      if (this.paperFilter == 'deleted') {
        return 'deleted'
      }
      return this.showRootFilter ? this.paperFilter : 'all'
    },
    sortOptions() {
      const options = [
        { key: 'createTime', label: this.ui.createTime },
        { key: 'modifyTime', label: this.ui.modifyTime },
        { key: 'name', label: this.ui.name }
      ]
      return this.isPngOnlySortList ? options.filter(item => item.key != 'modifyTime') : options
    },
    currentListFolder() {
      const folderPath = this.normalizeCloudPaperFolderPath(this.paperFolderPath)
      return folderPath.length > 0 ? folderPath[folderPath.length - 1] : null
    },
    isPngOnlySortList() {
      const folder = this.currentListFolder
      if (!folder) {
        return false
      }
      const resourceType = Number(folder.resourceType)
      return folder.template === true || resourceType === 5
    },
    effectiveSortField() {
      return this.sortOptions.some(option => option.key == this.sortField) ? this.sortField : 'createTime'
    },
    activeFilterLabel() {
      const item = this.filterOptions.find(option => option.key == this.effectivePaperFilter)
      return item ? item.label : this.ui.all
    },
    activeFilterIcon() {
      const item = this.filterOptions.find(option => option.key == this.effectivePaperFilter)
      return item && item.icon ? item.icon : this.paperIcons.filterAll
    },
    activeSortLabel() {
      const item = this.sortOptions.find(option => option.key == this.effectiveSortField)
      return item ? item.label : this.ui.createTime
    },
    viewSwitchIcon() {
      return this.viewMode == 'list'
        ? this.paperIcons.viewList
        : this.paperIcons.viewGrid
    },
    selectionBarLayerStyle() {
      return {
        transform: `translateY(${this.selectionBarOffsetY}px)`,
        right: `${this.selectionBarScrollbarOffset}px`
      }
    },
    paperBreadcrumbItems() {
      return [{ name: 'Paper', resourceId: '', pathKey: 'root' }].concat(this.normalizeCloudPaperFolderPath(this.paperFolderPath))
    },
    paperDownloadTaskSummary() {
      const total = this.paperDownloadTasks.length
      const running = this.paperDownloadTasks.filter(task => task.state == 'running').length
      const failed = this.paperDownloadTasks.filter(task => task.state == 'error').length
      if (running > 0) {
        return `${running}/${total} ${this.ui.downloading}`
      }
      if (failed > 0) {
        return `${failed}/${total} ${this.ui.downloadFailed}`
      }
      return `${total}/${total} ${this.ui.downloadDone}`
    },
    interactionLocked() {
      return this.paperListLoading || this.paperDetailLoading || this.recordTextLoading || this.paperPasswordSubmitting
    },
    visiblePaperItems() {
      return this.paperItems.slice()
    },
    selectedPaperItems() {
      const selectedIds = new Set(this.selectedPaperIds)
      return this.paperItems.filter(item => selectedIds.has(item.id))
    },
    selectedPaper() {
      return this.selectedPaperItems[0] || null
    },
    selectedCount() {
      return this.selectedPaperItems.length
    },
    hasPaperSelection() {
      return this.selectedCount > 0
    },
    canBatchSelectionAction() {
      return this.selectedCount > 0 && this.selectedPaperItems.every(item => this.isPaperDownloadableItem(item))
    },
    canRenameSelected() {
      return this.selectedCount == 1
    },
    canEncryptSelected() {
      return this.selectedCount == 1 && !!this.selectedPaper
    },
    canUnfavoriteSelected() {
      return this.selectedCount == 1 && this.selectedPaper && this.selectedPaper.favorite
    },
    selectionEncryptLabel() {
      return this.selectedPaper && this.selectedPaper.encrypted ? this.ui.decrypt : this.ui.encrypt
    },
    paperDetailTitle() {
      if (this.paperDetail && this.paperDetail.name) {
        return this.paperDetail.name
      }
      return this.currentPaper && this.currentPaper.name ? this.currentPaper.name : 'Paper'
    },
    detailPages() {
      return this.paperDetail && Array.isArray(this.paperDetail.imagePages) ? this.paperDetail.imagePages : []
    },
    selectedDetailPages() {
      const selectedNos = new Set(this.selectedDetailPageNos)
      return this.detailPages.filter(page => selectedNos.has(page.pageNo))
    },
    detailRecordings() {
      return this.paperDetail && Array.isArray(this.paperDetail.recordings) ? this.paperDetail.recordings : []
    },
    currentDetailResourceType() {
      const detailResourceType = this.paperDetail && this.paperDetail.resourceType !== undefined && this.paperDetail.resourceType !== null && this.paperDetail.resourceType !== ''
        ? this.paperDetail.resourceType
        : this.currentPaper && this.currentPaper.resourceType
      const resourceType = Number(detailResourceType)
      return Number.isFinite(resourceType) ? resourceType : null
    },
    canShowRecordButton() {
      return this.currentDetailResourceType !== 8
    },
    canUsePageGridButton() {
      return this.currentDetailResourceType !== 8
    },
    currentPageIndex() {
      const index = this.detailPages.findIndex(page => page.pageNo == this.currentPageNo)
      return index >= 0 ? index : 0
    },
    currentPage() {
      return this.detailPages[this.currentPageIndex] || null
    },
    activeRecordingName() {
      return this.activeRecording && this.activeRecording.recordingName ? this.activeRecording.recordingName : ''
    },
    activeRecordingText() {
      if (!this.activeRecording) {
        return ''
      }
      if (this.recordTextLoading) {
        return this.ui.transcriptLoading
      }
      if (this.activeRecording.transcriptText) {
        return this.activeRecording.transcriptText
      }
      if (this.recordTextError) {
        return this.recordTextError
      }
      if (this.activeRecording.transcriptTextUrl) {
        return this.ui.transcriptPending
      }
      return this.ui.noTranscript
    }
  },
  watch: {
    language: {
      immediate: true,
      handler(value) {
        this.ui = Object.assign({}, getAppUiText(value), {
          recentDeleted: getTrashLabel(value)
        })
      }
    },
    viewMode() {
      this.updatePaperGridColumnCount()
      this.syncSelectionBarScrollbarOffset()
    },
    visiblePaperItems() {
      this.updatePaperGridColumnCount()
      this.syncSelectionBarScrollbarOffset()
    },
    pageGridOpen() {
      this.updatePaperPagesGridColumnCount()
    },
    sortOptions() {
      this.ensureSupportedSortField()
    },
    detailPages() {
      this.updatePaperPagesGridColumnCount()
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handlePaperWindowResize)
    }
    this.applyInitialRefreshState()
    if (!this.skipInitialListLoad) {
      this.loadPaperList()
    }
    this.updatePaperGridColumnCount()
    this.syncSelectionBarScrollbarOffset()
  },
  beforeDestroy() {
    this.paperListRequestSeq += 1
    this.paperDetailRequestSeq += 1
    this.paperPasswordRequestSeq += 1
    this.paperListPendingLoad = false
    if (!this.$hasRunningDownloadTasks()) {
      this.clearPaperDetailCache()
      this.clearPaperPreviewObjectUrls()
    }
    this.clearPaperItemClickTimer()
    this.stopSelectionBarDrag()
    this.stopPaperPreviewDrag()
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handlePaperWindowResize)
    }
  },
  methods: {
    applyInitialRefreshState() {
      const state = this.initialRefreshState && typeof this.initialRefreshState == 'object' ? this.initialRefreshState : null
      if (!state) {
        return
      }
      const folderPath = this.normalizeRefreshFolderPath(state.paperFolderPath)
      const resourceId = state.paperListResourceId === undefined || state.paperListResourceId === null ? '' : String(state.paperListResourceId).trim()
      this.paperFolderPath = folderPath
      this.paperListResourceId = resourceId || (folderPath.length > 0 ? folderPath[folderPath.length - 1].resourceId || '' : '')
      this.paperFilter = this.normalizeRefreshPaperFilter(state.paperFilter)
    },
    getRefreshState() {
      return {
        paperListResourceId: this.paperListResourceId || '',
        paperFolderPath: this.normalizeRefreshFolderPath(this.paperFolderPath),
        paperFilter: this.normalizeRefreshPaperFilter(this.paperFilter)
      }
    },
    normalizeRefreshPaperFilter(filter) {
      const key = String(filter || '')
      return this.filterOptions.some(option => option.key == key) ? key : 'all'
    },
    normalizeRefreshFolderPath(folderPath) {
      return this.normalizeCloudPaperFolderPath(folderPath)
    },
    ensureSupportedSortField() {
      if (!this.sortOptions.some(option => option.key == this.sortField)) {
        this.sortField = 'createTime'
      }
    },
    loadPaperList(options = {}) {
      const pageOptions = this.normalizePaperListLoadOptions(options)
      if (this.paperListLoading || this.paperListAppendLoading) {
        this.paperListPendingLoad = pageOptions
        return Promise.resolve()
      }
      const preserveScrollTop = pageOptions.preserveScroll && this.$refs.paperListArea ? this.$refs.paperListArea.scrollTop : null
      const requestId = this.paperListRequestSeq + 1
      this.paperListRequestSeq = requestId
      this.paperListLoading = !pageOptions.append && !pageOptions.silent
      this.paperListAppendLoading = pageOptions.append
      return getPaperSyncList(this.buildPaperListParams(pageOptions.pageIndex)).then(ret => {
        if (!this.isActivePaperListRequest(requestId)) {
          return
        }
        const payload = this.resolveApiPayload(ret)
        if (payload === null) {
          this.handlePaperListLoadFailed(ret)
          return
        }
        this.applyPaperListPage(payload, this.normalizePaperList(payload), pageOptions)
      }).catch(error => {
        if (!this.isActivePaperListRequest(requestId)) {
          return
        }
        this.handlePaperListLoadFailed(error)
      }).finally(() => {
        if (!this.isActivePaperListRequest(requestId)) {
          return
        }
        this.syncSelectionWithItems()
        this.paperListLoading = false
        this.paperListAppendLoading = false
        this.updatePaperGridColumnCount()
        if (preserveScrollTop !== null) {
          this.$nextTick(() => {
            if (this.$refs.paperListArea) {
              this.$refs.paperListArea.scrollTop = preserveScrollTop
            }
          })
        }
        if (this.paperListPendingLoad) {
          const pendingOptions = this.paperListPendingLoad
          this.paperListPendingLoad = false
          this.loadPaperList(pendingOptions)
        }
      })
    },
    isActivePaperListRequest(requestId) {
      return requestId == this.paperListRequestSeq
    },
    buildPaperListParams(pageIndex = this.paperListPageIndex) {
      const filter = this.effectivePaperFilter
      return {
        pageIndex,
        pageSize: this.paperListPageSize,
        appType: PAPER_APP_TYPE,
        resourceId: this.paperListResourceId,
        isDelete: filter == 'deleted' ? 2 : -1,
        subTab: -1,
        resourceType: filter == 'pdf' ? 4 : -1,
        star: filter == 'favorite' ? 1 : -1,
        sortField: this.toResourceSortField(this.effectiveSortField),
        sortOrder: this.sortDesc ? 'desc' : 'asc'
      }
    },
    toResourceSortField(field) {
      if (field == 'createTime') {
        return 'creation_time'
      }
      if (field == 'modifyTime') {
        return 'last_modified_time'
      }
      if (field == 'name') {
        return 'name'
      }
      return ''
    },
    async loadPaperDetail(item, options = {}) {
      const password = options.password || ''
      const requestId = this.paperDetailRequestSeq + 1
      const cacheKey = this.getPaperDetailCacheKey(item, password)
      this.paperDetailRequestSeq = requestId
      this.paperDetailLoading = true
      this.paperDetailProgress = 0
      this.paperDetailError = ''
      this.clearPaperPreviewObjectUrls({ revoke: false })
      this.paperDetail = null
      this.currentPageNo = 1
      this.pageGridOpen = false
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = ''
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetPaperZoom()
      this.clearPreviewZoomTip()

      const isDirectSourceItem = this.isDirectFilePreviewItem(item)
      const cachedDetail = !options.force && !isDirectSourceItem ? this.getCachedPaperDetail(cacheKey) : null
      if (cachedDetail) {
        this.paperPreviewObjectUrls = cachedDetail.objectUrls || []
        this.applyPaperDetail(cachedDetail.detail, item)
        this.setPaperDetailProgress(100, requestId)
        this.paperDetailLoading = false
        return true
      }

      let directFallbackSource = null
      try {
        this.setPaperDetailProgress(5, requestId)
        const hasPrefetchedRet = Object.prototype.hasOwnProperty.call(options, 'prefetchedRet')
        const ret = isDirectSourceItem
          ? await this.fetchPaperDetailResponse(item, password)
          : (hasPrefetchedRet
              ? await Promise.resolve(options.prefetchedRet)
              : await this.fetchPaperDetailResponse(item, password))
        if (requestId != this.paperDetailRequestSeq) {
          return false
        }
        directFallbackSource = ret
        this.setPaperDetailProgress(18, requestId)
        if (password && this.isPaperPasswordCodeError(ret)) {
          throw new Error(this.getPaperPasswordErrorMessage())
        }
        let rawDetail
        if (this.isPaperApiCodeFailed(ret)) {
          if (isDirectSourceItem && this.canFallbackDirectFilePreview(item, ret, password)) {
            rawDetail = this.createDirectFilePreviewFallbackDetail(item, {})
          } else {
            throw new Error(this.getPaperApiErrorMessage(ret) || 'Paper detail failed')
          }
        } else {
          const payload = this.resolveApiPayload(ret)
          if (payload === null && isDirectSourceItem) {
            rawDetail = this.createDirectFilePreviewFallbackDetail(item, {})
          } else if (payload !== null) {
            rawDetail = this.normalizePaperDetail(payload, item)
          }
        }
        if (!rawDetail) {
          throw new Error('Paper detail failed')
        }
        this.setPaperDetailProgress(25, requestId)
        const prepared = await this.preparePaperPreviewDetail(rawDetail, progress => {
          this.setPaperDetailProgress(progress, requestId)
        })
        if (requestId != this.paperDetailRequestSeq) {
          this.revokePaperPreviewObjectUrls(prepared.objectUrls)
          return false
        }
        this.paperPreviewObjectUrls = prepared.objectUrls || []
        this.applyPaperDetail(prepared.detail, item)
        if (!isDirectSourceItem) {
          this.setPaperDetailCache(cacheKey, prepared.detail, prepared.objectUrls)
        }
        this.setPaperDetailProgress(100, requestId)
        return true
      } catch (error) {
        if (requestId != this.paperDetailRequestSeq) {
          return false
        }
        let detailError = error
        const fallbackSource = directFallbackSource || error
        if (isDirectSourceItem && this.canFallbackDirectFilePreview(item, fallbackSource, password)) {
          try {
            const fallbackDetail = this.createDirectFilePreviewFallbackDetail(item, {})
            if (fallbackDetail) {
              const preparedFallback = await this.preparePaperPreviewDetail(fallbackDetail, progress => {
                this.setPaperDetailProgress(progress, requestId)
              })
              if (requestId != this.paperDetailRequestSeq) {
                this.revokePaperPreviewObjectUrls(preparedFallback.objectUrls)
                return false
              }
              this.paperPreviewObjectUrls = preparedFallback.objectUrls || []
              this.applyPaperDetail(preparedFallback.detail, item)
              this.setPaperDetailProgress(100, requestId)
              return true
            }
          } catch (fallbackError) {
            detailError = fallbackError
          }
        }
        this.paperDetail = null
        this.clearPaperPreviewObjectUrls({ revoke: false })
        this.paperDetailError = password && this.isPaperPasswordCodeError(detailError)
          ? this.getPaperPasswordErrorMessage()
          : this.getPaperDetailErrorMessage(detailError)
        return false
      } finally {
        if (requestId == this.paperDetailRequestSeq) {
          this.paperDetailLoading = false
        }
      }
    },
    retryPaperDetailLoad() {
      if (this.paperDetailLoading || !this.currentPaper) {
        return
      }
      this.loadPaperDetail(this.currentPaper, { force: true, password: this.currentPaperPassword || '' })
    },
    applyPaperDetail(detail, sourceItem) {
      this.paperDetail = detail
      if (detail && detail.imagePages && detail.imagePages.length > 0) {
        this.currentPageNo = this.resolvePaperInitialPageNo(detail, sourceItem)
      }
      this.centerPaperPreviewScroll()
    },
    setPaperDetailProgress(progress, requestId) {
      if (requestId && requestId != this.paperDetailRequestSeq) {
        return
      }
      const value = Math.max(0, Math.min(100, Math.round(Number(progress) || 0)))
      this.paperDetailProgress = Math.max(this.paperDetailProgress || 0, value)
    },
    getPaperDetailCacheKey(item, password = '') {
      const resourceId = item && item.resourceId ? String(item.resourceId) : ''
      if (!resourceId) {
        return ''
      }
      return this.isPaperEncryptedItem(item) ? `${resourceId}:${password || 'encrypted'}` : resourceId
    },
    getPaperDetailSharedCacheScope() {
      return this.$options && this.$options.name ? this.$options.name : 'PaperSync'
    },
    getCachedPaperDetail(cacheKey) {
      if (!cacheKey) {
        return null
      }
      const cached = this.paperDetailCache && this.paperDetailCache[cacheKey]
      if (cached && cached.detail) {
        return cached
      }
      const shared = typeof this.getSharedPaperPreviewDetailCache == 'function'
        ? this.getSharedPaperPreviewDetailCache(this.getPaperDetailSharedCacheScope(), cacheKey)
        : null
      if (shared && shared.detail && this.paperDetailCache) {
        this.$set(this.paperDetailCache, cacheKey, shared)
      }
      return shared && shared.detail ? shared : null
    },
    setPaperDetailCache(cacheKey, detail, objectUrls) {
      if (!cacheKey || !detail) {
        return
      }
      const previous = this.paperDetailCache ? this.paperDetailCache[cacheKey] : null
      const entry = typeof this.setSharedPaperPreviewDetailCache == 'function'
        ? this.setSharedPaperPreviewDetailCache(this.getPaperDetailSharedCacheScope(), cacheKey, detail, objectUrls)
        : {
            detail,
            objectUrls: Array.isArray(objectUrls) ? objectUrls.slice() : [],
            cachedAt: Date.now()
          }
      if (previous && previous.objectUrls && previous.objectUrls !== objectUrls && previous !== entry) {
        this.revokePaperPreviewObjectUrls(previous.objectUrls)
      }
      this.$set(this.paperDetailCache, cacheKey, entry)
      this.trimPaperPreviewDetailCache(this.paperDetailCache, cacheKey)
    },
    getPaperDetailErrorMessage(error) {
      return this.getPaperApiErrorMessage(error) || this.ui.detailLoadFailed
    },
    getPaperPasswordErrorMessage() {
      return this.ui.passwordWrong
    },
    fetchPaperDetailResponse(item, password = '') {
      const resourceId = item && item.resourceId ? item.resourceId : ''
      const params = {
        uuid: resourceId
      }
      if (password || this.isPaperEncryptedItem(item)) {
        params.password = password
      }
      return getPaperSyncDetail(params, { skipGlobalErrorMessage: true })
    },
    getPaperApiResponseData(source) {
      const responseData = source && source.response && source.response.data ? source.response.data : null
      return responseData || (source && source.data ? source.data : source)
    },
    getPaperApiCode(source) {
      const data = this.getPaperApiResponseData(source)
      if (!data || typeof data != 'object') {
        return undefined
      }
      return this.pickValue(data, ['code', 'statusCode'])
    },
    isPaperApiCodeFailed(source) {
      const code = this.getPaperApiCode(source)
      if (code === undefined || code === null || code === '') {
        return false
      }
      return code != 200 && code != '200'
    },
    isPaperPasswordCodeError(source) {
      const code = this.getPaperApiCode(source)
      return code == 10013 || code == '10013'
    },
    resolveApiPayload(ret) {
      const directPayload = this.resolveDirectFilePreviewApiPayload(ret)
      if (directPayload !== null) {
        return directPayload
      }
      const data = ret && ret.data ? ret.data : ret
      if (!data) {
        return null
      }
      if (data.code == 200 || data.code == '200') {
        return data.data === undefined ? {} : data.data
      }
      if (data.success === true) {
        return data.data === undefined ? {} : data.data
      }
      if (Array.isArray(data) || data.list || data.records || data.items || Object.prototype.hasOwnProperty.call(data, 'meta') || Object.prototype.hasOwnProperty.call(data, 'resource') || data.paperMeeting) {
        return data
      }
      return null
    },
    handlePaperListLoadFailed(error) {
      this.resetPaperListPagination()
      this.paperItems = []
      this.clearSelection()
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.showPaperListLoadError(error)
    },
    showPaperListLoadError(error) {
      const message = this.getPaperApiErrorMessage(error) || this.ui.dataLoadFailed.replace('{app}', 'Paper')
      if (typeof this.$error == 'function') {
        this.$error({
          title: this.ui.notice,
          content: message,
          okButtonProps: {
            style: {
              backgroundColor: '#1f3a2d',
              borderColor: '#1f3a2d'
            }
          }
        })
        return
      }
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(message)
      }
    },
    getPaperApiErrorMessage(error) {
      const responseData = error && error.response && error.response.data ? error.response.data : null
      const data = responseData || (error && error.data ? error.data : error)
      if (data && typeof data == 'object') {
        return this.pickString(data, ['msg', 'message', 'errorMessage', 'error'])
      }
      if (error && error.message) {
        return error.message
      }
      return ''
    },
    showPaperDetailRequestError(error) {
      const message = this.getPaperDetailErrorMessage(error)
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(message)
      }
    },
    normalizePaperList(payload) {
      const list = this.extractArray(payload)
      return list.map((item, index) => this.normalizePaperItem(item, index))
    },
    extractArray(payload, depth = 0) {
      if (Array.isArray(payload)) {
        return payload
      }
      if (!payload || typeof payload != 'object' || depth > 4) {
        return []
      }
      const keys = ['list', 'items', 'records', 'rows', 'data', 'resourceList', 'paperList']
      for (let i = 0; i < keys.length; i++) {
        if (Array.isArray(payload[keys[i]])) {
          return payload[keys[i]]
        }
      }
      const values = Object.keys(payload).map(key => payload[key])
      for (let i = 0; i < values.length; i++) {
        const nested = this.extractArray(values[i], depth + 1)
        if (nested.length > 0) {
          return nested
        }
      }
      return []
    },
    normalizePaperItem(raw, index) {
      const item = raw && typeof raw == 'object' ? raw : {}
      const meta = this.parseMeta(this.pickValue(item, ['meta']))
      const paperMeeting = this.resolvePaperMeetingMeta(meta)
      const imagePages = Array.isArray(paperMeeting.imagePages) ? paperMeeting.imagePages : []
      const explicitResourceId = this.pickValue(item, ['resourceId'])
      const fallbackResourceId = this.pickString(item, ['uuid', 'id', 'resourceUuid', 'paperId'])
      const resourceId = explicitResourceId !== undefined && explicitResourceId !== null
        ? String(explicitResourceId).trim()
        : fallbackResourceId
      const resourceType = this.pickValue(item, ['resourceType', 'resource_type', 'resrouceType', 'resrouce_type'])
      const numericResourceType = Number(resourceType)
      const hasNumericResourceType = resourceType !== undefined && resourceType !== null && resourceType !== '' && Number.isFinite(numericResourceType)
      const isFolder = hasNumericResourceType
        ? numericResourceType === 0 || numericResourceType === 5
        : this.pickBoolean(item, ['isFolder', 'folder', 'isDir', 'directory'])
      const isTemplate = hasNumericResourceType
        ? numericResourceType === 5
        : this.pickBoolean(item, ['template', 'isTemplate', 'templateFolder'])
      const name = this.pickString(item, ['name', 'title', 'fileName', 'resourceName', 'paperName']) || paperMeeting.name || `\u5f55\u97f3 ${index + 1}`
      const cover = this.pickString(item, ['cover'])
        || this.pickString(item, ['coverUrl', 'imageUrl', 'thumbnail', 'thumbnailUrl'])
      const gridCover = this.pickPaperCoverImageUrl(item)
      const filePath = this.pickString(item, ['filePath', 'filepath'])
        || this.pickString(item, ['file_path', 'path', 'fileUrl', 'url', 'src'])
        || this.pickString(paperMeeting, ['filePath', 'file_path', 'path', 'fileUrl', 'url', 'src'])
      const pageCount = this.pickValue(item, ['pageCount', 'pages', 'pageNum', 'imagePageCount', 'totalPage', 'childSize'])
      const childSize = this.pickValue(item, ['childSize', 'child_size', 'childrenSize', 'childCount', 'childrenCount'])
      const fileSize = this.pickValue(item, ['fileSize', 'fileSizeBytes', 'size'])
      const pathKey = this.getPaperFolderPathKey({ resourceId, resourceType, name, filePath }, index)
      const id = this.getPaperItemKey({ resourceId, resourceType, name, filePath }, index) || resourceId || fallbackResourceId || `paper-${index}`
      const createdAt = this.pickValue(item, ['creationTime', 'createdAt', 'createAt', 'createTime', 'ctime', 'createDate', 'createdDate', 'createdTime', 'creationDate', 'gmtCreate'])
        || this.pickValue(paperMeeting, ['creationTime', 'createdAt', 'createAt', 'createTime', 'ctime', 'createDate', 'createdDate', 'createdTime', 'creationDate', 'gmtCreate'])
      const updatedAt = this.pickValue(item, ['lastModifiedTime', 'updatedAt', 'updateAt', 'modifyAt', 'modifyTime', 'modifiedAt', 'modifiedTime', 'mtime', 'updateTime', 'updatedTime', 'updateDate', 'updatedDate', 'lastModified', 'lastUpdateTime', 'gmtModify', 'gmtModified'])
        || this.pickValue(paperMeeting, ['lastModifiedTime', 'updatedAt', 'updateAt', 'modifyAt', 'modifyTime', 'modifiedAt', 'modifiedTime', 'mtime', 'updateTime', 'updatedTime', 'updateDate', 'updatedDate', 'lastModified', 'lastUpdateTime', 'gmtModify', 'gmtModified'])
      const currentPage = this.pickPaperCurrentPageValue(paperMeeting, meta, item)
      return {
        id,
        pathKey,
        resourceId,
        resourceType,
        name,
        currentPage,
        cover,
        gridCover,
        filePath,
        fileSize,
        fileSizeBytes: fileSize,
        pageCount: pageCount === undefined || pageCount === null || pageCount === '' ? imagePages.length : pageCount,
        childSize: childSize === undefined || childSize === null || childSize === '' ? pageCount : childSize,
        updatedAt,
        createdAt,
        lastModifiedTime: updatedAt,
        creationTime: createdAt,
        favorite: this.pickBoolean(item, ['favorite', 'isFavorite', 'starred', 'isStar', 'star', 'start']),
        deleted: this.pickDeletedFlag(item),
        encrypted: this.pickBoolean(item, ['encrypted', 'isEncrypted', 'locked', 'isLock', 'encrypt', 'encryp']),
        isFolder,
        template: isTemplate,
        openFolder: this.pickBoolean(item, ['openFolder']),
        raw: item,
        meta
      }
    },
    normalizePaperDetail(payload, sourceItem) {
      const raw = payload && typeof payload == 'object' ? payload : {}
      const detail = raw.resource && typeof raw.resource == 'object' ? raw.resource : raw
      const isDirectSourceItem = this.isDirectFilePreviewItem(sourceItem)
      const sourceResourceType = this.getDirectFilePreviewResourceType(sourceItem)
      const shouldPreferDetailMetaForSourcePath = isDirectSourceItem && [7, 8].includes(Number(sourceResourceType))
      const detailMeta = this.resolveDirectFilePreviewMeta(raw, { preferEmpty: isDirectSourceItem })
      const isDetailMetaEmpty = this.isDirectFilePreviewMetaEmpty(detailMeta)
      const directFallbackDetail = isDetailMetaEmpty
        ? this.createDirectFilePreviewFallbackDetail(sourceItem, detailMeta)
        : null
      if (directFallbackDetail) {
        return directFallbackDetail
      }
      const canUseSourceDirectPath = isDirectSourceItem && (!shouldPreferDetailMetaForSourcePath || isDetailMetaEmpty)
      const sourceMeta = sourceItem && sourceItem.meta ? sourceItem.meta : {}
      const meta = Object.assign({}, sourceMeta, detailMeta)
      if (detail.paperMeeting && typeof detail.paperMeeting == 'object') {
        meta.paperMeeting = detail.paperMeeting
      }
      const paperMeeting = this.resolvePaperMeetingMeta(meta)
      const fallbackName = sourceItem && sourceItem.name ? sourceItem.name : 'Paper'
      const resourceType = this.pickValue(paperMeeting, ['resourceType', 'resource_type', 'resrouceType', 'resrouce_type'])
        || this.pickValue(meta, ['resourceType', 'resource_type', 'resrouceType', 'resrouce_type'])
        || this.pickValue(detail, ['resourceType', 'resource_type', 'resrouceType', 'resrouce_type'])
        || this.pickValue(sourceItem || {}, ['resourceType', 'resource_type', 'resrouceType', 'resrouce_type'])
        || sourceResourceType
      const resourceUrl = this.pickString(paperMeeting, ['resourceUrl', 'resource_url', 'fileUrl', 'url'])
        || this.pickString(meta, ['resourceUrl', 'resource_url', 'fileUrl', 'url'])
        || this.pickString(detail, ['resourceUrl', 'resource_url', 'fileUrl', 'url'])
        || (canUseSourceDirectPath ? this.pickDirectFilePreviewPath(detail) : '')
      const metaPages = Array.isArray(paperMeeting.imagePages)
        ? paperMeeting.imagePages
        : this.extractArray(this.pickValue(paperMeeting, ['imagePages', 'pages', 'pageList']))
      const rawPages = metaPages.length > 0
        ? metaPages
        : this.extractArray(this.pickValue(detail, ['imagePages', 'pages', 'pageList']))
      const isPdfResource = this.isPaperPdfUrl(resourceUrl)
      const allowPageFallback = !(sourceItem && sourceItem.disableDownloadPageFallback) && !(isPdfResource || (Number(resourceType) === 5 && resourceUrl))
      const imagePages = this.normalizeImagePages(rawPages, sourceItem, allowPageFallback, { pdfResource: isPdfResource })
      const metaRecordings = Array.isArray(paperMeeting.recordings)
        ? paperMeeting.recordings
        : this.extractArray(this.pickValue(paperMeeting, ['recordings', 'recordingList', 'audios']))
      const rawRecordings = metaRecordings.length > 0
        ? metaRecordings
        : this.extractArray(this.pickValue(detail, ['recordings', 'recordingList', 'audios']))
      const currentPage = this.pickPaperCurrentPageValue(paperMeeting, meta, detail, sourceItem || {})
      const detailFilePath = this.pickDirectFilePreviewString(detail, ['filePath', 'filepath', 'file_path', 'path'])
      const sourceFilePath = canUseSourceDirectPath ? this.pickDirectFilePreviewPath(sourceItem) : ''
      return {
        name: this.pickString(paperMeeting, ['name', 'title', 'resourceName', 'paperName']) || this.pickString(detail, ['name', 'title', 'resourceName']) || fallbackName,
        currentPage,
        resourceType,
        resourceUrl,
        filePath: detailFilePath || sourceFilePath,
        isDirectFilePreview: !!(detail && detail.isDirectFilePreview) || canUseSourceDirectPath,
        imagePages,
        recordings: this.normalizeRecordings(rawRecordings)
      }
    },
    normalizeImagePages(rawPages, sourceItem, allowFallback = true, options = {}) {
      const isPdfResource = !!(options && options.pdfResource)
      const pages = Array.isArray(rawPages) ? rawPages : []
      const normalized = pages.map((page, index) => {
        const item = page && typeof page == 'object' ? page : { imageUrl: page }
        const pageNo = Number(this.pickValue(item, ['pageNo', 'page', 'index', 'no'])) || index + 1
        const overlayImageUrl = this.pickPaperOverlayImageUrl(item)
        const displayImageUrl = this.pickString(item, ['imageUrl', 'image_url', 'url', 'src'])
        return {
          pageNo,
          imageUrl: isPdfResource
            ? (overlayImageUrl || displayImageUrl)
            : (this.pickPaperBestImageUrl(item) || this.pickString(item, ['imageUrl', 'url', 'src', 'cover'])),
          overlayImageUrl,
          pageImageUrl: this.pickPaperPageImageUrl(item)
        }
      }).filter(page => page.imageUrl || page.pageImageUrl || page.pageNo)
        .sort((a, b) => a.pageNo - b.pageNo)
      if (normalized.length > 0 || !allowFallback) {
        return normalized
      }
      const cover = this.pickPaperBestImageUrl(sourceItem, { includeThumbnail: false }) || (sourceItem && sourceItem.cover ? sourceItem.cover : MOCK_PAGE_IMAGE)
      return [{ pageNo: 1, imageUrl: cover }]
    },
    normalizeRecordings(rawRecordings) {
      const recordings = Array.isArray(rawRecordings) ? rawRecordings : []
      return recordings.map((recording, index) => {
        const item = recording && typeof recording == 'object' ? recording : {}
        const fileUrl = this.pickString(item, ['fileUrl', 'audioUrl', 'recordingUrl', 'url', 'src'])
        const fallbackTextUrl = this.pickString(item, ['url', 'src', 'fileUrl'])
        const explicitTextUrl = this.pickString(item, [
          'transcriptTextUrl',
          'transcriptUrl',
          'transcriptFileUrl',
          'transcriptFile',
          'textUrl',
          'textFileUrl',
          'txtUrl',
          'txtFileUrl',
          'contentUrl',
          'contentFileUrl',
          'recordTextUrl',
          'recordingTextUrl',
          'fileTextUrl',
          'asrTextUrl',
          'asrUrl'
        ])
        const transcriptTextUrl = explicitTextUrl || (this.isTranscriptTextFileUrl(fallbackTextUrl) ? fallbackTextUrl : '')
        return {
          id: this.pickString(item, ['id', 'recordingId', 'uuid', 'fileUrl']) || `recording-${index}`,
          recordingName: this.pickString(item, ['recordingName', 'name', 'title', 'fileName']) || `\u5f55\u97f3 ${index + 1}`,
          duration: this.pickValue(item, ['duration', 'durationMs', 'durationMillis']),
          fileSizeBytes: this.pickValue(item, ['fileSizeBytes', 'size', 'fileSize']),
          recordedAtMs: this.pickValue(item, ['recordedAtMs', 'recordedAt', 'createTime', 'createdAt']),
          fileUrl: this.isTranscriptTextFileUrl(fileUrl) ? '' : fileUrl,
          transcriptTextUrl,
          transcriptText: this.pickString(item, ['transcriptText', 'text', 'content'])
        }
      })
    },
    getMockPaperItems() {
      return [
        { id: 'mock-template', resourceId: '', resourceType: 5, name: '\u6211\u7684\u6a21\u677f', cover: '', pageCount: 4, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: true, openFolder: false, template: true },
        { id: 'mock-folder-1', resourceId: '', name: '\u6587\u4ef6\u5939 1', cover: '', pageCount: 4, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: true, isFolder: true, openFolder: true },
        { id: 'mock-folder-2', resourceId: '', name: '\u6587\u4ef6\u5939 2', cover: '', pageCount: 4, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: true, openFolder: false },
        { id: 'mock-paper-1', resourceId: 'mock-paper-1', name: '\u6587\u4ef6\u540d\u79f0\u6587\u4ef6\u540d\u6587\u4ef6\u540d\u79f0\u6587\u4ef6', cover: MOCK_SMALL_PAGE_IMAGE, pageCount: 23, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: false },
        { id: 'mock-paper-2', resourceId: 'mock-paper-2', name: '\u6708\u8ba1\u5212', cover: MOCK_TEMPLATE_IMAGE, pageCount: 13, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: true, isFolder: false },
        { id: 'mock-paper-3', resourceId: 'mock-paper-3', name: '\u6211\u7684\u7b14\u8bb0', cover: '', pageCount: 13, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: true, encrypted: true, isFolder: false },
        { id: 'mock-paper-4', resourceId: 'mock-paper-4', name: '\u65e5\u5e38\u8bb0\u5f55', cover: MOCK_PAGE_IMAGE, pageCount: 13, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: false }
      ]
    },
    getMockPaperDetail(item) {
      const name = item && item.name ? item.name : '\u65e5\u5e38\u8bb0\u5f55'
      const pages = []
      for (let i = 1; i <= 23; i++) {
        pages.push({
          pageNo: i,
          imageUrl: i == 1 ? (item && item.cover && !item.encrypted ? item.cover : MOCK_PAGE_IMAGE) : MOCK_PAGE_IMAGE
        })
      }
      return {
        name,
        imagePages: pages,
        recordings: [
          { id: 'mock-recording-1', recordingName: '\u5f55\u97f3\u540d\u79f0', duration: 158000, recordedAtMs: 1780056000000, fileUrl: '', transcriptText: '\u5404\u4f4d\u540c\u4e8b\uff0c\u6211\u4eec\u6765\u5bf9\u4e00\u4e0b\u8fd9\u5468\u7684\u4ea7\u54c1\u8fdb\u5ea6\u3002' },
          { id: 'mock-recording-2', recordingName: '\u5f55\u97f3\u540d\u79f0', duration: 158000, recordedAtMs: 1780056000000, fileUrl: '', transcriptTextUrl: 'mock://recording-2.txt' }
        ]
      }
    },
    parseMeta(value) {
      if (!value) {
        return {}
      }
      if (typeof value == 'object') {
        return value
      }
      const text = String(value).trim()
      if (!text) {
        return {}
      }
      const directMeta = this.parseMetaJson(text)
      if (directMeta) {
        return directMeta
      }
      const decodedText = this.decodeGzipBase64Text(text)
      if (!decodedText) {
        return {}
      }
      const decodedMeta = this.parseMetaJson(decodedText)
      return decodedMeta || {}
    },
    parseMetaJson(value) {
      try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed == 'object' ? parsed : null
      } catch (error) {
        return null
      }
    },
    decodeGzipBase64Text(value) {
      try {
        const base64 = String(value || '').replace(/\s/g, '').replace(/-/g, '+').replace(/_/g, '/')
        if (!base64) {
          return ''
        }
        const padding = base64.length % 4
        const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64
        const binary = typeof window !== 'undefined' && typeof window.atob == 'function'
          ? window.atob(paddedBase64)
          : atob(paddedBase64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i)
        }
        return pako.ungzip(bytes, { to: 'string' })
      } catch (error) {
        return ''
      }
    },
    resolvePaperMeetingMeta(meta) {
      if (!meta || typeof meta != 'object') {
        return {}
      }
      const nested = this.pickValue(meta, ['paperMeeting', 'paper_meeting', 'paper', 'meeting'])
      if (nested && typeof nested == 'object') {
        return nested
      }
      return meta
    },
    pickPaperCurrentPageValue(...sources) {
      const keys = ['currentPage', 'current_page']
      for (let i = 0; i < sources.length; i++) {
        const source = sources[i]
        if (!source || typeof source != 'object') {
          continue
        }
        const value = this.pickValue(source, keys)
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          return value
        }
      }
      return undefined
    },
    hasPaperCurrentPageField(...sources) {
      const keys = ['currentPage', 'current_page']
      for (let i = 0; i < sources.length; i++) {
        const source = sources[i]
        if (!source || typeof source != 'object') {
          continue
        }
        const value = this.pickValue(source, keys)
        if (value !== undefined && value !== null) {
          return true
        }
      }
      return false
    },
    toPaperCurrentPageNo(value) {
      const pageNo = Number(value)
      return Number.isFinite(pageNo) && pageNo > 0 ? Math.floor(pageNo) : 0
    },
    resolvePaperInitialPageNo(detail, sourceItem) {
      const pages = detail && Array.isArray(detail.imagePages) ? detail.imagePages : []
      if (pages.length == 0) {
        return 1
      }
      const fallbackPageNo = pages[0].pageNo || 1
      const sourceMeta = sourceItem && sourceItem.meta && typeof sourceItem.meta == 'object'
        ? sourceItem.meta
        : this.parseMeta(this.pickValue(sourceItem || {}, ['meta']))
      const sourcePaperMeeting = this.resolvePaperMeetingMeta(sourceMeta)
      const sourceCurrentPageSources = [sourcePaperMeeting, sourceMeta, sourceItem || {}]
      const currentPageValue = this.hasPaperCurrentPageField(...sourceCurrentPageSources)
        ? this.pickPaperCurrentPageValue(...sourceCurrentPageSources)
        : this.pickPaperCurrentPageValue(...sourceCurrentPageSources, detail || {})
      const targetPageNo = this.toPaperCurrentPageNo(currentPageValue)
      if (!targetPageNo) {
        return fallbackPageNo
      }
      const matchedPage = pages.find(page => Number(page.pageNo) == targetPageNo)
      return matchedPage ? matchedPage.pageNo : fallbackPageNo
    },
    pickValue(source, keys) {
      if (!source || typeof source != 'object') {
        return undefined
      }
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          return source[key]
        }
      }
      const sourceKeys = Object.keys(source)
      for (let i = 0; i < keys.length; i++) {
        const normalized = this.normalizeFieldKey(keys[i])
        const matched = sourceKeys.find(key => this.normalizeFieldKey(key) == normalized)
        if (matched) {
          return source[matched]
        }
      }
      return undefined
    },
    pickString(source, keys) {
      const value = this.pickValue(source, keys)
      if (value === undefined || value === null) {
        return ''
      }
      return String(value).trim()
    },
    pickBoolean(source, keys) {
      const value = this.pickValue(source, keys)
      if (typeof value == 'boolean') {
        return value
      }
      if (value === undefined || value === null || value === '') {
        return false
      }
      const normalized = this.normalizeFieldKey(value)
      return ['true', '1', 'yes', 'y', 'favorite', 'starred', 'deleted', 'folder', 'dir', 'locked', 'encrypted'].includes(normalized)
    },
    pickDeletedFlag(source) {
      const value = this.pickValue(source, ['deleted', 'isDeleted', 'inTrash', 'isDelete'])
      if (typeof value == 'boolean') {
        return value
      }
      if (value === undefined || value === null || value === '') {
        return false
      }
      const numericValue = Number(value)
      if (Number.isFinite(numericValue)) {
        return numericValue === 2
      }
      const normalized = this.normalizeFieldKey(value)
      return ['true', '2', 'yes', 'y', 'deleted', 'trash', 'trashed', 'recycle', 'recycled', 'removed'].includes(normalized)
    },
    isPaperEncryptedItem(item) {
      if (!item) {
        return false
      }
      if (item.encrypted) {
        return true
      }
      const raw = item.raw && typeof item.raw == 'object' ? item.raw : item
      return this.pickBoolean(raw, ['encrypted', 'isEncrypted', 'locked', 'isLock', 'encrypt', 'encryp'])
    },
    getPaperDisplayName(item) {
      const name = item && item.name !== undefined && item.name !== null ? String(item.name).trim() : ''
      const isTemplateFolder = Number(item && item.resourceType) === 5
      return getTemplateFolderDisplayName(name, this.language, isTemplateFolder)
    },
    normalizeFieldKey(value) {
      return String(value || '').toLowerCase().replace(/[\s_\-/.]/g, '')
    },
    comparePaperItems(a, b) {
      const sortField = this.effectiveSortField
      let result = 0
      if (sortField == 'name') {
        result = String(a.name || '').localeCompare(String(b.name || ''))
      } else {
        const aValue = this.getSortTime(a, sortField == 'createTime' ? 'creationTime' : 'lastModifiedTime')
        const bValue = this.getSortTime(b, sortField == 'createTime' ? 'creationTime' : 'lastModifiedTime')
        result = aValue - bValue
      }
      return this.sortDesc ? -result : result
    },
    getSortTime(item, field) {
      const value = item && item[field] ? item[field] : ''
      const timestamp = this.parseDateTime(value)
      return timestamp || 0
    },
    getDisplayDate(item) {
      return item && (item.creationTime || item.createdAt || item.createTime)
    },
    isPaperSelected(item) {
      return !!(item && this.selectedPaperIds.includes(item.id))
    },
    getPaperGridItemClasses(item, index) {
      return {
        folder: item && item.isFolder,
        selected: this.isPaperSelected(item),
        'grid-edge-left': this.isPaperGridLeftEdge(index),
        'grid-edge-right': this.isPaperGridRightEdge(index),
        'grid-edge-top': this.isPaperGridTopEdge(index),
        'grid-edge-bottom': this.isPaperGridBottomEdge(index)
      }
    },
    isPaperGridLeftEdge(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      if (this.isPaperGridLastIncompleteRowLastItem(index)) {
        return false
      }
      return columnCount == 1 || index % columnCount == 0
    },
    isPaperGridRightEdge(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      if (this.isPaperGridLastIncompleteRowLastItem(index)) {
        return false
      }
      return columnCount == 1 || (index + 1) % columnCount == 0
    },
    isPaperGridTopEdge(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      return index < columnCount
    },
    isPaperGridBottomEdge() {
      return false
    },
    isPaperGridLastIncompleteRowLastItem(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      const itemCount = this.visiblePaperItems.length
      return itemCount > 0 && itemCount % columnCount != 0 && index == itemCount - 1
    },
    updatePaperGridColumnCount() {
      if (this.viewMode != 'grid') {
        return
      }
      this.$nextTick(() => {
        const grid = this.$refs.paperGrid
        if (!grid || typeof window === 'undefined') {
          this.paperGridColumnCount = 1
          return
        }
        const styles = window.getComputedStyle(grid)
        const columns = String(styles.gridTemplateColumns || '')
          .split(/\s+/)
          .filter(column => column && column != 'none')
        this.paperGridColumnCount = Math.max(1, columns.length || 1)
      })
    },
    isPaperPagesGridTopEdge(index) {
      const columnCount = Math.max(1, Number(this.paperPagesGridColumnCount) || 1)
      return index < columnCount
    },
    isPaperPagesGridSelectedRowStart(index) {
      const columnCount = Math.max(1, Number(this.paperPagesGridColumnCount) || 1)
      if (this.mobilePreviewGridAdditiveLongPressSelection) {
        const previousPage = this.detailPages[index - columnCount]
        return !previousPage || !this.isDetailPageSelected(previousPage.pageNo)
      }
      const rowStart = Math.floor(index / columnCount) * columnCount
      if (rowStart == 0) {
        return true
      }
      return !this.hasSelectedPaperPagesGridRow(rowStart - columnCount, columnCount)
    },
    isPaperPagesGridSelectionContinuesBelow(index) {
      const columnCount = Math.max(1, Number(this.paperPagesGridColumnCount) || 1)
      if (this.mobilePreviewGridAdditiveLongPressSelection) {
        const nextPage = this.detailPages[index + columnCount]
        return !!(nextPage && this.isDetailPageSelected(nextPage.pageNo))
      }
      const rowStart = Math.floor(index / columnCount) * columnCount
      return this.hasSelectedPaperPagesGridRow(rowStart + columnCount, columnCount)
    },
    hasSelectedPaperPagesGridRow(rowStart, columnCount) {
      if (rowStart < 0 || rowStart >= this.detailPages.length) {
        return false
      }
      const rowEnd = Math.min(rowStart + columnCount, this.detailPages.length)
      for (let index = rowStart; index < rowEnd; index += 1) {
        const page = this.detailPages[index]
        if (page && this.isDetailPageSelected(page.pageNo)) {
          return true
        }
      }
      return false
    },
    updatePaperPagesGridColumnCount() {
      if (!this.pageGridOpen) {
        return
      }
      this.$nextTick(() => {
        const grid = this.$refs.paperPagesGrid
        if (!grid || typeof window === 'undefined') {
          this.paperPagesGridColumnCount = 1
          return
        }
        const columns = String(window.getComputedStyle(grid).gridTemplateColumns || '')
          .split(/\s+/)
          .filter(column => column && column != 'none')
        this.paperPagesGridColumnCount = Math.max(1, columns.length || 1)
      })
    },
    handlePaperWindowResize() {
      this.updatePaperGridColumnCount()
      this.updatePaperPagesGridColumnCount()
      this.syncSelectionBarScrollbarOffset()
      this.selectionBarOffsetY = this.clampSelectionBarOffset(this.selectionBarOffsetY)
      if (this.detailMode && !this.pageGridOpen) {
        this.centerPaperPreviewScroll()
      }
    },
    syncSelectionBarScrollbarOffset() {
      this.$nextTick(() => {
        const area = this.$refs.paperListArea
        const nextOffset = area ? Math.max(0, area.offsetWidth - area.clientWidth) : 0
        if (this.selectionBarScrollbarOffset != nextOffset) {
          this.selectionBarScrollbarOffset = nextOffset
        }
      })
    },
    startSelectionBarDrag(event) {
      if (!event || event.button !== 0) {
        return
      }
      if (event.target && event.target.closest && event.target.closest('button')) {
        return
      }
      event.preventDefault()
      this.selectionBarDragging = true
      this.selectionBarDragState = {
        startY: event.clientY,
        startOffset: this.selectionBarOffsetY
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('mousemove', this.handleSelectionBarDrag)
        document.addEventListener('mouseup', this.stopSelectionBarDrag)
      }
    },
    handleSelectionBarDrag(event) {
      if (!this.selectionBarDragState || !event) {
        return
      }
      const nextOffset = this.selectionBarDragState.startOffset + event.clientY - this.selectionBarDragState.startY
      this.selectionBarOffsetY = this.clampSelectionBarOffset(nextOffset)
    },
    stopSelectionBarDrag() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousemove', this.handleSelectionBarDrag)
        document.removeEventListener('mouseup', this.stopSelectionBarDrag)
      }
      this.selectionBarDragging = false
      this.selectionBarDragState = null
    },
    clampSelectionBarOffset(value) {
      const number = Number(value) || 0
      const maxOffset = this.getSelectionBarMaxOffset()
      return Math.min(Math.max(0, number), maxOffset)
    },
    getSelectionBarMaxOffset() {
      const workspace = this.$refs.paperWorkspace
      const layer = this.$refs.paperSelectionLayer
      if (!workspace || !layer) {
        return 0
      }
      const layerTop = layer.offsetTop || 0
      const layerHeight = layer.offsetHeight || 0
      return Math.max(0, workspace.clientHeight - layerTop - layerHeight)
    },
    resetSelectionBarOffset() {
      this.stopSelectionBarDrag()
      this.selectionBarOffsetY = 0
    },
    syncSelectionWithItems() {
      if (this.selectedPaperIds.length == 0) {
        this.selectedPaperId = ''
        this.selectedPaperAnchorId = ''
        return
      }
      const existingIds = new Set(this.paperItems.map(item => item.id))
      this.selectedPaperIds = this.selectedPaperIds.filter(id => existingIds.has(id))
      this.selectedPaperId = this.selectedPaperIds[0] || ''
      if (!existingIds.has(this.selectedPaperAnchorId)) {
        this.selectedPaperAnchorId = this.selectedPaperId
      }
    },
    clearSelection() {
      this.clearPaperItemClickTimer()
      this.selectedPaperIds = []
      this.selectedPaperId = ''
      this.selectedPaperAnchorId = ''
      this.moreMenuId = ''
      this.resetSelectionBarOffset()
    },
    selectPaperItem(item, event) {
      if (!item) {
        return
      }
      const hadSelection = this.selectedPaperIds.length > 0
      const result = selectItemWithWindowsRange(
        this.visiblePaperItems,
        this.selectedPaperIds,
        item,
        this.selectedPaperAnchorId,
        event
      )
      const selectedIds = result.selectedIds

      this.selectedPaperIds = selectedIds
      this.selectedPaperId = selectedIds[0] || ''
      this.selectedPaperAnchorId = result.anchorId
      if (!hadSelection || selectedIds.length == 0) {
        this.resetSelectionBarOffset()
      }
      this.moreMenuId = ''
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.searchOpen = false
    },
    handlePaperItemClick(item, event) {
      if (!item) {
        return
      }
      if (event && event.detail > 1) {
        this.clearPaperItemClickTimer()
        return
      }
      this.clearPaperItemClickTimer()
      const clickEvent = event
        ? { ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey }
        : null
      this.paperItemClickTimer = setTimeout(() => {
        this.paperItemClickTimer = null
        this.selectPaperItem(item, clickEvent)
      }, 260)
    },
    handlePaperItemDblClick(item, event) {
      this.clearPaperItemClickTimer()
      if (event && typeof event.preventDefault == 'function') {
        event.preventDefault()
      }
      if (!this.canOpenPaperResourceFolder(item)) {
        this.openPaperItem(item, { force: true })
        return
      }
      this.openPaperResourceFolder(item)
    },
    handlePaperItemKeyup(item) {
      if (!item) {
        return
      }
      this.clearPaperItemClickTimer()
      this.selectPaperItem(item)
    },
    clearPaperItemClickTimer() {
      if (this.paperItemClickTimer) {
        clearTimeout(this.paperItemClickTimer)
        this.paperItemClickTimer = null
      }
    },
    rememberPaperListScrollPosition() {
      const area = this.$refs.paperListArea
      this.paperListReturnScrollTop = area ? Math.max(0, Number(area.scrollTop) || 0) : 0
    },
    restorePaperListScrollPosition(attempt = 0) {
      const targetTop = Math.max(0, Number(this.paperListReturnScrollTop) || 0)
      this.$nextTick(() => {
        const area = this.$refs.paperListArea
        if (!area) {
          return
        }
        area.scrollTop = targetTop
        const maxTop = Math.max(0, area.scrollHeight - area.clientHeight)
        if (attempt < 4 && targetTop > 0 && area.scrollTop < Math.min(targetTop, maxTop) - 2) {
          setTimeout(() => this.restorePaperListScrollPosition(attempt + 1), 50)
        }
      })
    },
    canOpenPaperResourceFolder(item) {
      if (!item || !item.resourceId) {
        return false
      }
      const resourceType = Number(item.resourceType)
      return resourceType === 0 || resourceType === 5 || !!item.isFolder
    },
    isPaperDownloadableItem(item) {
      if (!item || !item.resourceId) {
        return false
      }
      const resourceType = Number(item.resourceType)
      if (resourceType === 5) {
        return true
      }
      if (this.isNonDownloadResourceId(item.resourceId)) {
        return false
      }
      if (Number.isFinite(resourceType) && resourceType === 0) {
        return false
      }
      return !item.isFolder && !item.template
    },
    isNonDownloadResourceId(resourceId) {
      const value = Number(resourceId)
      return Number.isFinite(value) && (value === 0 || value === 5)
    },
    resetPaperListTransientState() {
      this.clearSelection()
      this.moreMenuId = ''
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.searchOpen = false
      this.searchKeyword = ''
    },
    openPaperResourceFolder(item) {
      this.resetPaperListTransientState()
      const resourceId = item.resourceId || ''
      this.paperListResourceId = resourceId
      const folder = this.normalizePaperFolderPathItem({
        name: item.name || 'Paper',
        resourceId,
        resourceType: item.resourceType,
        filePath: item.filePath,
        pathKey: item.pathKey,
        template: item.template === true
      }, this.paperFolderPath.length)
      const folderPath = this.normalizeCloudPaperFolderPath(this.paperFolderPath)
      const existedIndex = folder ? folderPath.findIndex(crumb => crumb.pathKey == folder.pathKey) : -1
      if (existedIndex >= 0) {
        this.paperFolderPath = folderPath.slice(0, existedIndex + 1)
      } else {
        this.paperFolderPath = folder ? folderPath.concat(folder) : folderPath
      }
      this.loadPaperList()
    },
    openPaperBreadcrumb(index) {
      const folderPath = this.normalizeCloudPaperFolderPath(this.paperFolderPath)
      const breadcrumbs = [{ name: 'Paper', resourceId: '', pathKey: 'root' }].concat(folderPath)
      const target = breadcrumbs[index]
      if (!target || index == breadcrumbs.length - 1) {
        return
      }
      this.resetPaperListTransientState()
      this.paperListResourceId = target.resourceId || ''
      this.paperFolderPath = index <= 0 ? [] : folderPath.slice(0, index)
      this.loadPaperList()
    },
    parseDateTime(value) {
      if (value instanceof Date) {
        return value.getTime()
      }
      const number = Number(value)
      if (Number.isFinite(number) && number > 0) {
        return number < 1000000000000 ? number * 1000 : number
      }
      const text = String(value || '').trim()
      if (!text) {
        return 0
      }
      const direct = Date.parse(text)
      if (Number.isFinite(direct)) {
        return direct
      }
      const normalized = Date.parse(text.replace(/-/g, '/'))
      return Number.isFinite(normalized) ? normalized : 0
    },
    selectFilter(key) {
      if (!this.showRootFilter) {
        this.filterMenuOpen = false
        return
      }
      if (this.paperFilter == key) {
        this.filterMenuOpen = false
        this.moreMenuId = ''
        return
      }
      this.clearSelection()
      this.paperFilter = key
      this.filterMenuOpen = false
      this.moreMenuId = ''
      this.loadPaperList()
    },
    selectSort(key) {
      if (!this.sortOptions.some(option => option.key == key)) {
        this.sortMenuOpen = false
        this.moreMenuId = ''
        return
      }
      if (this.sortField == key) {
        this.sortMenuOpen = false
        this.moreMenuId = ''
        return
      }
      this.sortField = key
      this.sortMenuOpen = false
      this.moreMenuId = ''
      this.loadPaperList()
    },
    toggleSortOrder() {
      this.sortDesc = !this.sortDesc
      this.moreMenuId = ''
      this.loadPaperList()
    },
    setViewMode(mode) {
      this.viewMode = mode
      this.moreMenuId = ''
    },
    toggleSearch() {
      this.searchOpen = !this.searchOpen
      this.$nextTick(() => {
        if (this.searchOpen && this.$refs.paperSearchInput) {
          this.$refs.paperSearchInput.focus()
        }
      })
    },
    closeFloatingMenus() {
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.moreMenuId = ''
    },
    toggleMoreMenu(id) {
      this.moreMenuId = this.moreMenuId == id ? '' : id
    },
    handleSelectionAction(type) {
      this.performPaperAction(type, this.selectedPaperItems)
    },
    handleItemMenuAction(type, item) {
      this.performPaperAction(type, item ? [item] : [])
    },
    canPerformPaperAction(type, items) {
      const list = Array.isArray(items) ? items : []
      if (list.length == 0) {
        return false
      }
      if (type == 'download') {
        return list.every(item => this.isPaperDownloadableItem(item))
      }
      if (type == 'move' || type == 'delete') {
        return true
      }
      if (list.length != 1) {
        return false
      }
      const item = list[0]
      if (type == 'encrypt') {
        return !item.encrypted
      }
      if (type == 'decrypt') {
        return !!item.encrypted
      }
      if (type == 'unfavorite') {
        return !!item.favorite
      }
      return type == 'rename' || type == 'favorite'
    },
    performPaperAction(type, items) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (!this.canPerformPaperAction(type, list)) {
        return
      }
      this.moreMenuId = ''

      if (type == 'download') {
        if (!this.$canStartFileDownloadBatch(list.length)) {
          return
        }
        const encryptedItem = list.find(item => this.isPaperEncryptedItem(item))
        if (encryptedItem) {
          this.openPaperPasswordModal(encryptedItem, '', false, 'download', list)
          return
        }
        this.openPaperDownloadFormatModal(list)
        return
      }
      if (type == 'favorite') {
        this.toggleFavorite(list[0])
        return
      }
      if (type == 'unfavorite') {
        this.$set(list[0], 'favorite', false)
        if (this.effectivePaperFilter == 'favorite') {
          this.clearSelection()
        }
        this.$message.info(this.ui.fakeTip)
        return
      }
      if (type == 'encrypt') {
        this.$set(list[0], 'encrypted', true)
        this.$message.info(this.ui.fakeTip)
        return
      }
      if (type == 'decrypt') {
        this.$set(list[0], 'encrypted', false)
        this.$message.info(this.ui.fakeTip)
        return
      }
      if (type == 'delete') {
        list.forEach(item => {
          this.$set(item, 'deleted', true)
        })
        this.clearSelection()
        this.$message.info(this.ui.fakeTip)
        return
      }
      this.fakeAction()
    },
    isPaperPngOnlyDownloadItem(item) {
      if (this.isPaperPngOnlyDownloadFolder()) {
        return true
      }
      const resourceType = Number(item && item.resourceType)
      return resourceType === 5
    },
    isPaperPngOnlyDownloadFolder() {
      const folder = this.currentListFolder
      if (!folder) {
        return false
      }
      const resourceType = Number(folder.resourceType)
      return folder.template === true || resourceType === 5
    },
    getPaperDownloadFormatForItem(item, format) {
      if (this.isPaperPngOnlyDownloadItem(item)) {
        return 'png'
      }
      return format == 'png' ? 'png' : 'pdf'
    },
    getPaperDetailDownloadFormat(format) {
      return (this.currentDetailResourceType === 8 || this.isPaperPngOnlyDownloadFolder()) ? 'png' : (format == 'png' ? 'png' : 'pdf')
    },
    enqueuePaperDownloadItems(items, format, password = '') {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (this.$shouldBundleDownloadItems(list)) {
        const task = this.$createDownloadBundleTask(list, format, password, {
          idPrefix: 'paper-bundle-download',
          kind: 'paper-bundle',
          fallbackName: 'Paper',
          sanitizeFileName: this.sanitizeFileName
        })
        this.$enqueuePaperDownloadTask(task, () => this.runPaperDownloadBundleTask(task))
        return
      }
      list.forEach(item => {
        const itemFormat = this.getPaperDownloadFormatForItem(item, format)
        const task = this.createPaperDownloadTask(item, itemFormat, password)
        this.$enqueuePaperDownloadTask(task, () => this.runPaperDownloadTask(task, item, itemFormat))
      })
    },
    runPaperDownloadBundleTask(task) {
      this.$runDownloadBundleTask(task, {
        items: task.sourceItems,
        format: task.format,
        password: task.password || '',
        kind: 'paper-bundle',
        fallbackName: 'Paper',
        sanitizeFileName: this.sanitizeFileName,
        getItemFormat: (item, format) => this.getPaperDownloadFormatForItem(item, format),
        createItemTask: (item, format, password) => this.createPaperDownloadTask(item, format, password),
        runItemTask: (bundleTask, item, format) => this.runPaperDownloadTask(bundleTask, item, format)
      })
    },
    shouldClearSelectionAfterPaperDownloadStart(items) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (list.length == 0 || this.selectedPaperIds.length != list.length) {
        return false
      }
      const selectedIds = new Set(this.selectedPaperIds)
      return list.every(item => item && selectedIds.has(item.id))
    },
    clearSelectionAfterPaperDownloadStart(items) {
      if (this.shouldClearSelectionAfterPaperDownloadStart(items)) {
        this.clearSelection()
      }
    },
    openPaperDownloadFormatModal(items, options = {}) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (list.length == 0) {
        this.$message.info(`${this.ui.chooseDownloadableFile}: Paper`)
        return
      }
      const hasUnsupportedItem = list.some(item => !this.isPaperDownloadableItem(item))
      if (hasUnsupportedItem) {
        this.$message.warning(`${this.ui.foldersNotDownloadable}: Paper`)
        return
      }
      if (!this.$canStartFileDownloadBatch(list.length)) {
        return
      }
      const password = options.password || ''
      if (list.every(item => this.isPaperPngOnlyDownloadItem(item))) {
        this.enqueuePaperDownloadItems(list, 'png', password)
        this.clearSelectionAfterPaperDownloadStart(list)
        return
      }
      this.paperDownloadPassword = password
      this.paperDownloadItems = list
      this.paperDownloadDetailPages = []
      this.paperDownloadFormatContext = 'items'
      this.paperDownloadFormat = 'pdf'
      this.paperDownloadFormatModalOpen = true
    },
    openDetailPagesDownloadFormatModal(pages) {
      const list = Array.isArray(pages) ? pages.filter(page => this.isPaperDetailPageDownloadable(page)) : []
      if (list.length == 0) {
        this.$message.info(this.ui.chooseDownloadableFile)
        return
      }
      if (!this.$canStartDownloadBatch(list.length, 1, { skipSelectionLimit: true })) {
        return
      }
      if (this.currentDetailResourceType === 8 || this.isPaperPngOnlyDownloadFolder()) {
        this.startDetailPagesDownload(list, 'png')
        return
      }
      this.paperDownloadItems = []
      this.paperDownloadDetailPages = list
      this.paperDownloadFormatContext = 'detail-pages'
      this.paperDownloadFormat = 'png'
      this.paperDownloadFormatModalOpen = true
    },
    closePaperDownloadFormatModal() {
      this.paperDownloadFormatModalOpen = false
      this.paperDownloadItems = []
      this.paperDownloadPassword = ''
      this.paperDownloadDetailPages = []
      this.paperDownloadFormatContext = 'items'
    },
    confirmPaperDownloadFormat() {
      const format = this.paperDownloadFormat == 'png' ? 'png' : 'pdf'
      if (this.paperDownloadFormatContext == 'detail-pages') {
        const pages = this.paperDownloadDetailPages.slice()
        if (!this.$canStartDownloadBatch(pages.length, 1, { skipSelectionLimit: true })) {
          return
        }
        this.closePaperDownloadFormatModal()
        this.startDetailPagesDownload(pages, this.getPaperDetailDownloadFormat(format))
        return
      }
      const items = this.paperDownloadItems.slice()
      if (!this.$canStartFileDownloadBatch(items.length)) {
        return
      }
      const password = this.paperDownloadPassword || ''
      this.closePaperDownloadFormatModal()
      this.enqueuePaperDownloadItems(items, format, password)
      this.clearSelectionAfterPaperDownloadStart(items)
    },
    createPaperDownloadTask(item, format, password = '') {
      const safeFormat = this.getPaperDownloadFormatForItem(item, format)
      const baseName = this.$getDownloadBaseName(item && item.name, 'Paper', this.sanitizeFileName)
      const extension = this.$getPaperDownloadTaskExtension(item, safeFormat)
      return {
        id: `paper-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        name: baseName,
        fileName: `${baseName}.${extension}`,
        format: safeFormat,
        sourceItem: item,
        password: this.isPaperEncryptedItem(item) ? (password || '') : '',
        openUrl: '',
        pages: [],
        pngFiles: [],
        pdfPages: [],
        failedPageIndex: 0,
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    updatePaperDownloadTask(task, patch) {
      patch = typeof this.$normalizePaperDownloadTaskPatch == 'function' ? this.$normalizePaperDownloadTaskPatch(task, patch) : patch
      Object.keys(patch || {}).forEach(key => {
        this.$set(task, key, patch[key])
      })
    },
    async runPaperDownloadTask(task, item, format) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        const safeFormat = this.getPaperDownloadFormatForItem(item, format || (task && task.format))
        if (task.format != safeFormat) {
          this.$set(task, 'format', safeFormat)
        }
        this.updatePaperDownloadTask(task, { state: 'running', progress: Math.max(task.progress || 0, 5), statusText: this.ui.loading })
        const detail = task.detail || await this.fetchPaperDownloadDetail(item, task.password || '')
        const pages = task.pages && task.pages.length > 0 ? task.pages : this.getPaperDetailPagesForDownload(detail)
        if (this.isPaperPdfResourceDetail(detail)) {
          const compositePages = task.pages && task.pages.length > 0 ? task.pages : this.getPaperDetailPagesForComposite(detail)
          this.updatePaperDownloadTask(task, { detail, pages: compositePages, progress: Math.max(task.progress || 0, 10) })
          await this.downloadPaperPdfResource(task, detail, safeFormat)
        } else {
          if (pages.length == 0) {
            throw new Error('No pages')
          }
          this.updatePaperDownloadTask(task, { detail, pages, progress: Math.max(task.progress || 0, 10) })
          if (safeFormat == 'png') {
            await this.downloadPaperAsPngZip(task, pages)
          } else {
            await this.downloadPaperAsPdf(task, pages)
          }
        }
        this.ensurePaperDownloadTaskActive(task)
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: isCorsLikeError ? this.ui.imageReadFailed : (error && error.message ? error.message : this.ui.downloadFailed)
        })
      }
    },
    retryPaperDownloadTask(task) {
      if (!task || task.state != 'error') {
        return
      }
      this.paperDownloadListVisible = true
      this.$set(task, 'cancelled', false)
      this.$set(task, 'abortController', null)
      this.updatePaperDownloadTask(task, {
        progress: task.progress || 0,
        state: 'running',
        statusText: this.ui.continueDownload
      })
      if (task.kind == 'paper-bundle') {
        this.runPaperDownloadBundleTask(task)
        return
      }
      if (task.kind == 'detail-pages') {
        this.runDetailPagesDownloadTask(task)
        return
      }
      if (task.kind == 'recording-file') {
        this.runRecordingDownloadTask(task)
        return
      }
      this.runPaperDownloadTask(task, task.sourceItem, task.format)
    },
    getPaperDownloadNativeOpener() {
      if (typeof window === 'undefined') {
        return null
      }
      return window.viwoodsNative || window.electronAPI || window.nativeBridge || null
    },
    canOpenPaperDownloadTask(task) {
      if (!task || task.state != 'done') {
        return false
      }
      const nativeOpener = this.getPaperDownloadNativeOpener()
      if (task.filePath && nativeOpener && typeof nativeOpener.showItemInFolder == 'function') {
        return true
      }
      return !!(nativeOpener && typeof nativeOpener.openDownloadFolder == 'function')
    },
    openPaperDownloadTask(task) {
      if (!this.canOpenPaperDownloadTask(task)) {
        return
      }
      const nativeOpener = this.getPaperDownloadNativeOpener()
      if (task.filePath && nativeOpener && typeof nativeOpener.showItemInFolder == 'function') {
        nativeOpener.showItemInFolder(task.filePath)
        return
      }
      if (nativeOpener && typeof nativeOpener.openDownloadFolder == 'function') {
        nativeOpener.openDownloadFolder()
        return
      }
    },
    removePaperDownloadTask(task) {
      if (!task) {
        return
      }
      this.$set(task, 'cancelled', true)
      if (task.abortController && typeof task.abortController.abort == 'function') {
        task.abortController.abort()
      }
      this.revokePaperDownloadTaskUrl(task)
      this.paperDownloadTasks = this.paperDownloadTasks.filter(item => item.id != task.id)
      if (this.paperDownloadTasks.length == 0) {
        this.paperDownloadListVisible = false
      }
    },
    closePaperDownloadList() {
      this.clearPaperDownloadTasks()
    },
    clearPaperDownloadTasks() {
      this.paperDownloadTasks.forEach(task => {
        this.$set(task, 'cancelled', true)
        if (task.abortController && typeof task.abortController.abort == 'function') {
          task.abortController.abort()
        }
        this.revokePaperDownloadTaskUrl(task)
      })
      this.paperDownloadTasks = []
      this.paperDownloadListVisible = false
    },
    async fetchPaperDownloadDetail(item, password = '') {
      const ret = await this.fetchPaperDetailResponse(item, this.isPaperEncryptedItem(item) ? password : '')
      const payload = this.resolveApiPayload(ret)
      if (payload === null) {
        throw new Error('Paper detail failed')
      }
      return this.normalizePaperDetail(payload, item)
    },
    revokePaperPreviewObjectUrls(urls) {
      const list = Array.isArray(urls) ? urls : []
      if (typeof this.revokePaperPreviewObjectUrlsSafely == 'function') {
        this.revokePaperPreviewObjectUrlsSafely(list)
        return
      }
      list.forEach(url => {
        if (url && String(url).startsWith('blob:')) {
          URL.revokeObjectURL(url)
        }
      })
    },
    clearPaperPreviewObjectUrls(options = {}) {
      if (options.revoke !== false) {
        this.revokePaperPreviewObjectUrls(this.paperPreviewObjectUrls)
      }
      this.paperPreviewObjectUrls = []
    },
    clearPaperDetailCache() {
      const cache = this.paperDetailCache || {}
      Object.keys(cache).forEach(key => {
        this.revokePaperPreviewObjectUrls(cache[key] && cache[key].objectUrls)
      })
      this.paperDetailCache = {}
    },
    async preparePaperPreviewDetail(detail, onProgress) {
      const objectUrls = []
      const reportProgress = progress => {
        if (typeof onProgress == 'function') {
          onProgress(progress)
        }
      }
      const revokePreparedUrls = () => {
        this.revokePaperPreviewObjectUrls(objectUrls)
      }
      reportProgress(30)
      if (!this.isPaperPdfResourceDetail(detail)) {
        reportProgress(100)
        return { detail, objectUrls }
      }
      let pdfDocument = null
      try {
        pdfDocument = await this.loadPaperPdfDocumentForPreview(detail.resourceUrl, percent => {
          reportProgress(30 + (Math.max(0, Math.min(Number(percent) || 0, 100)) * 0.2))
        })
        reportProgress(56)
        const pages = this.getPaperPdfPreviewCompositePages(detail, pdfDocument.numPages)
        const previewPages = pages.filter((page, index) => {
          const pageNo = Number(page && page.pageNo) || index + 1
          return !pdfDocument.numPages || pageNo <= pdfDocument.numPages || (page && (page.imageUrl || page.pageImageUrl))
        })
        if (previewPages.length == 0) {
          reportProgress(100)
          return { detail: Object.assign({}, detail, { imagePages: [] }), objectUrls }
        }
        const compositeFrameSize = await this.getUnifiedPaperPdfCompositeFrameSize(pdfDocument, previewPages, null, percent => {
          reportProgress(56 + Math.max(0, Math.min(Number(percent) || 0, 100)) * 0.04)
        })
        reportProgress(60)
        const imagePages = await this.buildRenderedPaperPreviewPages({
          pages: previewPages,
          objectUrls,
          onProgress: reportProgress,
          startProgress: 60,
          endProgress: 96,
          renderPage: async (page, index, total, progress) => {
            const pageNo = Number(page && page.pageNo) || index + 1
            if (pdfDocument.numPages && pageNo > pdfDocument.numPages) {
              return this.fetchPaperImageBlob(page.imageUrl || page.pageImageUrl, progress)
            }
            const canvas = await this.renderPaperPdfCompositeCanvas(pdfDocument, page, index, total, null, null, null, progress, compositeFrameSize)
            try {
              return await this.canvasToPngBlob(canvas)
            } finally {
              this.releaseCanvas(canvas)
            }
          }
        })
        reportProgress(100)
        return { detail: Object.assign({}, detail, { imagePages, sourceImagePages: previewPages }), objectUrls }
      } catch (error) {
        revokePreparedUrls()
        throw error
      } finally {
        if (pdfDocument && typeof pdfDocument.destroy == 'function') {
          Promise.resolve().then(() => pdfDocument.destroy()).catch(() => {})
        }
      }
    },
    canUseDirectPaperPdfPreview(detail) {
      if (!detail || !this.isDirectFilePreviewDetail(detail)) {
        return false
      }
      const sourceUrl = this.getDirectPaperPdfPreviewSource(detail)
      if (!sourceUrl) {
        return false
      }
      return Number(detail.resourceType) === 7 || this.isPaperPdfUrl(sourceUrl)
    },
    getDirectPaperPdfPreviewSource(detail) {
      if (!detail || typeof detail !== 'object') {
        return ''
      }
      return this.getDirectFilePreviewSource(detail) || this.pickString(detail, [
        'resourceUrl',
        'resource_url',
        'filePath',
        'file_path',
        'fileUrl',
        'file_url',
        'downloadUrl',
        'download_url',
        'path',
        'url',
        'src'
      ])
    },
    async resolveDirectPaperPdfPreviewUrl(url, objectUrls, onProgress) {
      const sourceUrl = String(url || '').trim()
      if (!sourceUrl) {
        throw new Error('PDF preview URL is empty')
      }
      if (typeof onProgress == 'function') {
        onProgress(100)
      }
      return sourceUrl
    },
    createDirectPaperPdfPreviewDetail(detail, previewUrl) {
      const resolvedPreviewUrl = String(previewUrl || '').trim()
      if (!resolvedPreviewUrl) {
        throw new Error('PDF preview URL is empty')
      }
      return Object.assign({}, detail, {
        resourceUrl: resolvedPreviewUrl,
        pdfPreviewUrl: resolvedPreviewUrl,
        imagePages: [{
          pageNo: 1,
          imageUrl: '',
          pdfPreviewUrl: resolvedPreviewUrl,
          isPdfPreview: true
        }]
      })
    },
    getPaperDetailPagesForDownload(detail) {
      const pages = detail && Array.isArray(detail.imagePages) ? detail.imagePages : []
      return pages
        .filter(page => page && (page.imageUrl || page.pageImageUrl))
        .slice()
        .sort((a, b) => Number(a.pageNo) - Number(b.pageNo))
    },
    getPaperDetailPagesForComposite(detail) {
      const pages = detail && Array.isArray(detail.imagePages) ? detail.imagePages : []
      return pages
        .filter(page => page && page.pageNo)
        .slice()
        .sort((a, b) => Number(a.pageNo) - Number(b.pageNo))
    },
    getPaperPdfPreviewCompositePages(detail, pageCount) {
      return this.getPaperPdfCompositePages(detail, pageCount)
    },
    isPaperPdfResourceDetail(detail) {
      if (!detail) {
        return false
      }
      if (this.isDirectFilePreviewPdfDetail(detail)) {
        return true
      }
      if (!detail.resourceUrl) {
        return false
      }
      return this.isPaperPdfUrl(detail.resourceUrl)
    },
    isPaperPdfUrl(url) {
      const text = String(url || '').toLowerCase()
      if (!text) {
        return false
      }
      return this.getUrlExtension(text, '') == 'pdf' || text.includes('.pdf')
    },
    getPaperPdfCompositePages(detail, pageCount) {
      const metaPages = detail && Array.isArray(detail.sourceImagePages) && detail.sourceImagePages.length > 0
        ? detail.sourceImagePages.slice()
        : this.getPaperDetailPagesForComposite(detail)
      const pagesByNo = {}
      let maxMetaPageNo = 0
      metaPages.forEach(page => {
        const pageNo = Number(page.pageNo)
        if (pageNo > 0) {
          pagesByNo[pageNo] = page
          maxMetaPageNo = Math.max(maxMetaPageNo, pageNo)
        }
      })
      const totalPageCount = Math.max(Number(pageCount) || 0, metaPages.length, maxMetaPageNo)
      const pages = []
      for (let i = 1; i <= totalPageCount; i++) {
        pages.push(Object.assign({ pageNo: i, imageUrl: '' }, pagesByNo[i] || {}))
      }
      return pages
    },
    ensurePaperDownloadTaskActive(task) {
      const isInList = !!(task && this.paperDownloadTasks.some(item => item.id == task.id))
      if (!task || task.cancelled || !isInList) {
        const error = new Error('Download cancelled')
        error.isPaperDownloadCancelled = true
        throw error
      }
    },
    getPaperDownloadImageProgress(index, total, start, end, percent) {
      const safeTotal = Math.max(total || 1, 1)
      const safePercent = Math.max(0, Math.min(Number(percent) || 0, 100))
      return Math.min(end, Math.round(start + ((index + safePercent / 100) / safeTotal) * (end - start)))
    },
    async downloadPaperPdfResource(task, detail, format) {
      if (format == 'png') {
        await this.downloadPaperPdfResourceAsPngZip(task, detail)
        return
      }
      await this.downloadPaperPdfResourceAsPdf(task, detail)
    },
    async getPaperPdfDocument(task, detail) {
      if (task.pdfDocument) {
        return task.pdfDocument
      }
      this.updatePaperDownloadTask(task, {
        progress: Math.max(task.progress || 0, 10),
        statusText: this.ui.downloading
      })
      const pdfBlob = await this.fetchPaperBinaryBlob(detail.resourceUrl, percent => {
        this.updatePaperDownloadTask(task, {
          progress: this.getPaperDownloadImageProgress(0, 1, 10, 25, percent),
          statusText: this.ui.downloading
        })
      }, task)
      this.ensurePaperDownloadTaskActive(task)
      this.updatePaperDownloadTask(task, { progress: 28, statusText: '\u89e3\u6790 PDF' })
      const pdfDocument = await this.loadPaperPdfDocumentFromBlob(pdfBlob)
      task.pdfDocument = pdfDocument
      task.pdfPageCount = pdfDocument.numPages
      return pdfDocument
    },
    async loadPaperPdfDocumentFromBlob(pdfBlob) {
      const bytes = new Uint8Array(await this.blobToArrayBuffer(pdfBlob))
      const loadingTask = pdfjsLib.getDocument({
        data: bytes
      })
      return loadingTask.promise
    },
    async loadPaperPdfDocumentForPreview(url, onProgress) {
      try {
        const pdfBlob = await this.fetchPaperBinaryBlob(url, onProgress)
        return await this.loadPaperPdfDocumentFromBlob(pdfBlob)
      } catch (blobError) {
        try {
          return await this.loadPaperPdfDocumentFromUrl(url, onProgress)
        } catch (urlError) {
          throw blobError || urlError
        }
      }
    },
    async loadPaperPdfDocumentFromUrl(url, onProgress) {
      const requestUrls = this.getPaperImageFetchUrls(url)
      let lastError = null
      for (const requestUrl of requestUrls) {
        try {
          const loadingTask = pdfjsLib.getDocument({
            url: requestUrl,
            disableAutoFetch: true,
            disableStream: false,
            disableRange: false,
            rangeChunkSize: 262144
          })
          loadingTask.onProgress = progress => {
            const total = Number(progress && progress.total) || 0
            const loaded = Number(progress && progress.loaded) || 0
            if (total > 0 && typeof onProgress == 'function') {
              onProgress(Math.min(100, Math.round((loaded / total) * 100)))
            }
          }
          return await loadingTask.promise
        } catch (error) {
          lastError = error
        }
      }
      throw lastError || new Error('PDF load failed')
    },
    async downloadPaperPdfResourceAsPdf(task, detail) {
      const pdfDocument = await this.getPaperPdfDocument(task, detail)
      const pages = this.getPaperPdfPreviewCompositePages(detail, pdfDocument.numPages)
        .filter((page, index) => {
          const pageNo = Number(page && page.pageNo) || index + 1
          return (pdfDocument.numPages && pageNo <= pdfDocument.numPages) || (page && (page.imageUrl || page.pageImageUrl))
        })
      this.$updatePaperDownloadTaskFileNameForPages(task, 'pdf', pages)
      if (pages.length == 0) {
        throw new Error('No pages')
      }
      const compositeFrameSize = await this.getUnifiedPaperPdfCompositeFrameSize(pdfDocument, pages, task)
      const imagePages = Array.isArray(task.pdfPages) ? task.pdfPages.slice() : []
      this.updatePaperDownloadTask(task, { pages, pdfPages: imagePages })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (imagePages[i]) {
          continue
        }
        const pageNo = Number(pages[i].pageNo) || i + 1
        if (pdfDocument.numPages && pageNo > pdfDocument.numPages) {
          const blob = await this.fetchPaperImageBlob(pages[i].imageUrl || pages[i].pageImageUrl, percent => {
            this.updatePaperDownloadTask(task, {
              progress: this.getPaperDownloadImageProgress(i, pages.length, 30, 85, percent),
              statusText: this.ui.downloading
            })
          }, task)
          const canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
          imagePages[i] = await this.canvasToJpegPage(canvas)
          this.releaseCanvas(canvas)
        } else {
          const canvas = await this.renderPaperPdfCompositeCanvas(pdfDocument, pages[i], i, pages.length, task, 30, 85, null, compositeFrameSize)
          imagePages[i] = await this.canvasToJpegPage(canvas)
          this.releaseCanvas(canvas)
        }
        this.updatePaperDownloadTask(task, {
          pdfPages: imagePages.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u751f\u6210 PDF' })
      const pdfBlob = this.buildPdfBlob(imagePages.filter(Boolean))
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, pdfBlob)
    },
    async downloadPaperPdfResourceAsPngZip(task, detail) {
      const pdfDocument = await this.getPaperPdfDocument(task, detail)
      const pages = this.getPaperPdfPreviewCompositePages(detail, pdfDocument.numPages)
        .filter((page, index) => {
          const pageNo = Number(page && page.pageNo) || index + 1
          return (pdfDocument.numPages && pageNo <= pdfDocument.numPages) || (page && (page.imageUrl || page.pageImageUrl))
        })
      if (pages.length == 0) {
        throw new Error('No pages')
      }
      const compositeFrameSize = await this.getUnifiedPaperPdfCompositeFrameSize(pdfDocument, pages, task)
      const pngFiles = Array.isArray(task.pngFiles) ? task.pngFiles.slice() : []
      this.updatePaperDownloadTask(task, { pages, pngFiles })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (pngFiles[i]) {
          continue
        }
        const pageNo = Number(pages[i].pageNo) || i + 1
        let blob = null
        if (pdfDocument.numPages && pageNo > pdfDocument.numPages) {
          const imageBlob = await this.fetchPaperImageBlob(pages[i].imageUrl || pages[i].pageImageUrl, percent => {
            this.updatePaperDownloadTask(task, {
              progress: this.getPaperDownloadImageProgress(i, pages.length, 30, 85, percent),
              statusText: this.ui.downloading
            })
          }, task)
          const canvas = await this.createPaperImageFrameCanvas(imageBlob, compositeFrameSize)
          blob = await this.canvasToPngBlob(canvas)
          this.releaseCanvas(canvas)
        } else {
          const canvas = await this.renderPaperPdfCompositeCanvas(pdfDocument, pages[i], i, pages.length, task, 30, 85, null, compositeFrameSize)
          blob = await this.canvasToPngBlob(canvas)
          this.releaseCanvas(canvas)
        }
        pngFiles[i] = {
          fileName: this.getPaperPageFileName(pages[i], i, 'png'),
          buffer: await this.blobToArrayBuffer(blob)
        }
        this.updatePaperDownloadTask(task, {
          pngFiles: pngFiles.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      if (pngFiles.length == 1 && pngFiles[0]) {
        this.updatePaperDownloadTask(task, {
          fileName: `${task.name}.png`,
          progress: 99,
          statusText: this.ui.savingFile
        })
        this.savePaperDownloadBlob(task, new Blob([pngFiles[0].buffer], { type: 'image/png' }))
        return
      }
      const zip = new JSZip()
      pngFiles.forEach((file, index) => {
        if (file) {
          zip.file(file.fileName || this.getPaperPageFileName(pages[index], index, 'png'), file.buffer)
        }
      })
      this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u6253\u5305 ZIP' })
      const zipBlob = await zip.generateAsync({ type: 'blob' }, metadata => {
        this.updatePaperDownloadTask(task, {
          progress: Math.min(98, Math.round(90 + (metadata.percent || 0) * 0.08)),
          statusText: '\u6253\u5305 ZIP'
        })
      })
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, zipBlob)
    },
    getPaperPdfOverlayImageUrl(pageInfo) {
      if (!pageInfo || pageInfo.isRenderedPreview) {
        return ''
      }
      return String(pageInfo.overlayImageUrl || pageInfo.imageUrl || '').trim()
    },
    async renderPaperPdfCompositeCanvas(pdfDocument, pageInfo, index, total, task, startProgress, endProgress, onProgress, frameSize) {
      const reportCompositeProgress = progress => {
        if (typeof onProgress == 'function') {
          onProgress(Math.max(0, Math.min(Number(progress) || 0, 100)))
        }
      }
      if (task) {
        this.updatePaperDownloadTask(task, {
          failedPageIndex: index,
          progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, 0),
          statusText: this.ui.downloading
        })
      }
      reportCompositeProgress(5)
      let overlayBlob = null
      let overlaySize = null
      const overlayImageUrl = this.getPaperPdfOverlayImageUrl(pageInfo)
      const includePreviewDisplayScale = !task && !this.shouldUseStableMobilePaperPdfPreviewRender()
      const canRenderPdfInParallel = !!overlayImageUrl && this.hasPaperPreviewFrameSize(frameSize)
      let pdfCanvasError = null
      const pdfCanvasPromise = canRenderPdfInParallel
        ? this.renderPaperPdfPageCanvas(
          pdfDocument,
          Number(pageInfo.pageNo) || index + 1,
          frameSize,
          includePreviewDisplayScale
        ).catch(error => {
          pdfCanvasError = error
          return null
        })
        : null
      if (overlayImageUrl) {
        try {
          overlayBlob = await this.fetchPaperImageBlob(overlayImageUrl, percent => {
            if (task) {
              this.updatePaperDownloadTask(task, {
                progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, percent),
                statusText: this.ui.downloading
              })
            }
            reportCompositeProgress(10 + Math.max(0, Math.min(Number(percent) || 0, 100)) * 0.45)
          }, task)
          overlaySize = await this.getPaperImageBlobSize(overlayBlob)
          reportCompositeProgress(55)
        } catch (error) {
          this.releasePaperPreviewCanvasWhenReady(pdfCanvasPromise)
          throw error
        }
      }
      const targetFrameSize = frameSize || overlaySize
      const outputScale = this.getPaperPreviewFrameScale(targetFrameSize, includePreviewDisplayScale)
      const canvas = pdfCanvasPromise
        ? await pdfCanvasPromise
        : await this.renderPaperPdfPageCanvas(
          pdfDocument,
          Number(pageInfo.pageNo) || index + 1,
          targetFrameSize,
          includePreviewDisplayScale
        )
      if (pdfCanvasError) {
        throw pdfCanvasError
      }
      reportCompositeProgress(overlayBlob ? 80 : 90)
      if (overlayBlob) {
        await this.drawPaperOverlayImage(canvas, overlayBlob, targetFrameSize, outputScale)
        reportCompositeProgress(95)
      } else if (frameSize) {
        this.drawPaperPdfIntoImageFrame(canvas, frameSize, outputScale)
        reportCompositeProgress(95)
      }
      if (task) {
        this.updatePaperDownloadTask(task, {
          progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, 100),
          statusText: this.ui.downloading
        })
      }
      reportCompositeProgress(100)
      return canvas
    },
    shouldUseStableMobilePaperPdfPreviewRender() {
      return true
    },
    async renderPaperPdfPageCanvas(pdfDocument, pageNo, targetSize, includeDisplayScale = true) {
      if (!pdfDocument || !pdfDocument.numPages) {
        return this.createBlankPaperPdfCanvas(null, targetSize)
      }
      if (pageNo > pdfDocument.numPages) {
        return this.createBlankPaperPdfCanvas(pdfDocument, targetSize)
      }
      const page = await pdfDocument.getPage(pageNo)
      const baseViewport = page.getViewport({ scale: 1 })
      const renderScale = this.getPaperPreviewRenderScale(baseViewport, targetSize, { includeDisplayScale })
      const viewport = page.getViewport({ scale: renderScale })
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)
      const context = canvas.getContext('2d')
      if (context && 'imageSmoothingEnabled' in context) {
        context.imageSmoothingEnabled = true
        if ('imageSmoothingQuality' in context) {
          context.imageSmoothingQuality = 'high'
        }
      }
      context.fillStyle = '#fff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      await page.render({ canvasContext: context, viewport }).promise
      if (typeof page.cleanup == 'function') {
        page.cleanup()
      }
      return canvas
    },
    async createBlankPaperPdfCanvas(pdfDocument, targetSize) {
      let width = Math.round(Number(targetSize && targetSize.width) || 0)
      let height = Math.round(Number(targetSize && targetSize.height) || 0)
      if (!(width > 0 && height > 0) && pdfDocument && pdfDocument.numPages) {
        try {
          const page = await pdfDocument.getPage(1)
          const baseViewport = page.getViewport({ scale: 1 })
          const renderScale = this.getPaperPreviewRenderScale(baseViewport, targetSize)
          const viewport = page.getViewport({ scale: renderScale })
          width = Math.ceil(viewport.width)
          height = Math.ceil(viewport.height)
          if (typeof page.cleanup == 'function') {
            page.cleanup()
          }
        } catch (error) {
          // Keep the default A4-like page size.
        }
      }
      if (!(width > 0 && height > 0)) {
        width = 1240
        height = 1754
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const context = canvas.getContext('2d')
      context.fillStyle = '#fff'
      context.fillRect(0, 0, width, height)
      return canvas
    },
    async getPaperCompositeFrameSize(pages, task, onProgress) {
      const framePage = Array.isArray(pages) ? pages.find(page => page && page.imageUrl) : null
      if (!framePage) {
        return null
      }
      try {
        const blob = await this.fetchPaperImageBlob(framePage.imageUrl, percent => {
          if (typeof onProgress == 'function') {
            onProgress(percent)
          }
        }, task)
        return await this.getPaperImageBlobSize(blob)
      } catch (error) {
        return null
      }
    },
    async getPaperImageBlobSize(blob) {
      const bitmapSize = await this.getPreviewPerformanceImageBlobSize(blob)
      if (bitmapSize) {
        return bitmapSize
      }
      return new Promise(resolve => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          const width = image.naturalWidth || image.width || 0
          const height = image.naturalHeight || image.height || 0
          URL.revokeObjectURL(url)
          resolve(width > 0 && height > 0 ? { width, height } : null)
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          resolve(null)
        }
        image.src = url
      })
    },
    drawPaperPdfIntoImageFrame(canvas, frameSize, outputScale = 1) {
      const frameWidth = Math.max(1, Math.round(Number(frameSize && frameSize.width) || 0))
      const frameHeight = Math.max(1, Math.round(Number(frameSize && frameSize.height) || 0))
      if (!canvas || !frameWidth || !frameHeight) {
        return
      }
      const pdfWidth = canvas.width || frameWidth
      const pdfHeight = canvas.height || frameHeight
      const pdfCanvas = document.createElement('canvas')
      pdfCanvas.width = pdfWidth
      pdfCanvas.height = pdfHeight
      pdfCanvas.getContext('2d').drawImage(canvas, 0, 0)
      const safeOutputScale = Math.max(1, Number(outputScale) || 1)
      const targetWidth = Math.max(1, Math.round(frameWidth * safeOutputScale))
      const targetHeight = Math.max(1, Math.round(frameHeight * safeOutputScale))
      const rect = this.getPaperWidthAlignedDrawRect(pdfWidth, pdfHeight, targetWidth, targetHeight, {
        strictTargetHeight: !!(frameSize && frameSize.fixedAspectRatio),
        allowHeightFallback: true
      })
      canvas.width = rect.targetWidth
      canvas.height = rect.targetHeight
      const context = canvas.getContext('2d')
      if (context && 'imageSmoothingEnabled' in context) {
        context.imageSmoothingEnabled = true
        if ('imageSmoothingQuality' in context) {
          context.imageSmoothingQuality = 'high'
        }
      }
      context.fillStyle = '#fff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(pdfCanvas, rect.x, rect.y, rect.drawWidth, rect.drawHeight)
      pdfCanvas.width = 0
      pdfCanvas.height = 0
    },
    drawPaperOverlayImage(canvas, blob, frameSize, outputScale = 1) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          const pdfWidth = canvas.width || image.naturalWidth || image.width || 1
          const pdfHeight = canvas.height || image.naturalHeight || image.height || 1
          const pdfCanvas = document.createElement('canvas')
          pdfCanvas.width = pdfWidth
          pdfCanvas.height = pdfHeight
          pdfCanvas.getContext('2d').drawImage(canvas, 0, 0)
          const imageWidth = image.naturalWidth || image.width || pdfWidth
          const imageHeight = image.naturalHeight || image.height || pdfHeight
          const frameWidth = Math.max(1, Math.round(Number(frameSize && frameSize.width) || imageWidth))
          const frameHeight = Math.max(1, Math.round(Number(frameSize && frameSize.height) || imageHeight))
          const safeOutputScale = Math.max(1, Number(outputScale) || 1)
          const targetWidth = Math.max(1, Math.round(frameWidth * safeOutputScale))
          const targetHeight = Math.max(1, Math.round(frameHeight * safeOutputScale))
          const strictTargetHeight = !!(frameSize && frameSize.fixedAspectRatio)
          const rect = this.getPaperWidthAlignedDrawRect(pdfWidth, pdfHeight, targetWidth, targetHeight, {
            strictTargetHeight,
            allowHeightFallback: true
          })
          const overlayRect = this.getPaperWidthAlignedDrawRect(imageWidth, imageHeight, rect.targetWidth, rect.targetHeight, {
            strictTargetHeight: true
          })
          canvas.width = rect.targetWidth
          canvas.height = rect.targetHeight
          const context = canvas.getContext('2d')
          if (context && 'imageSmoothingEnabled' in context) {
            context.imageSmoothingEnabled = true
            if ('imageSmoothingQuality' in context) {
              context.imageSmoothingQuality = 'high'
            }
          }
          context.fillStyle = '#fff'
          context.fillRect(0, 0, canvas.width, canvas.height)
          context.drawImage(pdfCanvas, rect.x, rect.y, rect.drawWidth, rect.drawHeight)
          context.save()
          context.globalCompositeOperation = typeof this.getPaperOverlayCompositeOperation == 'function'
            ? this.getPaperOverlayCompositeOperation(image)
            : 'source-over'
          context.drawImage(image, overlayRect.x, overlayRect.y, overlayRect.drawWidth, overlayRect.drawHeight)
          context.restore()
          pdfCanvas.width = 0
          pdfCanvas.height = 0
          URL.revokeObjectURL(url)
          resolve()
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Overlay image load failed'))
        }
        image.src = url
      })
    },
    canvasToJpegPage(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(async blob => {
          if (!blob) {
            reject(new Error('Canvas convert failed'))
            return
          }
          resolve({
            width: canvas.width,
            height: canvas.height,
            data: new Uint8Array(await this.blobToArrayBuffer(blob))
          })
        }, 'image/jpeg', 1)
      })
    },
    canvasToPngBlob(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
          if (!blob) {
            reject(new Error('Canvas convert failed'))
            return
          }
          resolve(blob)
        }, 'image/png')
      })
    },
    releaseCanvas(canvas) {
      if (canvas) {
        canvas.width = 0
        canvas.height = 0
      }
    },
    async downloadPaperAsPngZip(task, pages) {
      this.$updatePaperDownloadTaskFileNameForPages(task, 'png', pages)
      const compositeFrameSize = await this.getUnifiedPaperCompositeFrameSize(pages, task)
      if (pages.length == 1) {
        const page = pages[0]
        this.updatePaperDownloadTask(task, {
          fileName: `${task.name}.png`,
          progress: Math.max(task.progress || 0, 10),
          statusText: this.ui.downloading
        })
        const blob = await this.fetchPaperImageBlob(page.imageUrl, percent => {
          this.updatePaperDownloadTask(task, {
            progress: this.getPaperDownloadImageProgress(0, 1, 10, 95, percent),
            statusText: this.ui.downloading
          })
        }, task)
        this.ensurePaperDownloadTaskActive(task)
        const canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
        const framedBlob = await this.canvasToPngBlob(canvas)
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, { progress: 99, statusText: this.ui.savingFile })
        this.savePaperDownloadBlob(task, framedBlob)
        return
      }
      const pngFiles = Array.isArray(task.pngFiles) ? task.pngFiles.slice() : []
      this.updatePaperDownloadTask(task, { pngFiles })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (pngFiles[i]) {
          continue
        }
        const page = pages[i]
        this.updatePaperDownloadTask(task, {
          failedPageIndex: i,
          progress: this.getPaperDownloadImageProgress(i, pages.length, 10, 80, 0),
          statusText: this.ui.downloading
        })
        const blob = await this.fetchPaperImageBlob(page.imageUrl, percent => {
          this.updatePaperDownloadTask(task, {
            progress: this.getPaperDownloadImageProgress(i, pages.length, 10, 80, percent),
            statusText: this.ui.downloading
          })
        }, task)
        const canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
        const framedBlob = await this.canvasToPngBlob(canvas)
        this.releaseCanvas(canvas)
        pngFiles[i] = {
          fileName: this.getPaperPageFileName(page, i, 'png'),
          buffer: await this.blobToArrayBuffer(framedBlob)
        }
        this.updatePaperDownloadTask(task, {
          pngFiles: pngFiles.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      const zip = new JSZip()
      pngFiles.forEach((file, index) => {
        if (file) {
          zip.file(file.fileName || this.getPaperPageFileName(pages[index], index, 'png'), file.buffer)
        }
      })
      this.updatePaperDownloadTask(task, { progress: 85, statusText: '\u6253\u5305 ZIP' })
      const zipBlob = await zip.generateAsync({ type: 'blob' }, metadata => {
        this.updatePaperDownloadTask(task, {
          progress: Math.min(98, Math.round(85 + (metadata.percent || 0) * 0.13)),
          statusText: '\u6253\u5305 ZIP'
        })
      })
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, zipBlob)
    },
    async downloadPaperAsPdf(task, pages) {
      const compositeFrameSize = await this.getUnifiedPaperCompositeFrameSize(pages, task)
      const imagePages = Array.isArray(task.pdfPages) ? task.pdfPages.slice() : []
      this.updatePaperDownloadTask(task, { pdfPages: imagePages })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (imagePages[i]) {
          continue
        }
        const page = pages[i]
        this.updatePaperDownloadTask(task, {
          failedPageIndex: i,
          progress: this.getPaperDownloadImageProgress(i, pages.length, 10, 75, 0),
          statusText: this.ui.downloading
        })
        let canvas
        if (task.kind == 'detail-pages' && this.isPaperDetailBlankPage(page)) {
          canvas = this.createPaperBlankPageCanvas(compositeFrameSize)
        } else {
          const blob = await this.fetchPaperImageBlob(page.imageUrl, percent => {
            this.updatePaperDownloadTask(task, {
              progress: this.getPaperDownloadImageProgress(i, pages.length, 10, 75, percent),
              statusText: this.ui.downloading
            })
          }, task)
          canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
        }
        imagePages[i] = await this.canvasToJpegPage(canvas)
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, {
          pdfPages: imagePages.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      this.updatePaperDownloadTask(task, { progress: 85, statusText: '\u751f\u6210 PDF' })
      const pdfBlob = this.buildPdfBlob(imagePages)
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, pdfBlob)
    },
    savePaperDownloadBlob(task, blob) {
      if (this.$captureDownloadBlob(task, blob, task && task.fileName)) {
        return
      }
      this.revokePaperDownloadTaskUrl(task)
      const openUrl = URL.createObjectURL(blob)
      this.updatePaperDownloadTask(task, { openUrl, progress: Math.max(task.progress || 0, 99) })
      saveAs(blob, task.fileName)
    },
    revokePaperDownloadTaskUrl(task) {
      if (task && task.openUrl && String(task.openUrl).startsWith('blob:')) {
        URL.revokeObjectURL(task.openUrl)
      }
      if (task) {
        this.$set(task, 'openUrl', '')
      }
    },
    async fetchPaperBinaryBlob(url, onProgress, task) {
      return this.fetchCachedPreviewPerformanceBlob(url, async progressHandler => {
        const requestUrls = this.getPaperImageFetchUrls(url)
        let lastError = null
        for (const requestUrl of requestUrls) {
          let controller = null
          try {
            if (task) {
              this.ensurePaperDownloadTaskActive(task)
            }
            if (typeof AbortController !== 'undefined') {
              controller = new AbortController()
              if (task) {
                this.registerPreviewPerformanceTaskAbortController(task, controller)
              }
            }
            const response = await fetch(requestUrl, controller ? { signal: controller.signal } : undefined)
            if (!response.ok) {
              throw new Error(`File failed: ${response.status}`)
            }
            if (this.isPaperUnexpectedProxyResponse(response, requestUrl, url)) {
              const error = new Error('File proxy returned non-file content')
              error.isCorsLikeDownloadError = true
              throw error
            }
            return await this.readPaperImageResponseBlob(response, progressHandler)
          } catch (error) {
            if (error && (error.name == 'AbortError' || task && task.cancelled)) {
              const cancelError = new Error('Download cancelled')
              cancelError.isPaperDownloadCancelled = true
              throw cancelError
            }
            error.isCorsLikeDownloadError = true
            lastError = error
          } finally {
            if (task) {
              this.unregisterPreviewPerformanceTaskAbortController(task, controller)
            }
          }
        }
        throw lastError || new Error('File download failed')
      }, { onProgress, task })
    },
    async fetchPaperImageBlob(url, onProgress, task) {
      return this.fetchCachedPreviewPerformanceBlob(url, async progressHandler => {
        if (this.getPaperImageFetchUrls) {
          const requestUrls = this.getPaperImageFetchUrls(url)
          let lastError = null
          for (const requestUrl of requestUrls) {
            let controller = null
            try {
              if (task) {
                this.ensurePaperDownloadTaskActive(task)
              }
              if (typeof AbortController !== 'undefined') {
                controller = new AbortController()
                if (task) {
                  this.registerPreviewPerformanceTaskAbortController(task, controller)
                }
              }
              const response = await fetch(requestUrl, controller ? { signal: controller.signal } : undefined)
              if (!response.ok) {
                const error = new Error(`Image failed: ${response.status}`)
                error.isCorsLikeDownloadError = requestUrl != url
                throw error
              }
              const contentType = response.headers && response.headers.get ? response.headers.get('content-type') : ''
              const normalizedContentType = String(contentType).toLowerCase()
              const isImageResponse = normalizedContentType.includes('image/') || normalizedContentType.includes('octet-stream')
              if (contentType && !isImageResponse) {
                const error = new Error('Image proxy returned non-image content')
                error.isCorsLikeDownloadError = true
                throw error
              }
              const blob = await this.readPaperImageResponseBlob(response, progressHandler)
              if (task) {
                this.ensurePaperDownloadTaskActive(task)
              }
              const imageMimeType = this.getPaperImageMimeType(url)
              if (imageMimeType && !String(blob.type || '').toLowerCase().includes('image/')) {
                return new Blob([blob], { type: imageMimeType })
              }
              return blob
            } catch (error) {
              if (error && (error.name == 'AbortError' || task && task.cancelled)) {
                const cancelError = new Error('Download cancelled')
                cancelError.isPaperDownloadCancelled = true
                throw cancelError
              }
              error.isCorsLikeDownloadError = true
              lastError = error
            } finally {
              if (task) {
                this.unregisterPreviewPerformanceTaskAbortController(task, controller)
              }
            }
          }
          throw lastError || new Error('Image download failed')
        }
        const requestUrl = this.getPaperImageFetchUrl(url)
        let response
        let controller = null
        try {
          if (task) {
            this.ensurePaperDownloadTaskActive(task)
          }
          if (typeof AbortController !== 'undefined') {
            controller = new AbortController()
            if (task) {
              this.registerPreviewPerformanceTaskAbortController(task, controller)
            }
          }
          response = await fetch(requestUrl, controller ? { signal: controller.signal } : undefined)
        } catch (error) {
          if (error && (error.name == 'AbortError' || task && task.cancelled)) {
            const cancelError = new Error('Download cancelled')
            cancelError.isPaperDownloadCancelled = true
            throw cancelError
          }
          error.isCorsLikeDownloadError = true
          throw error
        } finally {
          if (task) {
            this.unregisterPreviewPerformanceTaskAbortController(task, controller)
          }
        }
        if (!response.ok) {
          throw new Error(`Image failed: ${response.status}`)
        }
        const contentType = response.headers && response.headers.get ? response.headers.get('content-type') : ''
        if (contentType && !String(contentType).toLowerCase().includes('image/')) {
          const error = new Error('\u56fe\u7247\u4ee3\u7406\u672a\u751f\u6548\uff0c\u8fd4\u56de\u5185\u5bb9\u4e0d\u662f\u56fe\u7247')
          error.isCorsLikeDownloadError = true
          throw error
        }
        const blob = await response.blob()
        if (progressHandler) {
          progressHandler(100)
        }
        return blob
      }, { onProgress, task })
    },
    async readPaperImageResponseBlob(response, onProgress) {
      const contentType = response.headers && response.headers.get ? response.headers.get('content-type') : ''
      const contentLength = Number(response.headers && response.headers.get ? response.headers.get('content-length') : 0)
      if (!response.body || typeof response.body.getReader != 'function' || !contentLength) {
        const blob = await response.blob()
        if (onProgress) {
          onProgress(100)
        }
        return blob
      }
      const reader = response.body.getReader()
      const chunks = []
      let receivedLength = 0
      let result = await reader.read()
      while (!result.done) {
        chunks.push(result.value)
        receivedLength += result.value.length
        if (onProgress) {
          onProgress(Math.min(100, Math.round((receivedLength / contentLength) * 100)))
        }
        result = await reader.read()
      }
      if (onProgress) {
        onProgress(100)
      }
      return new Blob(chunks, { type: contentType || 'application/octet-stream' })
    },
    getPaperImageFetchUrl(url) {
      return url || ''
    },
    getPaperImageFetchUrls(url) {
      return url ? [url] : []
    },
    getPaperProxyFetchUrl(url) {
      return url || ''
    },
    getPaperProxyBaseUrl() {
      return ''
    },
    canUsePaperImageProxy() {
      return false
    },
    isPaperUnexpectedProxyResponse() {
      return false
    },
    getPaperImageMimeType(url) {
      const extension = this.getUrlExtension(url, '').toLowerCase()
      if (extension == 'jpg' || extension == 'jpeg') {
        return 'image/jpeg'
      }
      if (extension == 'png') {
        return 'image/png'
      }
      if (extension == 'webp') {
        return 'image/webp'
      }
      if (extension == 'gif') {
        return 'image/gif'
      }
      if (extension == 'bmp') {
        return 'image/bmp'
      }
      return ''
    },
    isLikelyCorsDownloadError(error) {
      return !!(error && (error.isCorsLikeDownloadError || error.name == 'TypeError' || String(error.message || '').toLowerCase().includes('failed to fetch')))
    },
    downloadExternalUrl(url, fileName) {
      if (!url) {
        return
      }
      const link = document.createElement('a')
      link.href = url
      link.download = this.sanitizeFileName(fileName) || ''
      link.target = '_blank'
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    getPaperPageFileName(page, index, extension) {
      const pageNo = page && page.pageNo ? page.pageNo : index + 1
      return `${pageNo}.${extension}`
    },
    blobToArrayBuffer(blob) {
      if (blob && typeof blob.arrayBuffer == 'function') {
        return blob.arrayBuffer()
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsArrayBuffer(blob)
      })
    },
    imageBlobToJpegPage(blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          const width = image.naturalWidth || image.width || 1
          const height = image.naturalHeight || image.height || 1
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const context = canvas.getContext('2d')
          if (context && 'imageSmoothingEnabled' in context) {
            context.imageSmoothingEnabled = true
            if ('imageSmoothingQuality' in context) {
              context.imageSmoothingQuality = 'high'
            }
          }
          context.fillStyle = '#fff'
          context.fillRect(0, 0, width, height)
          context.drawImage(image, 0, 0, width, height)
          URL.revokeObjectURL(url)
          canvas.toBlob(async jpegBlob => {
            if (!jpegBlob) {
              reject(new Error('Image convert failed'))
              return
            }
            resolve({
              width,
              height,
              data: new Uint8Array(await this.blobToArrayBuffer(jpegBlob))
            })
          }, 'image/jpeg', 1)
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Image load failed'))
        }
        image.src = url
      })
    },
    buildPdfBlob(imagePages) {
      const encoder = new TextEncoder()
      const chunks = []
      const offsets = []
      let offset = 0
      const writeString = value => {
        const bytes = encoder.encode(value)
        chunks.push(bytes)
        offset += bytes.length
      }
      const writeBytes = bytes => {
        chunks.push(bytes)
        offset += bytes.length
      }
      const objectCount = 2 + imagePages.length * 3
      const writeObject = (id, parts) => {
        offsets[id] = offset
        writeString(`${id} 0 obj\n`)
        parts.forEach(part => {
          if (typeof part == 'string') {
            writeString(part)
          } else {
            writeBytes(part)
          }
        })
        writeString('\nendobj\n')
      }
      writeString('%PDF-1.4\n')
      writeObject(1, ['<< /Type /Catalog /Pages 2 0 R >>'])
      const pageIds = imagePages.map((page, index) => 3 + index * 3)
      writeObject(2, [`<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`])
      imagePages.forEach((page, index) => {
        const pageId = 3 + index * 3
        const contentId = pageId + 1
        const imageId = pageId + 2
        const layout = this.$getDownloadPdfPageLayout(page)
        const imageName = `Im${index + 1}`
        const content = `q\n${layout.drawWidth} 0 0 ${layout.drawHeight} ${layout.offsetX} ${layout.offsetY} cm\n/${imageName} Do\nQ`
        writeObject(pageId, [`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${layout.pageWidth} ${layout.pageHeight}] /Resources << /XObject << /${imageName} ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>`])
        writeObject(contentId, [`<< /Length ${encoder.encode(content).length} >>\nstream\n${content}\nendstream`])
        writeObject(imageId, [
          `<< /Type /XObject /Subtype /Image /Width ${layout.imageWidth} /Height ${layout.imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.data.length} >>\nstream\n`,
          page.data,
          '\nendstream'
        ])
      })
      const xrefOffset = offset
      writeString(`xref\n0 ${objectCount + 1}\n`)
      writeString('0000000000 65535 f \n')
      for (let id = 1; id <= objectCount; id++) {
        writeString(`${String(offsets[id]).padStart(10, '0')} 00000 n \n`)
      }
      writeString(`trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`)
      return new Blob(chunks, { type: 'application/pdf' })
    },
    toggleFavorite(item) {
      if (!item) {
        return
      }
      this.$set(item, 'favorite', !item.favorite)
      this.moreMenuId = ''
      if (this.effectivePaperFilter == 'favorite' && !item.favorite) {
        this.selectedPaperIds = this.selectedPaperIds.filter(id => id != item.id)
        this.selectedPaperId = this.selectedPaperIds[0] || ''
      }
    },
    openPaperItem(item, options = {}) {
      if (!item) {
        return
      }
      if (
        this.detailMode &&
        this.currentPaper &&
        this.currentPaper.resourceId &&
        item.resourceId &&
        String(this.currentPaper.resourceId) == String(item.resourceId)
      ) {
        return
      }
      const openOptions = options && typeof options == 'object' && typeof options.preventDefault != 'function'
        ? Object.assign({}, options)
        : {}
      if (!Object.prototype.hasOwnProperty.call(openOptions, 'force')) {
        openOptions.force = true
      }
      if (item.isFolder || !item.resourceId) {
        this.clearSelection()
        this.fakeAction(item.isFolder ? '\u6253\u5f00\u6587\u4ef6\u5939' : '\u6253\u5f00\u8d44\u6e90')
        return
      }
      if (this.isPaperEncryptedItem(item)) {
        this.clearSelection()
        this.resetPaperDetailBeforePasswordPrompt()
        this.openPaperPasswordModal(item, '', false, 'open', [], openOptions)
        return
      }
      this.openPaperDetail(item, '', openOptions)
    },
    async openPaperDetail(item, password = '', options = {}) {
      this.rememberPaperListScrollPosition()
      this.clearSelection()
      this.detailMode = true
      this.currentPaper = item
      this.currentPaperPassword = password || ''
      this.clearPreviewZoomTip()
      return this.loadPaperDetail(item, Object.assign({}, options, { password: this.currentPaperPassword }))
    },
    resetPaperDetailBeforePasswordPrompt() {
      this.paperDetailRequestSeq += 1
      this.detailMode = false
      this.currentPaper = null
      this.currentPaperPassword = ''
      this.paperDetail = null
      this.paperDetailLoading = false
      this.paperDetailProgress = 0
      this.paperDetailError = ''
      this.clearPaperPreviewObjectUrls({ revoke: false })
      this.currentPageNo = 1
      this.pageGridOpen = false
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = ''
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
      this.stopPaperPreviewDrag()
      this.resetPaperZoom()
      this.clearPreviewZoomTip()
    },
    openPaperPasswordModal(item, error = '', keepInput = false, action = 'open', items = [], openOptions = {}) {
      this.paperPasswordTarget = item
      this.paperPasswordAction = action == 'download' ? 'download' : 'open'
      this.paperPasswordItems = Array.isArray(items) ? items.filter(Boolean) : []
      this.paperPasswordOpenOptions = openOptions && typeof openOptions == 'object' ? Object.assign({}, openOptions) : null
      this.paperPasswordError = error || ''
      this.paperPasswordVisible = false
      this.paperPasswordSubmitting = false
      if (!keepInput) {
        this.paperPasswordInput = ''
      }
      this.paperPasswordModalOpen = true
      this.$nextTick(() => {
        const input = this.$refs.paperPasswordInput
        if (input && typeof input.focus == 'function') {
          input.focus()
        }
      })
    },
    closePaperPasswordModal() {
      if (this.paperPasswordSubmitting) {
        return
      }
      this.paperPasswordRequestSeq += 1
      this.paperPasswordModalOpen = false
      this.paperPasswordTarget = null
      this.paperPasswordInput = ''
      this.paperPasswordVisible = false
      this.paperPasswordError = ''
      this.paperPasswordAction = 'open'
      this.paperPasswordItems = []
      this.paperPasswordOpenOptions = null
    },
    togglePaperPasswordVisible() {
      this.paperPasswordVisible = !this.paperPasswordVisible
    },
    async confirmPaperPassword() {
      if (this.paperPasswordSubmitting) {
        return
      }
      const item = this.paperPasswordTarget
      const rawPassword = String(this.paperPasswordInput || '')
      if (!rawPassword) {
        this.paperPasswordError = this.ui.passwordRequired
        this.$nextTick(() => {
          const input = this.$refs.paperPasswordInput
          if (input && typeof input.focus == 'function') {
            input.focus()
          }
        })
        return
      }
      if (!item) {
        this.closePaperPasswordModal()
        return
      }
      const action = this.paperPasswordAction == 'download' ? 'download' : 'open'
      const actionItems = Array.isArray(this.paperPasswordItems) ? this.paperPasswordItems.slice() : []
      this.paperPasswordSubmitting = true
      this.paperPasswordError = ''
      const requestId = this.paperPasswordRequestSeq + 1
      this.paperPasswordRequestSeq = requestId
      try {
        const hashedPassword = await this.encryptPaperPasswordToSHA256String(rawPassword)
        if (requestId != this.paperPasswordRequestSeq) {
          return
        }
        const ret = await checkCloudPassword({ password: hashedPassword }, { skipGlobalErrorMessage: true })
        if (requestId != this.paperPasswordRequestSeq) {
          return
        }
        if (this.isPaperApiCodeFailed(ret)) {
          this.paperPasswordError = this.getPaperPasswordErrorMessage()
          return
        }
        const passwordOpenOptions = this.paperPasswordOpenOptions && typeof this.paperPasswordOpenOptions == 'object'
          ? this.paperPasswordOpenOptions
          : {}
        this.paperPasswordModalOpen = false
        this.paperPasswordSubmitting = false
        this.paperPasswordTarget = null
        this.paperPasswordError = ''
        this.paperPasswordInput = ''
        this.paperPasswordVisible = false
        this.paperPasswordAction = 'open'
        this.paperPasswordItems = []
        this.paperPasswordOpenOptions = null
        if (action == 'download') {
          this.openPaperDownloadFormatModal(actionItems.length > 0 ? actionItems : [item], { password: hashedPassword })
          return
        }
        await this.openPaperDetail(item, hashedPassword, passwordOpenOptions)
      } catch (error) {
        if (requestId != this.paperPasswordRequestSeq) {
          return
        }
        if (this.isPaperPasswordCodeError(error) || this.isPaperApiCodeFailed(error)) {
          this.paperPasswordError = this.getPaperPasswordErrorMessage()
        } else {
          this.paperPasswordError = this.getPaperApiErrorMessage(error) || this.getPaperPasswordErrorMessage()
        }
      } finally {
        if (requestId == this.paperPasswordRequestSeq) {
          this.paperPasswordSubmitting = false
        }
      }
    },
    async encryptPaperPasswordToSHA256String(pass) {
      if (!pass) {
        return ''
      }
      if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle || typeof TextEncoder === 'undefined') {
        throw new Error('\u5f53\u524d\u6d4f\u89c8\u5668\u4e0d\u652f\u6301\u5bc6\u7801\u52a0\u5bc6')
      }
      const bytes = new TextEncoder().encode(pass)
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', bytes)
      return Array.from(new Uint8Array(hashBuffer))
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase()
    },
    closePaperDetail() {
      this.paperDetailRequestSeq += 1
      this.detailMode = false
      this.currentPaper = null
      this.currentPaperPassword = ''
      this.paperDetail = null
      this.paperDetailLoading = false
      this.paperDetailProgress = 0
      this.paperDetailError = ''
      this.clearPaperPreviewObjectUrls({ revoke: false })
      this.currentPageNo = 1
      this.pageGridOpen = false
      this.pageGridReturnPageNo = 1
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = ''
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
      this.stopPaperPreviewDrag()
      this.resetPaperZoom()
      this.clearPreviewZoomTip()
      this.restorePaperListScrollPosition()
    },
    closePageGridToPreview() {
      if (!this.pageGridOpen) {
        return false
      }
      const targetPageNo = Number(this.pageGridReturnPageNo) || Number(this.currentPageNo) || 1
      const returnZoomLevel = this.takePreviewGridZoomLevel('paper')
      this.pageGridOpen = false
      this.currentPageNo = targetPageNo
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = ''
      this.cancelMobilePreviewGridTouch()
      this.setPaperZoomLevel(returnZoomLevel)
      return true
    },
    handleDetailBack() {
      if (this.closePageGridToPreview()) {
        return
      }
      if (this.paperDetailLoading) {
        this.closePaperDetail()
        if (this.detailBackRoot) {
          this.$emit('back-root')
        }
        return
      }
      this.closePaperDetail()
      if (this.detailBackRoot) {
        this.$emit('back-root')
      }
    },
    toggleRecordPanel() {
      if (this.paperDetailLoading || !this.canShowRecordButton || (this.contextPreviewMode && this.detailRecordings.length == 0)) {
        return
      }
      this.recordPanelOpen = !this.recordPanelOpen
      if (!this.recordPanelOpen) {
        this.recordTextOpen = false
        this.activeRecording = null
        this.resetRecordTextState()
      }
    },
    handleRecordPanelBack() {
      if (this.contextPreviewMode && this.recordTextOpen) {
        this.closeRecordingText()
        return
      }
      this.closeRecordPanel()
    },
    closeRecordPanel() {
      if (this.paperDetailLoading) {
        return
      }
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
    },
    togglePageGrid() {
      if (this.paperDetailLoading || this.pageGridOpen || !this.canUsePageGridButton) {
        return
      }
      this.pageGridReturnPageNo = Number(this.currentPageNo) || 1
      this.rememberPreviewGridZoomLevel('paper', this.zoomLevel)
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = this.pageGridReturnPageNo
      this.pageGridOpen = true
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
      this.clearPreviewZoomTip()
    },
    selectCurrentDetailPageInGrid() {
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = Number(this.currentPageNo) || 1
    },
    isDetailPageSelected(pageNo) {
      return this.selectedDetailPageNos.includes(pageNo)
    },
    toggleDetailPageSelection(pageNo, event) {
      if (this.paperDetailLoading) {
        return
      }
      const result = selectItemWithWindowsRange(
        this.detailPages,
        this.selectedDetailPageNos,
        { pageNo },
        this.selectedDetailPageAnchorNo || this.currentPageNo,
        event,
        page => page && page.pageNo
      )
      this.selectedDetailPageNos = result.selectedIds
      this.selectedDetailPageAnchorNo = result.anchorId
    },
    openDetailPageFromGrid(pageNo) {
      if (this.paperDetailLoading) {
        return
      }
      const isSamePage = Number(pageNo) == Number(this.pageGridReturnPageNo)
      const returnZoomLevel = this.takePreviewGridZoomLevel('paper')
      this.currentPageNo = pageNo
      this.pageGridOpen = false
      if (isSamePage) {
        this.setPaperZoomLevel(returnZoomLevel)
        return
      }
      this.resetPaperZoom()
      this.clearPreviewZoomTip()
    },
    selectDetailPage(pageNo) {
      if (this.paperDetailLoading) {
        return
      }
      this.currentPageNo = pageNo
      this.pageGridOpen = false
      this.clearPreviewZoomTip()
      this.centerPaperPreviewScroll()
    },
    goPrevPage() {
      if (this.isPaperPreviewNavigationLocked()) {
        return
      }
      if (this.currentPageIndex <= 0) {
        return
      }
      this.currentPageNo = this.detailPages[this.currentPageIndex - 1].pageNo
      this.clearPreviewZoomTip()
    },
    goNextPage() {
      if (this.isPaperPreviewNavigationLocked()) {
        return
      }
      if (this.currentPageIndex >= this.detailPages.length - 1) {
        return
      }
      this.currentPageNo = this.detailPages[this.currentPageIndex + 1].pageNo
      this.clearPreviewZoomTip()
    },
    zoomIn() {
      if (this.paperDetailLoading || this.pageGridOpen) {
        return
      }
      const nextLevel = this.setPaperZoomLevel(this.zoomLevel + 1)
      this.showPreviewZoomTip('paper', 'in', nextLevel, this.ui)
    },
    zoomOut() {
      if (this.paperDetailLoading || this.pageGridOpen) {
        return
      }
      const nextLevel = this.setPaperZoomLevel(this.zoomLevel - 1)
      this.showPreviewZoomTip('paper', 'out', nextLevel, this.ui)
    },
    resetPaperZoom() {
      this.setPaperZoomLevel(0)
    },
    setPaperZoomLevel(level, options = {}) {
      const nextLevel = this.clampPreviewZoomLevel(level)
      this.zoomLevel = nextLevel
      this.zoomValue = this.getPaperZoomValue(nextLevel)
      if (!options.keepScrollPosition) {
        this.centerPaperPreviewScroll()
      }
      return nextLevel
    },
    getPaperZoomValue(level) {
      return this.getPreviewZoomValue(level)
    },
    centerPaperPreviewScroll() {
      this.$nextTick(() => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame == 'function') {
          window.requestAnimationFrame(this.applyPaperPreviewCenterScroll)
          return
        }
        this.applyPaperPreviewCenterScroll()
      })
    },
    applyPaperPreviewCenterScroll() {
      const shell = this.$refs.paperPreviewShell
      if (!shell) {
        return
      }
      shell.scrollLeft = Math.max(0, (shell.scrollWidth - shell.clientWidth) / 2)
      shell.scrollTop = Math.max(0, (shell.scrollHeight - shell.clientHeight) / 2)
    },
    startPaperPreviewDrag(event) {
      const shell = this.$refs.paperPreviewShell
      if (this.paperDetailLoading || !event || event.button !== 0 || !shell) {
        return
      }
      if (shell.scrollWidth <= shell.clientWidth && shell.scrollHeight <= shell.clientHeight) {
        return
      }
      event.preventDefault()
      this.paperPreviewDragState = {
        x: event.clientX,
        y: event.clientY,
        scrollLeft: shell.scrollLeft,
        scrollTop: shell.scrollTop
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('mousemove', this.handlePaperPreviewDrag)
        document.addEventListener('mouseup', this.stopPaperPreviewDrag)
      }
    },
    handlePaperPreviewDrag(event) {
      const shell = this.$refs.paperPreviewShell
      const state = this.paperPreviewDragState
      if (!shell || !state || !event) {
        return
      }
      shell.scrollLeft = state.scrollLeft - (event.clientX - state.x)
      shell.scrollTop = state.scrollTop - (event.clientY - state.y)
    },
    stopPaperPreviewDrag() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousemove', this.handlePaperPreviewDrag)
        document.removeEventListener('mouseup', this.stopPaperPreviewDrag)
      }
      this.paperPreviewDragState = null
    },
    resetRecordTextState() {
      this.recordTextRequestSeq += 1
      this.recordTextLoading = false
      this.recordTextError = ''
    },
    closeRecordingText() {
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
    },
    openRecordingText(recording) {
      if (!this.hasTranscript(recording)) {
        return
      }
      this.resetRecordTextState()
      this.activeRecording = recording
      this.recordTextOpen = true
      if (!recording.transcriptText && recording.transcriptTextUrl) {
        this.loadRecordingTranscript(recording)
      }
    },
    async loadRecordingTranscript(recording) {
      const url = recording && recording.transcriptTextUrl ? String(recording.transcriptTextUrl).trim() : ''
      if (!url) {
        return
      }
      const requestId = this.recordTextRequestSeq + 1
      this.recordTextRequestSeq = requestId
      this.recordTextLoading = true
      this.recordTextError = ''
      try {
        let text = ''
        if (url.startsWith('mock://')) {
          text = ''
        } else {
          const transcriptRawText = await this.fetchRecordingTranscriptText(url)
          text = this.normalizeTranscriptText(transcriptRawText)
        }
        if (requestId != this.recordTextRequestSeq || this.activeRecording !== recording) {
          return
        }
        this.$set(recording, 'transcriptText', text || this.ui.noTranscript)
        this.recordTextError = ''
      } catch (error) {
        if (requestId == this.recordTextRequestSeq && this.activeRecording === recording) {
          this.recordTextError = this.ui.loadFailed
        }
      } finally {
        if (requestId == this.recordTextRequestSeq && this.activeRecording === recording) {
          this.recordTextLoading = false
        }
      }
    },
    async fetchRecordingTranscriptText(url) {
      const sourceUrl = String(url || '').trim()
      const requestUrls = [sourceUrl]
      try {
        const fallbackUrls = this.getPaperImageFetchUrls(sourceUrl)
        if (Array.isArray(fallbackUrls)) {
          fallbackUrls.forEach(item => {
            if (item && !requestUrls.includes(item)) {
              requestUrls.push(item)
            }
          })
        }
      } catch (error) {
        // The image proxy helpers are optional; plain text files should still load directly.
      }
      let lastError = null
      for (const requestUrl of requestUrls) {
        try {
          const response = await fetch(requestUrl)
          if (!response.ok) {
            throw new Error(`Transcript failed: ${response.status}`)
          }
          return await response.text()
        } catch (error) {
          error.isTranscriptFetchError = true
          lastError = error
        }
      }
      throw lastError || new Error('Transcript failed')
    },
    isTranscriptTextFileUrl(url) {
      return /\.txt(?:[?#].*)?$/i.test(String(url || '').trim())
    },
    normalizeTranscriptText(value) {
      const text = String(value || '').trim()
      if (!text) {
        return ''
      }
      const parsed = this.parseMetaJson(text)
      if (parsed) {
        const extracted = this.extractTranscriptTextPayload(parsed)
        if (extracted) {
          return extracted
        }
      }
      return text
    },
    extractTranscriptTextPayload(payload) {
      if (payload === undefined || payload === null) {
        return ''
      }
      if (Array.isArray(payload)) {
        return payload.map(item => this.extractTranscriptTextPayload(item)).filter(Boolean).join('\n')
      }
      if (typeof payload == 'object') {
        const direct = this.pickValue(payload, ['transcriptText', 'text', 'content', 'transcript', 'data', 'result'])
        if (direct !== undefined) {
          return this.extractTranscriptTextPayload(direct)
        }
        return ''
      }
      return String(payload).trim()
    },
    hasTranscript(recording) {
      return !!(recording && (recording.transcriptText || recording.transcriptTextUrl))
    },
    enqueueRecordingDownload(recording) {
      const url = recording && recording.fileUrl ? String(recording.fileUrl).trim() : ''
      if (!url || url.startsWith('mock://')) {
        this.fakeAction(this.ui.download)
        return
      }
      if (!this.$canStartDownloadBatch(1, 1)) {
        return
      }
      const task = this.createRecordingDownloadTask(recording)
      this.$enqueuePaperDownloadTask(task, () => this.runRecordingDownloadTask(task))
    },
    createRecordingDownloadTask(recording) {
      const url = recording && recording.fileUrl ? String(recording.fileUrl).trim() : ''
      const fileName = this.sanitizeFileName(this.getRecordingDownloadName(recording)) || this.getUrlDownloadName(url, this.ui.recordings)
      return {
        id: `paper-recording-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind: 'recording-file',
        name: this.sanitizeFileName(recording && recording.recordingName ? recording.recordingName : this.ui.recordings) || this.ui.recordings,
        fileName,
        sourceRecording: recording || null,
        sourceUrl: url,
        openUrl: '',
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    async runRecordingDownloadTask(task) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        const url = task && task.sourceUrl ? String(task.sourceUrl).trim() : ''
        if (!url || url.startsWith('mock://')) {
          throw new Error('No recording file')
        }
        this.updatePaperDownloadTask(task, {
          state: 'running',
          progress: Math.max(task.progress || 0, 5),
          statusText: this.ui.downloading
        })
        const blob = await this.fetchPaperBinaryBlob(url, percent => {
          this.updatePaperDownloadTask(task, {
            progress: Math.max(5, Math.min(95, Number(percent) || 0)),
            statusText: this.ui.downloading
          })
        }, task)
        this.ensurePaperDownloadTaskActive(task)
        this.updatePaperDownloadTask(task, { progress: 99, statusText: this.ui.savingFile })
        this.savePaperDownloadBlob(task, blob)
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: error && error.message ? error.message : this.ui.downloadFailed
        })
      }
    },
    downloadCurrentPage() {
      if (this.paperDetailLoading) {
        return
      }
      if (this.pageGridOpen) {
        if (this.selectedDetailPages.length > 0) {
          this.openDetailPagesDownloadFormatModal(this.selectedDetailPages)
          return
        }
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (this.currentPage && this.currentPage.imageUrl) {
        this.openDetailPagesDownloadFormatModal([this.currentPage])
        return
      }
      if (this.currentPage && this.currentPage.pdfPreviewUrl) {
        this.downloadExternalUrl(this.currentPage.pdfPreviewUrl, this.$getDownloadFileName(this.paperDetailTitle, 'pdf', 'Paper', this.sanitizeFileName))
        return
      }
      if (this.isPaperDetailBlankPage(this.currentPage)) {
        this.openDetailPagesDownloadFormatModal([this.currentPage])
        return
      }
      this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
    },
    startDetailPagesDownload(pages, format = 'png') {
      const list = Array.isArray(pages) ? pages.filter(page => this.isPaperDetailPageDownloadable(page)) : []
      if (list.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (!this.$canStartDownloadBatch(list.length, 1, { skipSelectionLimit: true })) {
        return
      }
      const task = this.createDetailPagesDownloadTask(list, format)
      this.$enqueuePaperDownloadTask(task, () => this.runDetailPagesDownloadTask(task))
      if (this.pageGridOpen) {
        this.selectedDetailPageNos = []
        this.selectedDetailPageAnchorNo = ''
      }
    },
    isPaperDetailBlankPage(page) {
      return !!(page && Number(page.pageNo) > 0 && !page.imageUrl && !page.pageImageUrl && !page.pdfPreviewUrl)
    },
    isPaperDetailPageDownloadable(page) {
      return !!(page && page.imageUrl) || this.isPaperDetailBlankPage(page)
    },
    createDetailPagesDownloadTask(pages, format = 'png') {
      const baseName = this.$getDownloadBaseName(this.paperDetailTitle, 'Paper', this.sanitizeFileName)
      const safeFormat = format == 'png' ? 'png' : 'pdf'
      const isSingle = pages.length == 1
      const extension = safeFormat == 'pdf' ? 'pdf' : (isSingle ? 'png' : 'zip')
      return {
        id: `paper-page-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind: 'detail-pages',
        name: baseName,
        fileName: isSingle ? `${baseName} page ${pages[0].pageNo || 1}.${extension}` : `${baseName}.${extension}`,
        format: safeFormat,
        pages: pages.slice(),
        pngFiles: [],
        pdfPages: [],
        failedPageIndex: 0,
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    async runDetailPagesDownloadTask(task) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        const pages = Array.isArray(task.pages) ? task.pages : []
        if (pages.length == 0) {
          throw new Error('No pages')
        }
        if (task.format == 'pdf') {
          await this.downloadPaperAsPdf(task, pages)
        } else {
          const compositeFrameSize = await this.getUnifiedPaperCompositeFrameSize(pages, task)
          const pngFiles = Array.isArray(task.pngFiles) ? task.pngFiles.slice() : []
          for (let i = 0; i < pages.length; i++) {
            this.ensurePaperDownloadTaskActive(task)
            if (pngFiles[i]) {
              continue
            }
            const page = pages[i]
            this.updatePaperDownloadTask(task, {
              failedPageIndex: i,
              progress: this.getPaperDownloadImageProgress(i, pages.length, 5, 85, 0),
              statusText: this.ui.downloading
            })
            let canvas
            if (this.isPaperDetailBlankPage(page)) {
              canvas = this.createPaperBlankPageCanvas(compositeFrameSize)
            } else {
              const blob = await this.fetchPaperImageBlob(page.imageUrl, percent => {
                this.updatePaperDownloadTask(task, {
                  progress: this.getPaperDownloadImageProgress(i, pages.length, 5, 85, percent),
                  statusText: this.ui.downloading
                })
              }, task)
              canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
            }
            const framedBlob = await this.canvasToPngBlob(canvas)
            this.releaseCanvas(canvas)
            pngFiles[i] = {
              fileName: this.getPaperPageFileName(page, i, 'png'),
              buffer: await this.blobToArrayBuffer(framedBlob)
            }
            this.updatePaperDownloadTask(task, {
              pngFiles: pngFiles.slice(),
              failedPageIndex: i + 1
            })
          }
          this.ensurePaperDownloadTaskActive(task)
          if (pages.length == 1 && pngFiles[0]) {
            this.updatePaperDownloadTask(task, { progress: 99, statusText: this.ui.savingFile })
            this.savePaperDownloadBlob(task, new Blob([pngFiles[0].buffer], { type: 'image/png' }))
          } else {
            const zip = new JSZip()
            pngFiles.forEach((file, index) => {
              if (file) {
                zip.file(file.fileName || this.getPaperPageFileName(pages[index], index, 'png'), file.buffer)
              }
            })
            this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u6253\u5305 ZIP' })
            const zipBlob = await zip.generateAsync({ type: 'blob' }, metadata => {
              this.updatePaperDownloadTask(task, {
                progress: Math.min(98, Math.round(90 + (metadata.percent || 0) * 0.08)),
                statusText: '\u6253\u5305 ZIP'
              })
            })
            this.ensurePaperDownloadTaskActive(task)
            this.savePaperDownloadBlob(task, zipBlob)
          }
        }
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: isCorsLikeError ? this.ui.imageReadFailed : (error && error.message ? error.message : this.ui.downloadFailed)
        })
      }
    },
    async downloadUrl(url, fileName) {
      if (url && !String(url).startsWith('mock://')) {
        const safeName = this.sanitizeFileName(fileName) || this.getUrlDownloadName(url, this.paperDetailTitle)
        try {
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`Download failed: ${response.status}`)
          }
          const blob = await response.blob()
          saveAs(blob, safeName)
        } catch (error) {
          const link = document.createElement('a')
          link.href = url
          link.download = safeName
          link.target = '_blank'
          link.rel = 'noopener'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
        return
      }
      this.fakeAction(this.ui.download)
    },
    getCurrentPageDownloadName() {
      const pageNo = this.currentPage && this.currentPage.pageNo ? this.currentPage.pageNo : this.currentPageIndex + 1
      const extension = this.getUrlExtension(this.currentPage && this.currentPage.imageUrl, 'png')
      const baseName = this.$getDownloadBaseName(this.paperDetailTitle, this.ui.download, this.sanitizeFileName)
      return `${baseName}-\u7b2c${pageNo}\u9875.${extension}`
    },
    getRecordingDownloadName(recording) {
      const name = recording && recording.recordingName ? recording.recordingName : this.ui.recordings
      const extension = this.getUrlExtension(recording && recording.fileUrl, 'mp4')
      return this.$getDownloadFileName(name, extension, this.ui.recordings, this.sanitizeFileName)
    },
    getUrlDownloadName(url, fallbackName) {
      const extension = this.getUrlExtension(url, '')
      const name = this.$getDownloadBaseName(fallbackName, this.ui.download, this.sanitizeFileName)
      return extension ? `${name}.${extension}` : name
    },
    getUrlExtension(url, fallback = '') {
      const cleanPath = String(url || '').split(/[?#]/)[0]
      const fileName = cleanPath.split(/[\\/]/).pop() || ''
      const dotIndex = fileName.lastIndexOf('.')
      if (dotIndex < 0 || dotIndex == fileName.length - 1) {
        return fallback
      }
      return fileName.slice(dotIndex + 1).toLowerCase()
    },
    sanitizeFileName(name) {
      return String(name || '')
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, ' ')
        .trim()
    },
    fakeAction() {
      this.moreMenuId = ''
      this.$message.info(this.ui.fakeTip)
    },
    markCoverFailed(item) {
      if (item) {
        this.$set(item, 'coverFailed', true)
      }
    },
    shouldDisplayCover(item) {
      return !!(item && item.cover && !item.coverFailed && !item.isFolder && !item.encrypted)
    },
    shouldDisplayGridCover(item) {
      return !!(item && this.getPaperGridCoverImageUrl(item) && !item.isFolder && !item.encrypted)
    },
    shouldShowPaperCoverFold(item) {
      return this.isPaperPdfFileItem(item)
    },
    isListPlaceholderCover(item) {
      return !!(item && !item.isFolder && !item.encrypted && !this.isPaperPdfFileItem(item))
    },
    isPaperPdfFileItem(item) {
      const resourceType = Number(item && item.resourceType)
      return !!(item && !item.isFolder && !item.encrypted && (resourceType === 4 || this.getPaperFileExtension(item) == 'pdf'))
    },
    getFallbackCover(item, mode = this.viewMode) {
      const isGrid = mode == 'grid'
      if (item && item.encrypted) {
        return isGrid ? this.paperIcons.fileGridEncrypt : this.paperIcons.fileListEncrypt
      }
      if (item && item.isFolder) {
        const hasChildren = this.getFolderChildSize(item) > 0
        if (item.template) {
          if (isGrid) {
            return hasChildren ? this.paperIcons.templateGridFilled : this.paperIcons.templateGridEmpty
          }
          return hasChildren ? this.paperIcons.templateListFilled : this.paperIcons.templateListEmpty
        }
        if (isGrid) {
          return hasChildren ? this.paperIcons.folderGridFilled : this.paperIcons.folderGridEmpty
        }
        return hasChildren ? this.paperIcons.folderListFilled : this.paperIcons.folderListEmpty
      }
      const pdfIcon = isGrid ? this.paperIcons.fileGridPdf : this.paperIcons.fileListPdf
      if (this.isPaperPdfFileItem(item)) {
        return pdfIcon
      }
      if (this.isListPlaceholderCover(item)) {
        return this.paperIcons.filePaperEmpty
      }
      const paperIcon = isGrid ? this.paperIcons.fileGridPaper : this.paperIcons.fileListPaper
      return paperIcon
    },
    getPaperFileExtension(item) {
      const raw = item && item.raw && typeof item.raw == 'object' ? item.raw : item
      const candidates = [
        item && item.filePath,
        this.pickString(raw, ['filePath', 'file_path', 'path', 'fileUrl', 'downloadUrl', 'resourceUrl', 'resource_url', 'url', 'src']),
        item && item.name,
        this.pickString(raw, ['name', 'title', 'fileName', 'filename', 'resourceName', 'paperName'])
      ]
      for (let i = 0; i < candidates.length; i += 1) {
        const extension = this.getUrlExtension(candidates[i], '')
        if (extension) {
          return extension
        }
      }
      return this.normalizePaperExtension(this.pickString(raw, ['extension', 'ext', 'suffix', 'fileExtension', 'file_extension', 'fileFormat', 'file_format', 'format']))
    },
    normalizePaperExtension(value) {
      const text = String(value || '').trim().toLowerCase().replace(/^\./, '')
      if (!text) {
        return ''
      }
      const mimeMatch = text.match(/\/([a-z0-9+.-]+)$/)
      const extension = (mimeMatch ? mimeMatch[1] : text).split(/[?#\s]/)[0]
      if (!extension || /^\d+$/.test(extension) || !/^[a-z0-9]{1,8}$/.test(extension)) {
        return ''
      }
      return extension
    },
    getFolderChildSize(item) {
      const value = item && item.childSize !== undefined && item.childSize !== null && item.childSize !== ''
        ? Number(item.childSize)
        : 0
      return Number.isFinite(value) ? value : 0
    },
    getListFileSizeValue(item) {
      const value = this.pickValue(item || {}, ['fileSize', 'fileSizeBytes', 'size'])
      if (value !== undefined && value !== null && value !== '') {
        return value
      }
      const raw = item && item.raw && typeof item.raw == 'object' ? item.raw : {}
      return this.pickValue(raw, ['fileSize', 'fileSizeBytes', 'size'])
    },
    shouldUseItemCountUnit(item) {
      const resourceType = Number(item && item.resourceType)
      return Number.isFinite(resourceType) && (resourceType === 0 || resourceType === 5)
    },
    formatPageCount(item) {
      const childSize = this.getFolderChildSize(item)
      if (this.shouldUseItemCountUnit(item)) {
        return this.joinResourceCountUnit(Math.max(0, childSize), getResourceItemUnitLabel(this.language), this.language)
      }
      return this.joinResourceCountUnit(Math.max(0, childSize), this.ui.page, this.language)
    },
    formatGridChildSize(item) {
      return String(Math.max(0, this.getFolderChildSize(item)))
    },
    formatByteSize(value) {
      const bytes = Number(value)
      if (!Number.isFinite(bytes) || bytes < 0) {
        return '-'
      }
      if (bytes === 0) {
        return '0B'
      }
      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let unitIndex = 0
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex += 1
      }
      const precision = unitIndex == 0 || Number.isInteger(size) ? 0 : 2
      return `${Number(size.toFixed(precision))}${units[unitIndex]}`
    },
    formatDate(value) {
      const time = this.parseDateTime(value)
      if (!time) {
        return '-'
      }
      const date = new Date(time)
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      return `${date.getFullYear()}/${month}/${day}`
    },
    formatDateTime(value) {
      const time = this.parseDateTime(value)
      if (!time) {
        return '-'
      }
      const date = new Date(time)
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      const hour = `${date.getHours()}`.padStart(2, '0')
      const minute = `${date.getMinutes()}`.padStart(2, '0')
      return `${date.getFullYear()}/${month}/${day} ${hour}:${minute}`
    },
    formatDuration(value) {
      const ms = Number(value) || 0
      const totalSeconds = Math.max(0, Math.floor(ms / 1000))
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = `${totalSeconds % 60}`.padStart(2, '0')
      return `${minutes}:${seconds}`
    }
  }
}
</script>

<style scoped>
.paper-workspace {
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #f3f2ed;
  color: #181d1f;
  overflow: hidden;
  font-family: var(--viwoods-wap-font-family);
}

.paper-list-header,
.paper-detail-header {
  height: 55px;
  border-bottom: 1px solid #dededb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px 0 32px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.paper-list-header {
  height: 72px;
  padding-right: 32px;
}

.paper-breadcrumb {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0;
  color: #202428;
  overflow: hidden;
}

.paper-breadcrumb-root {
  border: 0;
  background: transparent;
  padding: 0;
  color: #202428;
  font-size: 24px;
  line-height: 34px;
  font-weight: 700;
  cursor: pointer;
  flex: 0 0 auto;
}

.paper-breadcrumb-segment {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
}

.paper-breadcrumb-segment:not(:last-child) {
  flex: 0 0 auto;
}

.paper-breadcrumb-link {
  max-width: 160px;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 0;
  color: #1f2326;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-breadcrumb-separator {
  width: 32px;
  height: 32px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.paper-breadcrumb strong {
  min-width: 0;
  max-width: 240px;
  color: #1f2326;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-header-actions,
.paper-detail-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.paper-icon-button,
.paper-tool-button,
.paper-order-button,
.paper-back-button,
.paper-filter-button,
.paper-sort-button,
.paper-view-hotspot,
.paper-more-button,
.paper-favorite,
.record-action,
.record-text-head button,
.paper-page-controls button {
  font: inherit;
}

.paper-icon-button,
.paper-tool-button {
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.paper-icon-button img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}

.paper-import-button {
  min-width: 128px;
  width: auto;
  height: 44px;
  border: 0;
  background: #1f3a2d;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  padding: 0 18px;
  white-space: nowrap;
  flex-shrink: 0;
}

.paper-import-button img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.paper-toolbar {
  height: 78px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 32px 0;
  box-sizing: border-box;
  flex-shrink: 0;
}

.paper-filter,
.paper-sort {
  position: relative;
}

.paper-filter-button,
.paper-sort-button {
  height: 36px;
  border: 1px solid #c8c8c5;
  background: transparent;
  color: #202428;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
}

.paper-filter-button {
  width: 138px;
  gap: 10px;
  padding: 0 12px;
}

.paper-sort-button {
  min-width: 88px;
  justify-content: center;
  padding: 0 14px;
}

.paper-filter-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  flex: 0 0 22px;
  display: block;
}

.filter-arrow {
  width: 9px;
  height: 9px;
  border-right: 1px solid #111;
  border-bottom: 1px solid #111;
  transform: rotate(45deg) translateY(-3px);
  margin-left: auto;
}

.filter-arrow.open {
  transform: rotate(225deg) translate(-2px, -1px);
}

.paper-filter-menu,
.paper-sort-menu {
  position: absolute;
  top: 40px;
  z-index: 30;
  background: #fff;
  border: 1px solid #d8d8d8;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .04);
}

.paper-filter-menu {
  left: 0;
  min-width: 100%;
  width: max-content;
  max-width: calc(100vw - 24px);
}

.paper-sort-menu {
  left: auto;
  right: 0;
}

.paper-filter-menu button,
.paper-sort-menu button {
  height: 40px;
  border: 0;
  background: #fff;
  color: #171b1d;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 17px;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
}

.paper-filter-menu button {
  width: max-content;
  min-width: 100%;
  white-space: nowrap;
}

.paper-sort-menu button {
  width: 100%;
}

.paper-filter-menu button.active,
.paper-sort-menu button.active {
  background: #dce2d8;
}

.paper-menu-icon {
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  object-fit: contain;
  display: block;
}

.filter-check {
  width: 20px;
  height: 20px;
  object-fit: contain;
  margin-left: auto;
}

.paper-toolbar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.paper-order-button {
  width: 36px;
  height: 36px;
  border: 1px solid #c8c8c5;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.paper-order-button img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}

.paper-view-switch {
  width: 72px;
  height: 36px;
  position: relative;
  flex: 0 0 72px;
}

.paper-view-switch img {
  width: 72px;
  height: 36px;
  display: block;
  object-fit: contain;
}

.paper-view-hotspot {
  position: absolute;
  top: 0;
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.paper-view-list {
  left: 0;
}

.paper-view-grid {
  right: 0;
}

.paper-selection-layer {
  position: absolute;
  top: 72px;
  right: 0;
  left: 0;
  z-index: 40;
  padding: 12px 12px 10px;
  box-sizing: border-box;
  pointer-events: none;
  will-change: transform;
}

.paper-selection-layer.dragging {
  user-select: none;
}

.paper-selection-bar {
  height: 48px;
  width: 100%;
  min-width: 0;
  padding: 0 16px;
  background: #DDE4D9;
  color: #333;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 18px;
  box-sizing: border-box;
  flex-shrink: 0;
  overflow-x: auto;
  overflow-y: hidden;
  pointer-events: auto;
  cursor: grab;
  scrollbar-width: thin;
}

.paper-selection-layer.dragging .paper-selection-bar {
  cursor: grabbing;
}

.paper-selection-close {
  width: 24px;
  height: 24px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  position: relative;
}

.paper-selection-close span {
  width: 14px;
  height: 14px;
  display: block;
  position: relative;
}

.paper-selection-close span::before,
.paper-selection-close span::after {
  content: '';
  width: 14px;
  height: 1.5px;
  background: #111;
  position: absolute;
  left: 0;
  top: 6px;
}

.paper-selection-close span::before {
  transform: rotate(45deg);
}

.paper-selection-close span::after {
  transform: rotate(-45deg);
}

.paper-selection-bar > span {
  color: #606462;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  flex: 0 0 auto;
  white-space: nowrap;
}

.paper-selection-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  gap: 34px;
}

.paper-selection-actions button {
  height: 32px;
  border: 0;
  background: transparent;
  padding: 0;
  color: #1f2326;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  white-space: nowrap;
}

.paper-selection-actions button:disabled {
  color: #9da39f;
  cursor: not-allowed;
  opacity: .45;
}

.paper-selection-actions img {
  width: 20px;
  height: 20px;
  object-fit: contain;
  display: block;
}

.paper-search-line {
  height: 46px;
  margin: -10px 32px 16px;
  border: 1px solid #d7d9d6;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.paper-search-line img {
  width: 22px;
  height: 22px;
}

.paper-search-line input {
  min-width: 0;
  flex: 1;
  height: 44px;
  border: 0;
  outline: none;
  background: transparent;
  color: #202428;
  font-size: 15px;
}

.paper-search-line button {
  width: 18px;
  height: 18px;
  border: 0;
  background: transparent;
  position: relative;
  cursor: pointer;
}

.paper-search-line button::before,
.paper-search-line button::after {
  content: "";
  width: 16px;
  height: 1px;
  background: #777;
  position: absolute;
  left: 1px;
  top: 8px;
}

.paper-search-line button::before {
  transform: rotate(45deg);
}

.paper-search-line button::after {
  transform: rotate(-45deg);
}

.paper-list-area {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 24px 40px 32px;
  box-sizing: border-box;
}

.paper-list-area /deep/ .ant-spin-nested-loading,
.paper-list-area /deep/ .ant-spin-container {
  min-height: calc(100vh - 150px);
}

.paper-list-area /deep/ .paper-list-spin.ant-spin-nested-loading {
  min-height: calc(100vh - 150px);
}

.paper-list-content {
  min-height: calc(100vh - 150px);
}

.paper-list-area.is-grid .paper-list-content {
  min-width: calc(148px * 4 + 15px * 3);
  padding-top: 7.5px;
  box-sizing: border-box;
}

.paper-list-area /deep/ .paper-list-spin.ant-spin-nested-loading > div > .ant-spin {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
  max-height: none;
  padding-top: 24px;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  color: #275B52;
}

.paper-list-area /deep/ .paper-list-spin.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot {
  position: static;
  margin: 0;
}

.paper-list-area /deep/ .paper-list-spin .ant-spin-dot-item {
  background-color: #275B52;
}

.paper-empty {
  min-height: calc(100vh - 180px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #15191b;
  font-size: 14px;
  gap: 12px;
}

.paper-empty img {
  width: 130px;
  height: auto;
}

.paper-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 148px));
  gap: 29.5px 15px;
  align-items: start;
  min-width: calc(148px * 4 + 15px * 3);
}

.paper-card {
  width: 148px;
  height: 245.4px;
  min-height: 245.4px;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  outline: none;
  z-index: 0;
  --paper-card-height: 245.4px;
  --paper-grid-column-gap: 15px;
  --paper-grid-row-gap: 44px;
  --paper-grid-column-gap-half: calc(var(--paper-grid-column-gap) / 2);
  --paper-grid-row-gap-half: calc(var(--paper-grid-row-gap) / 2);
}

.paper-card.selected {
  z-index: 1;
}

.paper-card.selected::before {
  content: '';
  position: absolute;
  top: calc(-1 * var(--paper-grid-column-gap-half));
  right: calc(-1 * var(--paper-grid-column-gap-half));
  bottom: calc(-1 * var(--paper-grid-row-gap-half));
  left: calc(-1 * var(--paper-grid-column-gap-half));
  background: #DDE4D9;
  pointer-events: none;
  z-index: 0;
}

.paper-card.selected.grid-edge-bottom::before {
  bottom: 0;
}

.paper-card.selected.grid-edge-top::before {
  top: calc(-1 * var(--paper-grid-column-gap-half));
}

.paper-card > * {
  position: relative;
  z-index: 1;
}

.paper-card.selected .paper-cover,
.paper-card:hover .paper-cover,
.paper-card:focus-visible .paper-cover {
  border-color: #b6b8b4;
  background: #f8f8f6;
}

.paper-card.selected .paper-cover {
  background: #fff;
}

.paper-card.selected .paper-cover.template {
  border-style: solid;
}

.paper-cover {
  width: 148px;
  height: 196.4px;
  flex: 0 0 196.4px;
  border: 1px solid #d7d9d6;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
}

.paper-cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.paper-cover-default {
  width: 46px;
  height: auto;
  object-fit: contain;
  display: block;
}

.paper-cover.folded-cover {
  --cover-fold-size: 22px;
  --cover-fold-mask-bleed: 1.5px;
  --cover-fold-backdrop: #f3f2ed;
  border: 0;
}

.paper-card.selected .paper-cover.folded-cover {
  --cover-fold-backdrop: #DDE4D9;
}

.paper-cover.folded-cover::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: var(--cover-fold-size);
  height: var(--cover-fold-size);
  background: var(--cover-fold-backdrop);
  pointer-events: none;
  z-index: 2;
}

.paper-cover.folded-cover::after {
  content: '';
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 148 196.4' preserveAspectRatio='none'%3E%3Cpath d='M0.5 0.5H126.5L147.5 21.5V195.9H0.5V0.5Z' fill='none' stroke='%23d7d9d6' stroke-width='1' stroke-linecap='square' stroke-linejoin='miter'/%3E%3Cpath d='M126.5 0.5V21.5H147.5' fill='none' stroke='%23d7d9d6' stroke-width='1' stroke-linecap='square' stroke-linejoin='miter'/%3E%3C/svg%3E");
  background-size: 100% 100%;
  background-repeat: no-repeat;
  pointer-events: none;
  z-index: 3;
}

.paper-cover-default.placeholder-cover {
  width: 46px;
  height: auto;
  object-fit: contain;
}

.paper-cover-default.folder-icon {
  width: 46px;
  height: auto;
}

.paper-cover-default.encrypt-icon {
  width: 46px;
  height: auto;
}

.paper-cover.template {
  border-style: solid;
}

.paper-card-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 22px;
  flex: 0 0 22px;
  box-sizing: border-box;
  margin-top: 8px;
  min-width: 0;
}

.paper-card-name {
  display: block;
  min-width: 0;
  flex: 1 1 auto;
  height: 22px;
  color: #0f1112;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-card-meta {
  height: 19px;
  flex: 0 0 19px;
  margin-top: 0;
  color: #9a9a9a;
  font-size: 13px;
  line-height: 19px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.paper-card-count {
  min-width: 0;
  max-width: 50%;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-favorite {
  width: 30px;
  height: 30px;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.paper-card .paper-favorite {
  position: absolute;
  right: 0;
  bottom: 17px;
  justify-content: flex-end;
  z-index: 2;
}

.paper-favorite img {
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
}

.paper-favorite-status {
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
}

.paper-card-favorite-status {
  flex: 0 0 18px;
}

.paper-table {
  margin: 0 -12px 0 -20px;
}

.paper-row {
  min-height: 78px;
  border-bottom: 1px solid #dededb;
  display: grid;
  grid-template-columns: 66px minmax(220px, 1fr) 120px 48px 170px 48px 52px 48px 56px;
  align-items: center;
  gap: 0;
  padding: 0 16px;
  box-sizing: border-box;
  cursor: pointer;
  outline: none;
}

.paper-row:hover,
.paper-row.selected,
.paper-row:focus-visible {
  background: #f1eee8;
}

.paper-row-cover {
  width: 54px;
  height: 54px;
  border: 1px solid transparent;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.paper-row-cover-image {
  width: 46px;
  height: auto;
  object-fit: cover;
  display: block;
}

.paper-row-cover-default {
  width: 46px;
  height: auto;
  object-fit: contain;
  display: block;
}

.paper-row-cover-default.placeholder-cover {
  object-fit: cover;
}

.paper-row-cover-default.folder-icon {
  width: 46px;
  height: auto;
}

.paper-row-cover-default.encrypt-icon {
  width: 46px;
  height: auto;
}

.paper-row strong {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 78px;
  min-width: 0;
  color: #121618;
  font-size: 15px;
  line-height: 22px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-row > span:not(.paper-row-cover):not(.paper-more-wrap) {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 78px;
  color: #7d8284;
  font-size: 14px;
  line-height: 22px;
  text-align: center;
}

.paper-row-count {
  grid-column: 3;
}

.paper-row-date {
  grid-column: 5;
}

.paper-more-wrap {
  align-self: center;
  grid-column: 9;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 78px;
}

.paper-row .paper-favorite {
  align-self: center;
  grid-column: 7;
  justify-self: center;
  height: 78px;
}

.paper-row-favorite-status {
  grid-column: 7;
}

.paper-more-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 48px;
  border: 0;
  background: transparent;
  color: #7B7C87;
  font-size: 0;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.paper-more-icon {
  display: block;
  flex: 0 0 12px;
  width: 12px;
  min-width: 12px;
  max-width: 12px;
  height: 4px;
  min-height: 4px;
  max-height: 4px;
  fill: #7B7C87;
  overflow: hidden;
  pointer-events: none;
  shape-rendering: auto;
}

.paper-row:hover .paper-more-button,
.paper-more-button:focus-visible {
  background: #F1EEE8;
}

.paper-more-menu {
  width: 168px;
  position: absolute;
  right: 16px;
  top: 47px;
  z-index: 50;
  border: 1px solid #d8d8d8;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .04);
  padding: 5px 0;
}

.paper-more-menu button {
  width: 100%;
  height: 48px;
  border: 0;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 18px;
  color: #171b1d;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
}

.paper-more-menu button:hover {
  background: #F1EEE8;
}

.paper-more-menu button:disabled {
  color: #9da39f;
  cursor: not-allowed;
  opacity: .45;
}

.paper-more-menu button:disabled:hover {
  background: #fff;
}

.paper-more-menu img {
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  object-fit: contain;
  display: block;
}

.page-fallback {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1f3a2d;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
}

.paper-detail-title {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
}

.paper-back-button {
  width: 26px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  position: relative;
  z-index: 4001;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.paper-back-button span,
.record-text-head button span {
  width: 12px;
  height: 12px;
  border-left: 2px solid #111;
  border-bottom: 2px solid #111;
  transform: rotate(45deg);
  display: block;
}

.paper-detail-title strong {
  min-width: 0;
  color: #14191b;
  font-size: 22px;
  line-height: 32px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-tool-button img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.paper-tool-button.active {
  background: #ebe8e2;
}

.paper-tool-button:disabled {
  opacity: .35;
  cursor: default;
}

.paper-back-button:disabled {
  opacity: .35;
  cursor: default;
}

.paper-detail-body {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  --paper-preview-base-height: max(480px, calc(100vh - 64px));
  --paper-preview-base-width: calc(var(--paper-preview-base-height) * 0.75);
}

.paper-detail-body.is-resolving {
  cursor: progress;
}

.paper-detail-body.is-resolving::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 30;
  cursor: progress;
}

.paper-detail-body /deep/ .ant-spin-nested-loading,
.paper-detail-body /deep/ .ant-spin-container {
  height: 100%;
}

.paper-preview-shell {
  height: calc(100vh - 64px);
  overflow: auto;
  display: block;
  padding: 0 96px 0 32px;
  box-sizing: border-box;
  --paper-preview-zoom: 1;
  cursor: grab;
  user-select: none;
}

.paper-detail-resolving {
  height: calc(100vh - 55px);
  padding: 0 32px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
}

.paper-detail-progress-box {
  width: min(var(--paper-preview-base-width), 100%);
  flex: 0 1 min(var(--paper-preview-base-width), 100%);
  margin: 0 auto;
  color: #15191b;
}

.paper-detail-progress-box strong,
.paper-detail-progress-box span {
  display: block;
  text-align: center;
}

.paper-detail-progress-box strong {
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
}

.paper-detail-progress-box span {
  margin-top: 10px;
  color: #59605d;
  font-size: 16px;
  line-height: 22px;
}

.paper-detail-progress-track {
  width: 100%;
  height: 4px;
  margin-top: 18px;
  background: #d8ddd8;
  overflow: hidden;
}

.paper-detail-progress-track i {
  display: block;
  height: 100%;
  width: 0;
  background: #1f3a2d;
  transition: width .18s ease;
}

.paper-detail-progress-box p {
  margin: 14px 0 0;
  color: #7a3b33;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.paper-detail-progress-box button {
  width: 96px;
  height: 40px;
  border: 0;
  background: #1f3a2d;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px auto 0;
  padding: 0;
  font-size: 15px;
  line-height: 20px;
  cursor: pointer;
}

.paper-preview-shell.dragging {
  cursor: grabbing;
}

.paper-preview-zoom {
  width: calc(var(--paper-preview-base-width) * var(--paper-preview-zoom));
  height: calc(var(--paper-preview-base-height) * var(--paper-preview-zoom));
  margin: 0 auto;
}

.paper-preview-stage {
  width: var(--paper-preview-base-width);
  height: var(--paper-preview-base-height);
  margin-top: 0;
  background: #fff;
  border: 1px solid #d7d9d6;
  transform-origin: top left;
  transform: scale(var(--paper-preview-zoom));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.paper-preview-stage img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.paper-preview-pdf {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: #fff;
}

.paper-preview-empty {
  width: 100%;
  height: 100%;
  background: #fff;
}

.paper-pages-grid {
  height: calc(100vh - 55px);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 16px 15px 82px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-content: start;
  align-items: start;
  gap: 6px;
  min-width: 0;
  box-sizing: border-box;
}

.paper-pages-grid button {
  width: auto;
  height: auto;
  min-height: 0;
  border: 0;
  background: transparent;
  padding: 0 0 29px;
  text-align: left;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  outline: none;
  z-index: 0;
}

.paper-pages-grid button.selected {
  z-index: 1;
}

.paper-pages-grid button.selected::before {
  content: '';
  position: absolute;
  top: -3px;
  right: -3px;
  bottom: -3px;
  left: -3px;
  background: #daded5;
  pointer-events: none;
  z-index: 0;
}

.paper-pages-grid button.selected.grid-edge-top::before {
  top: -3px;
}

.paper-pages-grid button.selected:not(.grid-selected-row-start)::before {
  top: -3px;
}

.paper-pages-grid button.selected.grid-selection-continues-below::before {
  bottom: -3px;
}

.paper-pages-grid button > * {
  position: relative;
  z-index: 1;
}

.paper-pages-grid button span {
  width: 100%;
  height: auto;
  aspect-ratio: 74 / 98;
  flex: 0 0 auto;
  margin: 0 auto;
  border: 1px solid #eee6df;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.paper-pages-grid button:hover span,
.paper-pages-grid button:focus-visible span {
  border-color: #b6b8b4;
  background: #f8f8f6;
}

.paper-pages-grid button.selected span {
  border-color: #b6b8b4;
  background: #fff;
}

.paper-pages-grid button.active span {
  border-color: #b6b8b4;
}

.paper-pages-grid img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.paper-pages-grid strong {
  display: block;
  width: 100%;
  height: 22px;
  flex: 0 0 22px;
  box-sizing: border-box;
  margin-top: 8px;
  padding: 0 4px;
  color: #121618;
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-page-controls {
  position: absolute;
  right: 24px;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.paper-page-controls button {
  width: 36px;
  height: 36px;
  border: 1px solid #d8d8d8;
  background: #f3f2ed;
  position: relative;
  cursor: pointer;
}

.paper-page-controls button::before {
  content: "";
  width: 9px;
  height: 9px;
  border-left: 1px solid #111;
  border-bottom: 1px solid #111;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-35%, -50%) rotate(45deg);
}

.paper-page-controls button:last-child::before {
  transform: translate(-65%, -50%) rotate(225deg);
}

.paper-page-controls button:disabled {
  background: #f3f2ed;
  cursor: default;
}

.paper-page-controls button:disabled::before {
  border-left-color: #b7bbb8;
  border-bottom-color: #b7bbb8;
}

.paper-page-count {
  display: flex;
  align-items: center;
  gap: 10px;
}

.paper-page-count span {
  min-width: 66px;
  height: 36px;
  border: 1px solid #d8d8d8;
  background: #f3f2ed;
  color: #5d6062;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  box-sizing: border-box;
}

.paper-password-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .32);
  z-index: 100;
}

.paper-password-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 372px;
  min-height: 180px;
  transform: translate(-50%, -50%);
  background: #fff;
  z-index: 101;
  padding: 22px 20px 20px;
  box-sizing: border-box;
  box-shadow: 0 2px 12px rgba(0, 0, 0, .12);
}

.paper-password-modal header {
  color: #1d1d1f;
  font-size: 17px;
  line-height: 22px;
  font-weight: 700;
  margin-bottom: 18px;
}

.paper-password-field {
  height: 35px;
  border: 1px solid #c9c9c9;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.paper-password-field.error {
  border-color: #c85f58;
}

.paper-password-field input {
  min-width: 0;
  flex: 1;
  height: 33px;
  border: 0;
  outline: 0;
  padding: 0 10px;
  color: #1d1d1f;
  font-size: 14px;
  box-sizing: border-box;
}

.paper-password-field input::placeholder {
  color: #b9b9b9;
}

.paper-password-field button {
  width: 36px;
  height: 33px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.paper-password-field button:disabled {
  cursor: default;
  opacity: .55;
}

.paper-password-field button span {
  width: 18px;
  height: 11px;
  border: 2px solid #5b5f62;
  border-radius: 50%;
  position: relative;
  display: block;
  box-sizing: border-box;
}

.paper-password-field button span::before {
  content: "";
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #5b5f62;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.paper-password-field button span::after {
  content: "";
  width: 21px;
  height: 2px;
  background: #5b5f62;
  position: absolute;
  left: -3px;
  top: 3px;
  transform: rotate(-38deg);
}

.paper-password-field button span.visible::after {
  display: none;
}

.paper-password-error {
  min-height: 18px;
  margin: 6px 0 0;
  color: #c85f58;
  font-size: 12px;
  line-height: 18px;
}

.paper-password-modal footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  margin-top: 32px;
}

.paper-password-modal footer > div {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.paper-password-forgot {
  display: none;
  color: #5e6b63;
  font-size: 13px;
  line-height: 20px;
}

.paper-password-modal footer > div button {
  min-width: 60px;
  height: 32px;
  border: 1px solid #d8d8d8;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: pointer;
}

.paper-password-modal footer > div button:last-child {
  border-color: #1f3a2d;
  background: #1f3a2d;
  color: #fff;
}

.paper-password-modal footer > div button:disabled {
  cursor: default;
  opacity: .6;
}

.paper-download-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .38);
  z-index: 80;
}

.paper-download-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 474px;
  min-height: 223px;
  transform: translate(-50%, -50%);
  background: #fff;
  z-index: 81;
  padding: 24px 28px 26px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.paper-download-modal header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #111;
  font-size: 19px;
  line-height: 27px;
  font-weight: 500;
}

.paper-download-modal header button {
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.paper-download-modal header button span {
  width: 18px;
  height: 18px;
  position: relative;
  display: block;
}

.paper-download-modal header button span::before,
.paper-download-modal header button span::after {
  content: "";
  width: 18px;
  height: 2px;
  background: #111;
  position: absolute;
  left: 0;
  top: 8px;
}

.paper-download-modal header button span::before {
  transform: rotate(45deg);
}

.paper-download-modal header button span::after {
  transform: rotate(-45deg);
}

.paper-download-options {
  padding: 30px 0 38px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 44px;
}

.paper-download-options label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #111;
  font-size: 17px;
  line-height: 23px;
  cursor: pointer;
}

.paper-download-options input {
  width: 20px;
  height: 20px;
  accent-color: #111;
}

.paper-download-modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.paper-download-modal footer button {
  min-width: 80px;
  height: 40px;
  border: 1px solid #d8d8d8;
  background: #fff;
  color: #333;
  font-size: 15px;
  cursor: pointer;
}

.paper-download-modal footer button:last-child {
  border-color: #1f3a2d;
  background: #1f3a2d;
  color: #fff;
}



.paper-record-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .28);
  z-index: 55;
}

.paper-record-panel {
  --record-row-height: 64px;
  --record-header-height: 52px;
  --record-list-min-height: 192px;
  --record-list-max-height: 320px;
  position: absolute;
  left: 50%;
  top: 50%;
  width: 95%;
  min-height: calc(var(--record-header-height) + var(--record-list-min-height));
  max-height: min(calc(var(--record-header-height) + var(--record-list-max-height)), calc(100% - 24px));
  transform: translate(-50%, -50%);
  background: #fff;
  box-shadow: 0 8px 18px rgba(0, 0, 0, .1);
  z-index: 60;
  display: flex;
  flex-direction: column;
}

.paper-record-close {
  position: absolute;
  top: 8px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.paper-record-close:hover {
  background: #F1EEE8;
}

.paper-record-close span {
  width: 16px;
  height: 16px;
  position: relative;
  display: block;
  transform: scale(.75);
}

.paper-record-close span::before,
.paper-record-close span::after {
  content: "";
  width: 17px;
  height: 1.5px;
  background: #111;
  position: absolute;
  left: 0;
  top: 7px;
}

.paper-record-close span::before {
  transform: rotate(45deg);
}

.paper-record-close span::after {
  transform: rotate(-45deg);
}

.paper-record-panel > header {
  height: var(--record-header-height);
  border-bottom: 1px solid #dededb;
  display: flex;
  align-items: center;
  padding: 0 16px;
  padding-right: 56px;
  box-sizing: border-box;
  color: #14191b;
  font-size: 15px;
  line-height: 22px;
  font-weight: 700;
  flex-shrink: 0;
}

.record-list {
  min-height: var(--record-list-min-height);
  max-height: var(--record-list-max-height);
  padding: 0 12px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.record-row {
  height: var(--record-row-height);
  border-bottom: 1px solid #dededb;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 40px 40px;
  align-items: center;
  gap: 0;
  box-sizing: border-box;
}

.record-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.record-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.record-main strong {
  color: #14191b;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.record-main span {
  color: #6e7578;
  font-size: 11px;
  line-height: 16px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.record-main em {
  display: inline-block;
  width: 1px;
  height: 10px;
  background: #d8d8d8;
  margin: 0 6px;
  vertical-align: -1px;
}

.record-action {
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.record-action:disabled {
  opacity: .25;
  cursor: default;
}

.record-action img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.record-empty {
  min-height: var(--record-list-min-height);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.record-empty img {
  width: 130px;
  height: auto;
  object-fit: contain;
}

.record-empty p {
  margin: 0;
  color: #8c8c8c;
  font-size: 14px;
  line-height: 20px;
}

.record-text-head {
  gap: 16px;
}

.record-text-head button {
  width: 28px;
  height: 40px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.record-text-head strong {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.record-text-body {
  min-height: 0;
  padding: 20px 16px;
  flex: 1;
  color: #161b1d;
  font-size: 14px;
  line-height: 22px;
  white-space: pre-wrap;
  overflow: auto;
}

.paper-record-panel.is-recent-preview {
  --record-row-height: 76px;
  position: fixed;
  inset: 0;
  width: 100%;
  min-height: 0;
  max-height: none;
  transform: none;
  background: #f3f2ed;
  box-shadow: none;
  z-index: 80;
}

.paper-record-panel.is-recent-preview .paper-record-close {
  top: 10px;
  right: auto;
  left: 17px;
  width: 36px;
  height: 36px;
  border: 1px solid #e5d5c8;
  border-radius: 50% !important;
  background: transparent;
}

.paper-record-panel.is-recent-preview .paper-record-close:hover,
.paper-record-panel.is-recent-preview .paper-record-close:active {
  background: rgba(229, 213, 200, .2);
}

.paper-record-panel.is-recent-preview .paper-record-close span {
  width: 11px;
  height: 11px;
  border-left: 2px solid #171a1b;
  border-bottom: 2px solid #171a1b;
  transform: translateX(2px) rotate(45deg);
}

.paper-record-panel.is-recent-preview .paper-record-close span::before,
.paper-record-panel.is-recent-preview .paper-record-close span::after {
  display: none;
}

.paper-record-panel.is-recent-preview > header,
.paper-record-panel.is-recent-preview .record-text-head {
  display: none;
}

.paper-record-panel.is-recent-preview .record-list {
  min-height: 0;
  max-height: none;
  padding: 76px 8px 24px 20px;
  box-sizing: border-box;
  flex: 1;
}

.paper-record-panel.is-recent-preview .record-row {
  position: relative;
  border-bottom: 0;
  grid-template-columns: 48px minmax(0, 1fr) 42px 42px;
}

.paper-record-panel.is-recent-preview .record-row::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: 0;
  left: 48px;
  height: 1px;
  background: #ebe5e0;
}

.paper-record-panel.is-recent-preview .record-icon {
  width: 40px;
  height: 40px;
}

.paper-record-panel.is-recent-preview .record-main {
  gap: 5px;
}

.paper-record-panel.is-recent-preview .record-main strong {
  color: #404243;
  font-size: 14px;
  line-height: 20px;
}

.paper-record-panel.is-recent-preview .record-main span {
  color: #aaa7a4;
  font-size: 12px;
  line-height: 18px;
}

.paper-record-panel.is-recent-preview .record-main em {
  height: 12px;
  margin: 0 8px;
  background: #c8c2be;
  vertical-align: -2px;
}

.paper-record-panel.is-recent-preview .record-action {
  width: 42px;
  height: 44px;
}

.paper-record-panel.is-recent-preview .record-action img {
  width: 24px;
  height: 24px;
}

.paper-record-panel.is-recent-preview .record-text-body {
  padding: 76px 18px 32px 20px;
  color: #8b8987;
  font-size: 14px;
  line-height: 22px;
  box-sizing: border-box;
}

.paper-workspace.mobile-file-browser-active /deep/ .mobile-file-browser-sort-sheet {
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
}

</style>

<style>
#app .paper-workspace.mobile-file-browser-active .paper-detail-body.is-default-zoom:not(.pages-open):not(.is-resolving):not(.is-knowledge-ai-note-preview) .paper-preview-zoom {
  width: 100% !important;
  height: auto !important;
  max-width: var(--paper-preview-base-width) !important;
  aspect-ratio: 3 / 4;
  box-sizing: border-box;
}

#app .paper-workspace.mobile-file-browser-active .paper-detail-body.is-default-zoom:not(.pages-open):not(.is-resolving):not(.is-knowledge-ai-note-preview) .paper-preview-shell {
  overflow-x: hidden !important;
  overflow-y: auto !important;
}

#app .paper-workspace.mobile-file-browser-active .paper-detail-body.is-default-zoom:not(.pages-open):not(.is-resolving):not(.is-knowledge-ai-note-preview) .paper-preview-stage {
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
}

@media screen and (max-width: 767px) {
  .paper-workspace.mobile-file-browser-active .paper-list-area .paper-row > .paper-row-cover.has-cover {
    width: 43px !important;
    height: 58px !important;
    border: 1px solid #d7d9d8;
    border-radius: 3px;
    background: #fff;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .paper-workspace.mobile-file-browser-active .paper-list-area .paper-row-cover-image {
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    max-height: 100% !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-sizing: border-box;
    object-fit: contain !important;
    object-position: center center;
    display: block;
  }

  .paper-workspace.mobile-file-browser-active .paper-list-area .paper-row.selected .paper-row-cover.has-cover {
    border-color: transparent;
  }

  .paper-workspace.mobile-file-browser-active .paper-list-area .paper-cover {
    width: 124px !important;
    max-width: 100%;
    height: auto !important;
    aspect-ratio: 116 / 154;
    flex: 0 0 auto;
    margin: 0 auto;
    background: #fff;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .paper-workspace.mobile-file-browser-active .paper-list-area .paper-cover:not(.folded-cover) {
    border: 1px solid #eee6df;
  }

  .paper-workspace.mobile-file-browser-active .paper-list-area .paper-card.selected .paper-cover,
  .paper-workspace.mobile-file-browser-active .paper-list-area .paper-card:focus-visible .paper-cover {
    border-color: transparent;
  }

  .paper-workspace.mobile-file-browser-active .paper-list-area .paper-cover-image {
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    max-height: 100% !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-sizing: border-box;
    object-fit: contain !important;
    object-position: center center;
    display: block;
  }

  #app .paper-workspace.mobile-file-browser-active .paper-detail-body:not(.pages-open):not(.is-resolving):not(.is-knowledge-ai-note-preview) .paper-preview-shell {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }

  #app .paper-workspace.mobile-file-browser-active .paper-detail-body.is-default-zoom:not(.pages-open):not(.is-resolving):not(.is-knowledge-ai-note-preview) .paper-preview-zoom {
    max-width: none !important;
  }
}

@media screen and (min-width: 768px) {
  #app .paper-workspace.mobile-file-browser-active .paper-detail-body:not(.pages-open):not(.is-resolving):not(.is-knowledge-ai-note-preview) {
    --paper-preview-base-height: max(480px, calc(100vh - 64px)) !important;
    --paper-preview-base-width: calc(var(--paper-preview-base-height) * 0.75) !important;
  }
}
</style>

